import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { neonQuery } from '../services/api';
import { canonicalFloorName, isShortFloorName, normalizeTaktHistory, floorNameOptions } from '../utils/floorNames';
function _defineProperty(obj, key, value) {
  obj[key] = value;
  return obj;
}
// ─── REACT ERROR BOUNDARY ─────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      err: null
    };
  }
  static getDerivedStateFromError(e) {
    return {
      err: e
    };
  }
  componentDidCatch(e, info) {
    console.error('[ErrorBoundary]', e, info);
  }
  render() {
    if (this.state.err) {
      const msg = String(this.state.err.message || this.state.err);
      const stk = String(this.state.err.stack || '').split('\n').slice(0, 6).join('\n');
      return <div style={{
        maxWidth: 560,
        margin: '80px auto',
        padding: '28px 32px',
        background: '#fef2f2',
        border: '2px solid #fecaca',
        borderRadius: 14,
        fontFamily: 'system-ui,sans-serif'
      }}>
          <div style={{
          color: '#b91c1c',
          fontSize: 17,
          fontWeight: 700,
          marginBottom: 8
        }}>⚠ Something went wrong</div>
          <div style={{
          color: '#7f1d1d',
          fontSize: 13,
          lineHeight: 1.7,
          marginBottom: 12
        }}>{msg}</div>
          <pre style={{
          color: '#991b1b',
          fontSize: 11,
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          background: '#fff8f8',
          padding: 10,
          borderRadius: 6,
          border: '1px solid #fecaca'
        }}>{stk}</pre>
          <button onClick={() => this.setState({
          err: null
        })} style={{
          marginTop: 16,
          background: '#b91c1c',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '8px 18px',
          fontWeight: 700,
          cursor: 'pointer',
          fontSize: 13
        }}>Try to recover</button>
        </div>;
    }
    return this.props.children;
  }
}

// ─── BRAND TOKENS ────────────────────────────────────────────────────────────
const B = {
  navy: "#1a2e4a",
  navyLight: "#243755",
  red: "#c0392b",
  redLight: "#e74c3c",
  cream: "#fdf8f3",
  white: "#ffffff",
  cardBg: "#ffffff",
  cardBorder: "#ddd5c8",
  rowAlt: "#faf6f1",
  rowSep: "#e8ddd3",
  textDark: "#1a2e4a",
  textMid: "#4a5568",
  textLight: "#8a9ab0",
  divider: "#e2d9cf",
  colSel: "#1a2e4a",
  colAvg: "#1e6fa8",
  colLY: "#2e7d52",
  colFC: "#c0392b",
  green: "#2e7d52",
  amber: "#b45309",
  orange: "#c2540a",
  alertRed: "#b91c1c",
  alertAmber: "#92400e",
  warnBg: "#fef2f2",
  warnBorder: "#fecaca",
  okBg: "#f0fdf4",
  okBorder: "#bbf7d0",
  infoBg: "#eff6ff",
  infoBorder: "#bfdbfe",
  urgentBg: "#fff7ed",
  urgentBorder: "#fed7aa",
  urgentText: "#c2540a",
  expiredBg: "#fef2f2",
  expiredBorder: "#fecaca",
  expiredText: "#b91c1c"
};

// ─── FINISHED SIZES ───────────────────────────────────────────────────────────
const SIZE_COLORS = {
  "Colossal": "#1d6fa8",
  // blue   — matches Excel recap
  "Premium": "#2e7d52",
  // green
  "Select": "#a16207",
  // amber/yellow
  "Standard": "#b91c1c",
  // red
  "Large Female": "#b45309",
  // orange/gold
  "Regular Female": "#6d28d9" // purple
};
// Soft pastel backgrounds for tinted rows (very light, not gaudy)
const SIZE_BG = {
  "Colossal": "#eff6ff",
  "Premium": "#f0fdf4",
  "Select": "#fefce8",
  "Standard": "#fff1f2",
  "Large Female": "#fff7ed",
  "Regular Female": "#f5f3ff"
};
const SIZES = [{
  size: "Colossal",
  dpb: 4,
  color: SIZE_COLORS["Colossal"]
}, {
  size: "Premium",
  dpb: 5,
  color: SIZE_COLORS["Premium"]
}, {
  size: "Select",
  dpb: 5.5,
  color: SIZE_COLORS["Select"]
}, {
  size: "Standard",
  dpb: 6,
  color: SIZE_COLORS["Standard"]
}, {
  size: "Large Female",
  dpb: 5,
  color: SIZE_COLORS["Large Female"]
}, {
  size: "Regular Female",
  dpb: 6,
  color: SIZE_COLORS["Regular Female"]
}];
const SIZE_MAP = Object.fromEntries(SIZES.map(function (s) {
  return [s.size, s];
}));

// ─── SHELF-LIFE ───────────────────────────────────────────────────────────────
// Day 0    = steam date
// Day 1–5  = FRESH
// Day 6–7  = AGING
// Day 8    = URGENT  — ship today via 2-day service
// Day 9    = URGENT  — last day, ship via next-day service
// Day 10+  = EXPIRED — excluded from usable inventory
//
// Weekend rule: we do not ship on weekends.
// If day-9 (the last usable ship day) falls on Saturday or Sunday, then the
// PREVIOUS FRIDAY is the last viable shipping day. We look ahead from today to
// determine whether day-9 will land on a weekend, and surface the alert early.
const LOT_STATUS = {
  FRESH: {
    label: "Fresh",
    color: "#166534",
    bg: "#f0fdf4",
    border: "#bbf7d0"
  },
  AGING: {
    label: "Aging",
    color: "#92400e",
    bg: "#fefce8",
    border: "#fde68a"
  },
  URGENT: {
    label: "Urgent",
    color: "#c2540a",
    bg: "#fff7ed",
    border: "#fed7aa"
  },
  EXPIRED: {
    label: "Expired",
    color: "#b91c1c",
    bg: "#fef2f2",
    border: "#fecaca"
  }
};
function lotAge(steamISO, todayStr) {
  return Math.round((new Date(todayStr + "T12:00:00") - new Date(steamISO + "T12:00:00")) / 86400000);
}
function lotStatus(age) {
  if (age <= 5) return "FRESH";
  if (age <= 7) return "AGING";
  if (age <= 9) return "URGENT";
  return "EXPIRED";
}
// Returns day-of-week (0=Sun, 1=Mon … 6=Sat) for an ISO date string
function _dow(isoDate) {
  return new Date(isoDate + "T12:00:00").getDay();
}
// Add `days` calendar days to an ISO date string, return new ISO string
function _addDays(isoDate, days) {
  const d = new Date(isoDate + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
// Returns a short operational shipping alert for urgent lots, accounting for weekends.
// todayStr is the planning date (ISO). steamISO is the lot's steam date.
function shipAlert(age, todayStr, steamISO) {
  if (age < 7 || age > 9) return null;

  // Determine what calendar date day-9 falls on
  const day9Date = steamISO ? _addDays(steamISO, 9) : null;
  const day9Dow = day9Date ? _dow(day9Date) : null; // 0=Sun,6=Sat

  const todayDow = todayStr ? _dow(todayStr) : null;

  // Weekend-adjusted: day-9 is a Saturday or Sunday
  // → The Friday before is the real last ship day
  const day9OnWeekend = day9Dow === 0 || day9Dow === 6;
  if (day9OnWeekend) {
    // How many days until the Friday before day-9?
    // day9-2 = Friday if day9 is Sunday, day9-1 = Friday if day9 is Saturday
    const fridayOffset = day9Dow === 6 ? 1 : 2; // days before day-9 that is Friday
    const fridayDate = _addDays(day9Date, -fridayOffset);
    const daysToFriday = day9Date ? Math.round((new Date(fridayDate + "T12:00:00") - new Date(todayStr + "T12:00:00")) / 86400000) : null;
    if (daysToFriday === 0) return "⚠ Ship TODAY (Friday) — last day before weekend";
    if (daysToFriday === 1) return "Ship tomorrow (Friday) — weekend cutoff";
    if (daysToFriday !== null && daysToFriday <= 3) return "Weekend cutoff \u2014 last ship day is this Friday";
    return "Weekend cutoff — verify ship date";
  }

  // Normal weekday rule
  if (age === 8) return "Ship today — 2-day service";
  if (age === 9) return "Last day — next-day only";
  return null;
}
function enrichLot(lot, today) {
  const age = lotAge(lot.steamDate, today);
  const status = lotStatus(age);
  var wkdCutoff = (function() {
    if (!lot.steamDate || status === "EXPIRED" || status === "FRESH") return false;
    // Compute the true last usable ship day (day 9, adjusted for weekends)
    var day9Date = _addDays(lot.steamDate, 9);
    var day9Dow = _dow(day9Date);
    var lastShipDay = day9Date;
    if (day9Dow === 6) lastShipDay = _addDays(day9Date, -1); // Sat → Fri
    if (day9Dow === 0) lastShipDay = _addDays(day9Date, -2); // Sun → Fri
    // Count calendar days from today to last ship day
    var daysToLastShip = Math.round(
      (new Date(lastShipDay + 'T12:00:00') - new Date(today + 'T12:00:00')) / 86400000
    );
    // Urgent if last ship day is today or tomorrow (regardless of day of week)
    // This handles all future cases: Fri cutoff, Mon cutoff, any day
    return daysToLastShip >= 0 && daysToLastShip <= 1;
  })();
  // isOperationallyUrgent: day 9 (last day) OR last ship day is today/tomorrow
  var isOperationallyUrgent = (age === 9) || wkdCutoff;
  return {
    ...lot,
    age: age,
    status: status,
    daysRemaining: Math.max(0, 9 - age),
    usable: status !== "EXPIRED",
    shipAlert: shipAlert(age, today, lot.steamDate),
    weekendCutoffUrgent: wkdCutoff,
    isOperationallyUrgent: isOperationallyUrgent
  };
}
function summarizeInventoryBySize(enrichedLots) {
  const out = {};
  SIZES.forEach(function ({ size }) {
    out[size] = {
      total: 0,
      usable: 0,
      urgent: 0,
      expired: 0,
      // Day-level buckets (usable lots only, keyed by age 1-9)
      weekendCutoff: 0,
      dayBuckets: { 1:0, 2:0, 3:0, 4:0, 5:0, 6:0, 7:0, 8:0, 9:0 },
      priorityBuckets: { urgent: 0, aging: 0, fresh: 0 }
    };
  });
  enrichedLots.forEach(function (lot) {
    const s = out[lot.size];
    if (!s) return;
    const bu = Number(lot.bushels) || 0;
    s.total = r2(s.total + bu);
    if (lot.usable) {
      s.usable = r2(s.usable + bu);
      // Populate day buckets for usable lots only
      const age = lot.age;
      if (age >= 1 && age <= 9) {
        s.dayBuckets[age] = r2((s.dayBuckets[age] || 0) + bu);
        // urgent bucket: only isOperationallyUrgent lots (age 9 or weekend cutoff)
        // day 8 alone goes to aging, not urgent
        if (lot.isOperationallyUrgent) {
          s.priorityBuckets.urgent = r2(s.priorityBuckets.urgent + bu);
        } else if (age >= 6) {
          s.priorityBuckets.aging = r2(s.priorityBuckets.aging + bu);
        } else {
          s.priorityBuckets.fresh = r2(s.priorityBuckets.fresh + bu);
        }
      }
    }
    if (lot.isOperationallyUrgent) s.urgent = r2(s.urgent + bu);
    if (lot.weekendCutoffUrgent) s.weekendCutoff = r2((s.weekendCutoff || 0) + bu);
    if (!lot.usable) s.expired = r2(s.expired + bu);
  });
  return out;
}

// ─── GRADE MODEL ─────────────────────────────────────────────────────────────
const GRADES = [{
  grade: "1s",
  label: "1s (Colossal / Premium)",
  color: "#1d6fa8",
  primarySizes: ["Colossal", "Premium"],
  buPerBox: 1.0
}, {
  grade: "2s",
  label: "2s (Select / Standard)",
  color: "#a16207",
  primarySizes: ["Select", "Standard"],
  buPerBox: 1.0
}, {
  grade: "3s",
  label: "3s (Large Female / Reg. Female)",
  color: "#c2540a",
  primarySizes: ["Large Female", "Regular Female"],
  buPerBox: 1.0
}];
const GRADE_MAP = Object.fromEntries(GRADES.map(function (g) {
  return [g.grade, g];
}));
const SIZE_TO_GRADE = {
  "Colossal": "1s",
  "Premium": "1s",
  "Select": "2s",
  "Standard": "2s",
  "Large Female": "3s",
  "Regular Female": "3s"
};

// ─── SUPPLY CONTEXT TAGS ──────────────────────────────────────────────────────

// shipdate is the authoritative date field. odate is intentionally excluded.
const SHIP_DATE_FIELD = "shipdate";
const SOURCE_TYPES = ["Louisiana", "White Marsh", "Local", "Nearby Run", "Other"];
const BUY_WINDOWS = ["Monday", "Wednesday", "Either", "As Needed"];

// ─── HARD CRAB PARSING ───────────────────────────────────────────────────────
// Returns { size, dpb, reason } on inclusion, or { size:null, dpb:null, reason } on exclusion.
// reason is a human-readable string for logging.
function classifyHardCrab(rawName) {
  const n = rawName.toLowerCase().replace(/<[^>]+>/g, " ").replace(/&amp;/g, "&").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

  // Hard exclusions — most specific first so the reason is precise
  if (/soft[\s\-]?(shell|crab|crabs?)|softshell/i.test(n)) return {
    size: null,
    dpb: null,
    reason: "excluded by soft-crab rule"
  };
  if (/crab[\s\-]?meat|lump\b|backfin|claw[\s\-]?meat|crab[\s\-]?cake|crab[\s\-]?dip|seasoning/i.test(n)) return {
    size: null,
    dpb: null,
    reason: "excluded by non-hard-crab rule (crab meat / product)"
  };
  if (/\b(shrimp|lobster|oyster|clam|scallop|flounder|fish|salmon|tuna|tilapia)\b/i.test(n)) return {
    size: null,
    dpb: null,
    reason: "excluded by non-hard-crab rule (non-crab seafood)"
  };
  const hasBushel = /\bbushel\b/i.test(n);
  const hasHardCrab = /\bhard[\s\-]?crabs?\b/i.test(n);
  const hasSizeWord = /\b(colossal|premium|jumbo|select|standard|large\s+female|lg\s+female|reg(ular)?\s+female|medium\s+female|female)\b/i.test(n);
  const hasDozen = /\bdozen\b/i.test(n);
  const hasCrab = /\bcrab(s)?\b/i.test(n);
  const isHardCrab = hasBushel || hasHardCrab || hasSizeWord && hasCrab || hasSizeWord && hasDozen || hasDozen && hasCrab;
  if (!isHardCrab) return {
    size: null,
    dpb: null,
    reason: "excluded by non-hard-crab rule (no bushel/size/dozen/crab signal)"
  };
  let size;
  if (/colossal/i.test(n)) size = "Colossal";else if (/premium[\s\-]female/i.test(n)) size = "Large Female";else if (/premium/i.test(n)) size = "Premium";else if (/\bjumbo\b/i.test(n)) size = "Premium";else if (/select[\s\-]female/i.test(n)) size = "Large Female";else if (/standard[\s\-]female/i.test(n)) size = "Regular Female";else if (/select/i.test(n)) size = "Select";else if (/standard/i.test(n)) size = "Standard";else if (/large[\s\-]female|lg[\s\-]female/i.test(n)) size = "Large Female";else if (/reg(ular)?[\s\-]female|medium[\s\-]female/i.test(n)) size = "Regular Female";else if (/\bfemale\b/i.test(n)) size = "Large Female";else size = "Standard";
  return {
    size: size,
    dpb: SIZE_MAP[size].dpb,
    reason: "included as ".concat(size)
  };
}
function parsePackage(rawName) {
  const n = rawName.toLowerCase();
  if (/\b(half|1\s*\/\s*2|0\.5)\s*bushel/i.test(n)) return {
    pkgDoz: null,
    pkgBu: 0.5,
    isBushel: true,
    pkgLabel: "½ bushel"
  };
  const nBuM = n.match(/\b(\d+(?:\.\d+)?)\s*bushels?\b/);
  if (nBuM) {
    const bu = parseFloat(nBuM[1]);
    return {
      pkgDoz: null,
      pkgBu: bu,
      isBushel: true,
      pkgLabel: "".concat(bu, " bushel")
    };
  }
  if (/\bbushels?\b/.test(n)) return {
    pkgDoz: null,
    pkgBu: 1,
    isBushel: true,
    pkgLabel: "1 bushel"
  };
  if (/\b(half|1\s*\/\s*2|½|0\.5)\s*dozen/i.test(n)) return {
    pkgDoz: 0.5,
    pkgBu: null,
    isBushel: false,
    pkgLabel: "½ dozen"
  };
  const nDzM = n.match(/\b(\d+(?:\.\d+)?)\s*dozens?\b/);
  if (nDzM) {
    const doz = parseFloat(nDzM[1]);
    return {
      pkgDoz: doz,
      pkgBu: null,
      isBushel: false,
      pkgLabel: "".concat(doz, " dozen")
    };
  }
  if (/\bdozens?\b/.test(n)) return {
    pkgDoz: 1,
    pkgBu: null,
    isBushel: false,
    pkgLabel: "1 dozen"
  };
  return {
    pkgDoz: 1,
    pkgBu: null,
    isBushel: false,
    pkgLabel: "1 dozen (assumed)"
  };
}
function convertToBushels(numItems, pkg, dpb) {
  if (pkg.isBushel) {
    const bu = r2(numItems * pkg.pkgBu);
    return {
      bu: bu,
      math: "".concat(numItems, " qty \xD7 ").concat(pkg.pkgLabel, " = ").concat(fb(bu), " bu"),
      ok: true
    };
  }
  if (pkg.pkgDoz != null) {
    const totalDoz = numItems * pkg.pkgDoz;
    const _bu = r2(totalDoz / dpb);
    return {
      bu: _bu,
      math: "".concat(numItems, " qty \xD7 ").concat(pkg.pkgLabel, " \xF7 ").concat(dpb, " dpb = ").concat(fb(_bu), " bu"),
      ok: true
    };
  }
  return {
    bu: null,
    math: "".concat(numItems, " \xD7 ").concat(pkg.pkgLabel, " \u2014 cannot convert"),
    ok: false
  };
}

// Legacy shims — kept to avoid accidental reference errors
function mapSize(name) {
  const r = classifyHardCrab(name);
  return r.size || null;
}
function detectUnit() {
  return "n/a";
}
function toBushels(qty, _, dpb) {
  return convertToBushels(qty, {
    pkgDoz: 1,
    pkgBu: null,
    isBushel: false,
    pkgLabel: "1 dozen"
  }, dpb);
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const clean = function clean(s) {
  if (s == null || s === "") return "";
  return String(s).replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
};
const fb = function fb(n) {
  return (Math.round(n * 100) / 100).toFixed(2);
};
const r2 = function r2(n) {
  return Math.round(n * 100) / 100;
};
const mean = function mean(a) {
  return a.length ? a.reduce(function (s, v) {
    return s + v;
  }, 0) / a.length : 0;
};
const toSafe = function toSafe(s) {
  const d = new Date(String(s || "").trim() + "T12:00:00");
  return isNaN(d.getTime()) ? null : d;
};
const weekStart = function weekStart(d) {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const m = new Date(dt);
  m.setDate(dt.getDate() - (dt.getDay() + 6) % 7);
  return m.toISOString().slice(0, 10);
};
const fmtShort = function fmtShort(s) {
  const d = toSafe(s);
  return d ? "".concat(d.getMonth() + 1, "/").concat(d.getDate()) : s || "";
};
const fmtLong = function fmtLong(s) {
  const d = toSafe(s);
  return d ? d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  }) : s || "";
};
const fmtDate = function fmtDate(s) {
  const d = toSafe(s);
  return d ? d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }) : s || "";
};
const todayISO = function todayISO() {
  return new Date().toISOString().slice(0, 10);
};
function nextBuyWindow(fromISO) {
  const d = toSafe(fromISO) || new Date();
  const out = [];
  for (let i = 1; i <= 7; i++) {
    let nd = new Date(d);
    nd.setDate(d.getDate() + i);
    if (nd.getDay() === 1) out.push({
      label: "Monday",
      date: nd.toISOString().slice(0, 10)
    });
    if (nd.getDay() === 3) out.push({
      label: "Wednesday",
      date: nd.toISOString().slice(0, 10)
    });
    if (out.length >= 2) break;
  }
  return out;
}
function daysBetween(a, b) {
  return Math.round((new Date(b + "T12:00:00") - new Date(a + "T12:00:00")) / 86400000);
}
function processCSV(rows) {
  const dateField = SHIP_DATE_FIELD; // the function reads SHIP_DATE_FIELD directly below
  const weeks = {},
    days = {},
    wkAudit = {},
    dayAudit = {},
    rowLog = {};
  let skippedNoDate = 0;
  rows.forEach(function (r) {
    const name = clean(r.itemname);
    const numItems = Number(r.numitems) || 0;
    if (!name || numItems <= 0) return;
    const rawShip = r[SHIP_DATE_FIELD];
    if (!rawShip || String(rawShip).trim() === "") {
      skippedNoDate++;
      return;
    }
    const shipDate = new Date(String(rawShip).trim());
    if (isNaN(shipDate.getTime())) {
      skippedNoDate++;
      return;
    }
    const dayKey = shipDate.toISOString().slice(0, 10);
    const cls = classifyHardCrab(name);
    if (!rowLog[dayKey]) rowLog[dayKey] = [];
    if (!cls.size) {
      rowLog[dayKey].push({
        name: name,
        numItems: numItems,
        included: false,
        reason: cls.reason,
        pkg: null,
        math: null,
        bu: null,
        size: null
      });
      return;
    }
    const pkg = parsePackage(name);
    const conv = convertToBushels(numItems, pkg, cls.dpb);
    rowLog[dayKey].push({
      name: name,
      numItems: numItems,
      included: true,
      size: cls.size,
      reason: cls.reason,
      pkg: pkg.pkgLabel,
      math: conv.math,
      bu: conv.bu,
      ok: conv.ok
    });
    if (!conv.ok) return;
    const wk = weekStart(shipDate);
    if (!wk) return;
    const auditLine = {
      name: name,
      qty: numItems,
      pkg: pkg.pkgLabel,
      math: conv.math,
      bu: conv.bu,
      ok: conv.ok,
      date: dayKey,
      unit: pkg.pkgLabel
    };
    weeks[wk] = weeks[wk] || {};
    weeks[wk][cls.size] = (weeks[wk][cls.size] || 0) + (conv.bu || 0);
    wkAudit[wk] = wkAudit[wk] || {};
    wkAudit[wk][cls.size] = wkAudit[wk][cls.size] || [];
    wkAudit[wk][cls.size].push(auditLine);
    days[dayKey] = days[dayKey] || {};
    days[dayKey][cls.size] = (days[dayKey][cls.size] || 0) + (conv.bu || 0);
    dayAudit[dayKey] = dayAudit[dayKey] || {};
    dayAudit[dayKey][cls.size] = dayAudit[dayKey][cls.size] || [];
    dayAudit[dayKey][cls.size].push(auditLine);
  });
  [weeks, days].forEach(function (map) {
    return Object.values(map).forEach(function (e) {
      return Object.keys(e).forEach(function (s) {
        e[s] = r2(e[s]);
      });
    });
  });
  return {
    weeks: weeks,
    days: days,
    wkAudit: wkAudit,
    dayAudit: dayAudit,
    dateField: dateField,
    skippedNoDate: skippedNoDate,
    rowLog: rowLog
  };
}

// ─── ANALYTICS ───────────────────────────────────────────────────────────────
const getBu = function getBu(map, key, size) {
  let _map$key;
  return ((_map$key = map[key]) === null || _map$key === void 0 ? void 0 : _map$key[size]) || 0;
};
function get4wkAvg(weeks, allWks, refWk, size) {
  const idx = allWks.indexOf(refWk);
  if (idx < 1) return 0;
  return r2(mean(allWks.slice(Math.max(0, idx - 4), idx).map(function (w) {
    return getBu(weeks, w, size);
  })));
}
function getWeekForecast(weeks, allWks, refWk, size) {
  const idx = allWks.indexOf(refWk);
  if (idx < 2) return null;
  const prev8 = allWks.slice(Math.max(0, idx - 8), idx).map(function (w) {
    return getBu(weeks, w, size);
  });
  const prev4 = prev8.slice(-4);
  const a4 = mean(prev4),
    a8 = mean(prev8);
  const xs = prev8.map(function (_, i) {
      return i;
    }),
    xBar = mean(xs),
    yBar = a8;
  const denom = xs.reduce(function (s, x) {
    return s + Math.pow(x - xBar, 2);
  }, 0) || 1;
  const slope = xs.reduce(function (s, x, i) {
    return s + (x - xBar) * (prev8[i] - yBar);
  }, 0) / denom;
  return r2(a4 * 0.6 + Math.max(0, a8 + slope * prev8.length) * 0.4);
}
function getSWLY(weeks, refWk, size) {
  const d = toSafe(refWk);
  if (!d) return null;
  d.setFullYear(d.getFullYear() - 1);
  for (let _i = 0, _arr = [0, -7, 7, -14, 14]; _i < _arr.length; _i++) {
    let _weeks$wk;
    let delta = _arr[_i];
    let dd = new Date(d);
    dd.setDate(dd.getDate() + delta);
    let wk = weekStart(dd);
    if (wk && ((_weeks$wk = weeks[wk]) === null || _weeks$wk === void 0 ? void 0 : _weeks$wk[size]) != null) return {
      wk: wk,
      bu: r2(weeks[wk][size])
    };
  }
  return null;
}
function weekTotalRow(weeks, allWks, refWk) {
  return SIZES.reduce(function (acc, {
    size
  }) {
    acc.sel = r2((acc.sel || 0) + getBu(weeks, refWk, size));
    acc.avg4 = r2((acc.avg4 || 0) + get4wkAvg(weeks, allWks, refWk, size));
    const swly = getSWLY(weeks, refWk, size);
    if (swly) acc.swly = r2((acc.swly || 0) + swly.bu);
    const fc = getWeekForecast(weeks, allWks, refWk, size);
    if (fc != null) acc.fc = r2((acc.fc || 0) + fc);
    return acc;
  }, {
    sel: 0,
    avg4: 0,
    swly: null,
    fc: null
  });
}
function get7dayAvg(days, allDays, refDay, size) {
  const idx = allDays.indexOf(refDay);
  if (idx < 1) return 0;
  return r2(mean(allDays.slice(Math.max(0, idx - 7), idx).map(function (d) {
    return getBu(days, d, size);
  })));
}
function getDayForecast(days, allDays, refDay, size) {
  const idx = allDays.indexOf(refDay);
  if (idx < 2) return null;
  const prev14 = allDays.slice(Math.max(0, idx - 14), idx).map(function (d) {
    return getBu(days, d, size);
  });
  const prev7 = prev14.slice(-7);
  const a7 = mean(prev7),
    a14 = mean(prev14);
  const xs = prev14.map(function (_, i) {
      return i;
    }),
    xBar = mean(xs),
    yBar = a14;
  const denom = xs.reduce(function (s, x) {
    return s + Math.pow(x - xBar, 2);
  }, 0) || 1;
  const slope = xs.reduce(function (s, x, i) {
    return s + (x - xBar) * (prev14[i] - yBar);
  }, 0) / denom;
  return r2(a7 * 0.6 + Math.max(0, a14 + slope * prev14.length) * 0.4);
}

// ─── WEEK COMPLETION FACTOR ───────────────────────────────────────────────────
// Wed-Fri are the biggest shipping days. When buying on Monday, orders placed
// later in the week haven't been committed yet, so we need to project the full
// week total based on historical day-of-week ratios.
//
// Returns: { factor: number, todayPct: number, remainingPct: number, dayLabel: string }
// factor = 1 / todayPct = multiplier to project full week from current committed
function getWeekCompletionFactor(days, allDays, todayIso) {
  const todayDow = new Date(todayIso + 'T12:00:00').getDay(); // 0=Sun,6=Sat
  // Build day-of-week buckets from historical data (last 20 weeks)
  const dowTotals = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  const weekTotals = {};
  // Get monday-week key for a date
  function monKey(iso) {
    const d = new Date(iso + 'T12:00:00');
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(d);
    mon.setDate(d.getDate() + diff);
    return mon.toISOString().slice(0, 10);
  }
  // Only use days that are not in the current week and not future
  const todayDate = new Date(todayIso + 'T12:00:00');
  const currentWk = monKey(todayIso);
  let weeksUsed = 0;
  allDays.forEach(function(day) {
    const wk = monKey(day);
    if (wk >= currentWk) return; // skip current/future week
    const d = new Date(day + 'T12:00:00');
    if (d > todayDate) return;
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return; // skip weekends
    // Sum all sizes for this day
    let dayBu = 0;
    SIZES.forEach(function({ size }) { dayBu += getBu(days, day, size); });
    if (dayBu <= 0) return;
    dowTotals[dow] = (dowTotals[dow] || 0) + dayBu;
    if (!weekTotals[wk]) { weekTotals[wk] = 0; weeksUsed++; }
    weekTotals[wk] = (weekTotals[wk] || 0) + dayBu;
  });
  // Only use last 20 complete weeks
  const wkKeys = Object.keys(weekTotals).sort().slice(-20);
  if (wkKeys.length < 4) return null; // not enough data
  // Recalculate dowTotals for just the last 20 weeks
  const recentDowTotals = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const recentWkTotals = {};
  allDays.forEach(function(day) {
    const wk = monKey(day);
    if (!wkKeys.includes(wk)) return;
    const dow = new Date(day + 'T12:00:00').getDay();
    if (dow === 0 || dow === 6) return;
    let dayBu = 0;
    SIZES.forEach(function({ size }) { dayBu += getBu(days, day, size); });
    if (dayBu <= 0) return;
    recentDowTotals[dow] = (recentDowTotals[dow] || 0) + dayBu;
    recentWkTotals[wk] = (recentWkTotals[wk] || 0) + dayBu;
  });
  const weekAvgTotal = r2(wkKeys.reduce((s, w) => s + (recentWkTotals[w] || 0), 0) / wkKeys.length);
  if (!weekAvgTotal) return null;
  // Pct of week total that falls on each day
  const dowPct = {};
  [1,2,3,4,5].forEach(function(d) {
    dowPct[d] = r2((recentDowTotals[d] || 0) / (weekAvgTotal * wkKeys.length));
  });
  // For current day of week, what % of the week has shipped so far (Mon through today)?
  const daysInWeekSoFar = [];
  for (let d = 1; d <= todayDow; d++) { if (d >= 1 && d <= 5) daysInWeekSoFar.push(d); }
  const soFarPct = r2(daysInWeekSoFar.reduce((s, d) => s + (dowPct[d] || 0), 0));
  if (!soFarPct || soFarPct >= 1) return null;
  const remainingPct = r2(1 - soFarPct);
  const factor = soFarPct > 0 ? r2(1 / soFarPct) : 1;
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return {
    factor,
    soFarPct,
    remainingPct,
    todayDow,
    dayLabel: dayNames[todayDow],
    dowPct,
    weekAvgTotal,
    weeksUsed: wkKeys.length
  };
}


// ─── HOLIDAY CALENDAR ─────────────────────────────────────────────────────────
// Major holidays that affect hard crab shipping patterns.
// UPS_NO_SHIP = UPS does not deliver on these dates.
// DEMAND_BOOST = high-demand holidays where sales spike before the date.
// SWLY_WEIGHT = how heavily to weight Same Week Last Year vs rolling average.
//   1.0 = use SWLY exclusively, 0.0 = ignore SWLY entirely.

function nthWeekday(year, month, n, dow) {
  // nth occurrence of day-of-week (0=Sun) in month
  const first = new Date(year, month - 1, 1);
  const diff = (dow - first.getDay() + 7) % 7;
  return new Date(year, month - 1, 1 + diff + (n - 1) * 7);
}
function lastWeekday(year, month, dow) {
  const last = new Date(year, month, 0); // last day of month
  const diff = (last.getDay() - dow + 7) % 7;
  return new Date(year, month - 1, last.getDate() - diff);
}

function buildHolidayCalendar(year) {
  return [
    { name: "New Year's Day",   date: new Date(year, 0, 1),  upsNoShip: true,  swlyWeight: 0.7, boost: false },
    { name: "Valentine's Day",  date: new Date(year, 1, 14), upsNoShip: false, swlyWeight: 0.8, boost: true  },
    { name: "Mother's Day",     date: nthWeekday(year, 5, 2, 0), upsNoShip: false, swlyWeight: 0.9, boost: true  },
    { name: "Memorial Day",     date: lastWeekday(year, 5, 1),   upsNoShip: true,  swlyWeight: 0.9, boost: true  },
    { name: "Father's Day",     date: nthWeekday(year, 6, 3, 0), upsNoShip: false, swlyWeight: 0.9, boost: true  },
    { name: "July 4th",         date: new Date(year, 6, 4),  upsNoShip: true,  swlyWeight: 1.0, boost: true  },
    { name: "Labor Day",        date: nthWeekday(year, 9, 1, 1), upsNoShip: true,  swlyWeight: 0.9, boost: true  },
    { name: "Thanksgiving",     date: nthWeekday(year, 11, 4, 4),upsNoShip: true,  swlyWeight: 0.9, boost: false },
    { name: "Christmas",        date: new Date(year, 11, 25), upsNoShip: true,  swlyWeight: 0.9, boost: false },
  ];
}

function isoFromDate(d) {
  return d.toISOString().slice(0, 10);
}

function mondayOfWeek(isoDate) {
  const d = new Date(isoDate + 'T12:00:00');
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  return isoFromDate(m);
}

// Returns holiday info if the given week contains a holiday, else null
function getHolidayForWeek(weekMonday) {
  const wkStart = new Date(weekMonday + 'T12:00:00');
  const wkEnd = new Date(wkStart);
  wkEnd.setDate(wkStart.getDate() + 6);
  const year = wkStart.getFullYear();
  // Check current year and adjacent years
  for (const yr of [year - 1, year, year + 1]) {
    for (const h of buildHolidayCalendar(yr)) {
      const hd = h.date;
      if (hd >= wkStart && hd <= wkEnd) {
        return { ...h, dateIso: isoFromDate(hd) };
      }
    }
  }
  return null;
}

// Get UPS no-ship dates in a given week
function getUpsNoShipDays(weekMonday) {
  const wkStart = new Date(weekMonday + 'T12:00:00');
  const wkEnd = new Date(wkStart);
  wkEnd.setDate(wkStart.getDate() + 6);
  const year = wkStart.getFullYear();
  const noShip = [];
  for (const yr of [year - 1, year, year + 1]) {
    for (const h of buildHolidayCalendar(yr)) {
      if (!h.upsNoShip) continue;
      const hd = h.date;
      if (hd >= wkStart && hd <= wkEnd) {
        noShip.push(isoFromDate(hd));
      }
    }
  }
  return noShip;
}

// Find SWLY week: same week last year (matched by holiday name if holiday week)
function getSWLYWeekMonday(weekMonday, days) {
  const holiday = getHolidayForWeek(weekMonday);
  if (holiday) {
    // Find the week last year that contained the same holiday
    const lyYear = new Date(weekMonday + 'T12:00:00').getFullYear() - 1;
    for (const h of buildHolidayCalendar(lyYear)) {
      if (h.name === holiday.name) {
        return mondayOfWeek(isoFromDate(h.date));
      }
    }
  }
  // Non-holiday: just go back 52 weeks
  const d = new Date(weekMonday + 'T12:00:00');
  d.setDate(d.getDate() - 364);
  return isoFromDate(d);
}

function getSDLY(days, refDay, size) {
  const d = toSafe(refDay);
  if (!d) return null;
  d.setFullYear(d.getFullYear() - 1);
  for (let _i2 = 0, _arr2 = [0, -1, 1, -2, 2]; _i2 < _arr2.length; _i2++) {
    let _days$key;
    let delta = _arr2[_i2];
    let dd = new Date(d);
    dd.setDate(dd.getDate() + delta);
    let key = dd.toISOString().slice(0, 10);
    if (((_days$key = days[key]) === null || _days$key === void 0 ? void 0 : _days$key[size]) != null) return {
      key: key,
      bu: r2(days[key][size])
    };
  }
  return null;
}
function dayTotalRow(days, allDays, refDay) {
  return SIZES.reduce(function (acc, {
    size
  }) {
    acc.sel = r2((acc.sel || 0) + getBu(days, refDay, size));
    acc.avg7 = r2((acc.avg7 || 0) + get7dayAvg(days, allDays, refDay, size));
    const sdly = getSDLY(days, refDay, size);
    if (sdly) acc.sdly = r2((acc.sdly || 0) + sdly.bu);
    const fc = getDayForecast(days, allDays, refDay, size);
    if (fc != null) acc.fc = r2((acc.fc || 0) + fc);
    return acc;
  }, {
    sel: 0,
    avg7: 0,
    sdly: null,
    fc: null
  });
}

// ─── DEMAND ENGINE ────────────────────────────────────────────────────────────
function computeDemandPicture({
  forecastBySize,
  demandOverrides
}) {
  const bySize = {};
  let totalObserved = 0,
    totalTrueDemand = 0;
  SIZES.forEach(function ({
    size
  }) {
    const observed = forecastBySize[size] || 0,
      override = demandOverrides[size] ? Number(demandOverrides[size]) : null;
    const trueDemand = override != null && override > 0 ? override : observed;
    bySize[size] = {
      observed: r2(observed),
      trueDemand: r2(trueDemand),
      note: override != null ? "manual override" : ""
    };
    totalObserved = r2(totalObserved + observed);
    totalTrueDemand = r2(totalTrueDemand + trueDemand);
  });
  return {
    bySize: bySize,
    totalObserved: totalObserved,
    totalTrueDemand: totalTrueDemand
  };
}

// Planning factors for internal coverage from live/ungraded supply:
//   Live boxes (in boxes, not yet steamed): count at 100% — ~50 lb box grades to ~1 bu
//   Steamed/ungraded boxes: count at 75% — some bushels are light or lose yield during grading
const LIVE_FACTOR = 1.00;
const UNGRADED_FACTOR = 0.75;
function computeGradeBuy({
  demandPicture,
  inventoryBySize,
  inboundByGrade,
  gradeOverrides,
  liveSupplyByGrade
}) {
  const gradeNetBu = {};
  const gradeLiveCoverage = {};
  GRADES.forEach(function (g) {
    gradeNetBu[g.grade] = 0;
    gradeLiveCoverage[g.grade] = 0;
  });
  SIZES.forEach(function ({
    size
  }) {
    let _demandPicture$bySize, _inventoryBySize$size;
    const trueDemand = ((_demandPicture$bySize = demandPicture.bySize[size]) === null || _demandPicture$bySize === void 0 ? void 0 : _demandPicture$bySize.trueDemand) || 0;
    const usableInv = (inventoryBySize === null || inventoryBySize === void 0 || (_inventoryBySize$size = inventoryBySize[size]) === null || _inventoryBySize$size === void 0 ? void 0 : _inventoryBySize$size.usable) || 0;
    const grade = SIZE_TO_GRADE[size];
    // Purchased inbound — spread evenly across grade's sizes
    const gradeInbound = (inboundByGrade === null || inboundByGrade === void 0 ? void 0 : inboundByGrade[grade]) || 0;
    const sizesInGrade = SIZES.filter(function (s) {
      return SIZE_TO_GRADE[s.size] === grade;
    }).length || 1;
    const inboundShare = r2(gradeInbound / sizesInGrade);
    // Live/ungraded internal coverage — two separate factors
    const gradeSupply = (liveSupplyByGrade === null || liveSupplyByGrade === void 0 ? void 0 : liveSupplyByGrade[grade]) || {};
    const liveBu = gradeSupply.liveBu || 0; // boxes still live
    const ungradedBu = gradeSupply.ungradedBu || 0; // boxes steamed, not yet graded
    const liveCovBu = r2(liveBu * LIVE_FACTOR + ungradedBu * UNGRADED_FACTOR);
    const liveShare = r2(liveCovBu / sizesInGrade);
    if (grade) gradeLiveCoverage[grade] = r2((gradeLiveCoverage[grade] || 0) + liveShare);
    const netDemand = r2(Math.max(0, trueDemand - usableInv - inboundShare - liveShare));
    if (grade) gradeNetBu[grade] = r2((gradeNetBu[grade] || 0) + netDemand);
  });
  const byGrade = {};
  GRADES.forEach(function ({
    grade,
    buPerBox
  }) {
    const override = gradeOverrides[grade] ? Number(gradeOverrides[grade]) : null;
    const idealBu = gradeNetBu[grade] || 0,
      idealBoxes = Math.ceil(idealBu / buPerBox);
    byGrade[grade] = {
      idealBu: r2(idealBu),
      idealBoxes: idealBoxes,
      realisticBoxes: override != null ? override : idealBoxes,
      overridden: override != null,
      liveCoverage: r2(gradeLiveCoverage[grade] || 0)
    };
  });
  const totalIdealBu = r2(Object.values(gradeNetBu).reduce(function (s, v) {
    return s + v;
  }, 0));
  const totalIdealBoxes = Object.values(byGrade).reduce(function (s, g) {
    return s + g.idealBoxes;
  }, 0);
  const totalRealisticBoxes = Object.values(byGrade).reduce(function (s, g) {
    return s + g.realisticBoxes;
  }, 0);
  const totalLiveCoverage = r2(Object.values(gradeLiveCoverage).reduce(function (s, v) {
    return s + v;
  }, 0));
  return {
    byGrade: byGrade,
    totalIdealBu: totalIdealBu,
    totalIdealBoxes: totalIdealBoxes,
    totalRealisticBoxes: totalRealisticBoxes,
    totalLiveCoverage: totalLiveCoverage
  };
}
function computeTimingPlan({
  totalUsableBu,
  inboundByGrade,
  totalTrueDemandBu,
  nextWeekDemandBu,
  today,
  parsedDays,
  allDays,
  parsedWeeks,
  allWks
}) {
  const totalInboundBu = r2(Object.values(inboundByGrade || {}).reduce((s,v)=>s+(Number(v)||0), 0));
  const todayDate = new Date(today + 'T12:00:00');

  function isBusySeason(isoDate) {
    const m = new Date(isoDate + 'T12:00:00').getMonth() + 1;
    return m >= 4 && m <= 11;
  }
  function addDays(isoDate, n) {
    const d = new Date(isoDate + 'T12:00:00');
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  }
  function shippingDaysInRange(startISO, endISO) {
    let count = 0;
    let d = new Date(startISO + 'T12:00:00');
    const end = new Date(endISO + 'T12:00:00');
    while (d <= end) { if (d.getDay() !== 0 && d.getDay() !== 6) count++; d.setDate(d.getDate()+1); }
    return count;
  }
  function shippingDaysBefore(fromISO, toISO) {
    // shipping days from fromISO (inclusive) up to but NOT including toISO
    const days = [];
    let d = new Date(fromISO + 'T12:00:00');
    const end = new Date(toISO + 'T12:00:00');
    while (d < end) {
      const iso = d.toISOString().slice(0,10);
      if (d.getDay() !== 0 && d.getDay() !== 6) days.push(iso);
      d.setDate(d.getDate()+1);
    }
    return days;
  }
  function fourWeekAvgForDow(targetDow) {
    if (!parsedDays || !allDays) return 0;
    const matches = allDays
      .filter(d => d < today && new Date(d+'T12:00:00').getDay() === targetDow)
      .slice(-4);
    if (!matches.length) return 0;
    const totals = matches.map(d =>
      Object.values(parsedDays[d]||{}).reduce((s,v)=>s+(Number(v)||0),0)
    );
    return r2(totals.reduce((s,v)=>s+v,0) / totals.length);
  }
  function actualCommittedForDate(isoDate) {
    if (!parsedDays || !parsedDays[isoDate]) return 0;
    return r2(Object.values(parsedDays[isoDate]).reduce((s,v)=>s+(Number(v)||0),0));
  }
  function committedBeforeArrival(arrivalISO) {
    const days = shippingDaysBefore(today, arrivalISO);
    return r2(days.reduce((sum, d) => {
      const actual = actualCommittedForDate(d);
      const avg = fourWeekAvgForDow(new Date(d+'T12:00:00').getDay());
      // During holiday spikes, actual today orders may be much higher than normal.
      // Use avg for future days (haven't happened yet), actual only for today itself.
      return sum + (d === today ? Math.max(actual, avg) : avg);
    }, 0));
  }

  // Compute the right weekly demand for the SPECIFIC week the crabs will sell in.
  // This prevents holiday-week forecasts from inflating buys for wrong windows.
  function windowWeekDemand(arrivalISO, expiryISO) {
    // What week does this sellable window primarily fall in?
    const winMonday = mondayOfWeek(arrivalISO);

    // Use getWeekForecast (trailing avg + trend) as the base
    let weekBu = 0;
    if (parsedWeeks && allWks && allWks.length >= 4) {
      SIZES.forEach(function({ size }) {
        const fc = getWeekForecast(parsedWeeks, allWks, winMonday, size);
        weekBu += fc || 0;
      });
      weekBu = r2(weekBu);
    }

    // If we got no forecast (window week not in history yet), fall back to totalTrueDemandBu
    if (!weekBu) weekBu = totalTrueDemandBu;

    // Check for holiday in the window week and blend SWLY if needed
    const holiday = getHolidayForWeek(winMonday);
    if (holiday && parsedWeeks && parsedDays) {
      const swlyMon = getSWLYWeekMonday(winMonday, parsedDays);
      let swlyTotal = 0;
      for (let di = 0; di < 7; di++) {
        const d = new Date(swlyMon + 'T12:00:00');
        d.setDate(d.getDate() + di);
        const iso = d.toISOString().slice(0, 10);
        SIZES.forEach(function({ size }) { swlyTotal += getBu(parsedDays, iso, size) || 0; });
      }
      if (swlyTotal > 0) {
        const w = holiday.swlyWeight || 0.6;
        weekBu = r2(swlyTotal * w + weekBu * (1 - w));
      }
    }

    // Scale to the actual sellable days in this window (may span partial weeks)
    const sellDays = shippingDaysInRange(arrivalISO, expiryISO);
    return r2((weekBu / 5) * sellDays);
  }

  // ── BUY WINDOW ─────────────────────────────────────────────────────────────
  const todayDow = todayDate.getDay();
  const busy = isBusySeason(today);

  let buyDate, buyLabel, arrivalDate;
  if (busy && (todayDow === 1 || todayDow === 2)) {
    const daysToWed = todayDow === 1 ? 2 : 1;
    buyDate = addDays(today, daysToWed);
    buyLabel = 'Wednesday';
    arrivalDate = addDays(buyDate, 2); // arrives Friday
  } else {
    const daysToMon = todayDow === 1 ? 7 : (8 - todayDow) % 7 || 7;
    buyDate = addDays(today, daysToMon);
    buyLabel = 'Monday';
    arrivalDate = addDays(buyDate, 2); // arrives Wednesday
  }

  // ── SELLABLE WINDOW ────────────────────────────────────────────────────────
  let expiryDate = addDays(arrivalDate, 9);
  const expiryDow = new Date(expiryDate + 'T12:00:00').getDay();
  if (expiryDow === 6) expiryDate = addDays(expiryDate, -1);
  if (expiryDow === 0) expiryDate = addDays(expiryDate, -2);

  const sellableDays = shippingDaysInRange(arrivalDate, expiryDate);

  // ── DEMAND: use window-specific weekly forecast, not current inflated week ──
  const windowDemandBu = windowWeekDemand(arrivalDate, expiryDate);
  const dailyBurnBu = sellableDays > 0 ? r2(windowDemandBu / sellableDays) : 0;

  // ── NET ON HAND AT ARRIVAL ─────────────────────────────────────────────────
  const totalAvail = r2(totalUsableBu + totalInboundBu);
  const committedBefore = committedBeforeArrival(arrivalDate);
  const netOnHandAtArrival = r2(Math.max(0, totalAvail - committedBefore));

  // ── BUY NEED ───────────────────────────────────────────────────────────────
  const rawBuyNeedBu = r2(Math.max(0, windowDemandBu - netOnHandAtArrival));
  const bufferPct = 0.10;
  const bufferedBuyBu = r2(rawBuyNeedBu * (1 + bufferPct));
  const buyBoxes = Math.ceil(bufferedBuyBu);

  // ── HOLIDAY NOTE (informational only) ─────────────────────────────────────
  const windowHoliday = getHolidayForWeek(mondayOfWeek(arrivalDate));
  const upcomingHoliday = windowHoliday
    ? { name: windowHoliday.name, date: windowHoliday.dateIso, daysAway: Math.round((new Date(windowHoliday.dateIso+'T12:00:00') - todayDate) / 86400000) }
    : null;

  return {
    buyDate, buyLabel, arrivalDate, expiryDate, sellableDays,
    dailyBurnBu, windowDemandBu,
    totalAvail, committedBeforeArrival: committedBefore, netOnHandAtArrival,
    rawBuyNeedBu, bufferedBuyBu, buyBoxes, bufferPct,
    noBuyNeeded: bufferedBuyBu <= 0,
    upcomingHoliday,
    // Legacy compat
    monBuyBoxes: buyBoxes, bufferedMonBuyBu: bufferedBuyBu, monBuyNeededBu: rawBuyNeedBu,
    needsWedBuy: busy && buyLabel === 'Wednesday',
    windows: [{ label: buyLabel, date: buyDate }],
    coverDays: sellableDays,
    projNextWeekDemandBu: windowDemandBu,
    shippingDaysToMon: shippingDaysBefore(today, arrivalDate).length,
    remainingThisWeekDemandBu: committedBefore,
    projectedOnHandMonday: netOnHandAtArrival,
    shortfallBu: 0, needsSupplemental: false
  };
}


let _uid = 1;
const uid = function uid() {
  return "".concat(Date.now(), "-").concat(_uid++);
};
const makeLot = function makeLot() {
  const o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    id: uid(),
    size: o.size !== undefined ? o.size : "",
    steamDate: o.steamDate || todayISO(),
    bushels: o.bushels || "",
    ...o
  };
};
const makePurchase = function makePurchase() {
  const o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    id: uid(),
    date: todayISO(),
    vendorId: "",
    sourceType: "Louisiana",
    buyWindow: "Monday",
    boxesRequested: "",
    boxesReceived: "",
    gradeBreakdown: {
      "1s": "",
      "2s": "",
      "3s": ""
    },
    femalesShort: false,
    notes: "",
    rawNote: "",
    ...o
  };
};
const makeVendor = function makeVendor() {
  const o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    id: uid(),
    name: "",
    sourceType: "Louisiana",
    typicalWindow: "Monday",
    reliable: true,
    goodForFemales: false,
    emergencyFillIn: false,
    notes: "",
    ...o
  };
};

// ─── LOGO ─────────────────────────────────────────────────────────────────────
const CrabSVG = function CrabSVG({
  size = 36
}) {
  return <svg width={size} height={size} viewBox="0 0 48 48" fill="none">{<ellipse cx="24" cy="26" rx="10" ry="8" fill={B.red} />}{<ellipse cx="24" cy="18" rx="6" ry="5" fill={B.red} />}{<circle cx="21" cy="16" r="1.5" fill={B.navy} />}{<circle cx="27" cy="16" r="1.5" fill={B.navy} />}{<path d="M14 22 Q8 18 7 14 Q10 12 13 16 Q15 19 15 22Z" fill={B.red} />}{<path d="M7 14 Q4 10 6 8 Q9 9 8 13Z" fill={B.redLight} />}{<path d="M34 22 Q40 18 41 14 Q38 12 35 16 Q33 19 33 22Z" fill={B.red} />}{<path d="M41 14 Q44 10 42 8 Q39 9 40 13Z" fill={B.redLight} />}{<line x1="15" y1="24" x2="8" y2="22" stroke={B.red} strokeWidth="2" strokeLinecap="round" />}{<line x1="15" y1="27" x2="7" y2="27" stroke={B.red} strokeWidth="2" strokeLinecap="round" />}{<line x1="15" y1="30" x2="9" y2="32" stroke={B.red} strokeWidth="2" strokeLinecap="round" />}{<line x1="33" y1="24" x2="40" y2="22" stroke={B.red} strokeWidth="2" strokeLinecap="round" />}{<line x1="33" y1="27" x2="41" y2="27" stroke={B.red} strokeWidth="2" strokeLinecap="round" />}{<line x1="33" y1="30" x2="39" y2="32" stroke={B.red} strokeWidth="2" strokeLinecap="round" />}</svg>;
};
const CrabPlaceLogo = function CrabPlaceLogo({
  size = 36
}) {
  return <div style={{
    display: "flex",
    alignItems: "center",
    gap: 8
  }}>{<CrabSVG size={size} />}{<div style={{
      lineHeight: 1.1
    }}>{<div style={{
        color: B.red,
        fontWeight: 900,
        fontSize: Math.max(12, size * 0.42),
        letterSpacing: .8,
        fontFamily: "Georgia,serif",
        textTransform: "uppercase"
      }}>Crab Place</div>}{<div style={{
        color: "rgba(255,255,255,.55)",
        fontWeight: 600,
        fontSize: Math.max(8, size * 0.21),
        letterSpacing: 2,
        textTransform: "uppercase"
      }}>Purchasing Planner</div>}</div>}</div>;
};

// ─── SHARED UI ATOMS ──────────────────────────────────────────────────────────
const Card = function Card({
  children,
  style = {}
}) {
  return <div style={{
    background: B.cardBg,
    border: "1px solid ".concat(B.cardBorder),
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 6px rgba(26,46,74,.06)",
    ...style
  }}>{children}</div>;
};
const CardHeader = function CardHeader({
  children,
  sub,
  right
}) {
  return <div style={{
    padding: "12px 16px",
    borderBottom: "1px solid ".concat(B.rowSep),
    background: B.rowAlt,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start"
  }}>{<div>{<div style={{
        color: B.navy,
        fontWeight: 700,
        fontSize: 14
      }}>{children}</div>}{sub && <div style={{
        color: B.textLight,
        fontSize: 11,
        marginTop: 2
      }}>{sub}</div>}</div>}{right && <div>{right}</div>}</div>;
};
const Label = function Label({
  children,
  required = false
}) {
  return <div style={{
    color: B.textMid,
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 4,
    letterSpacing: .3
  }}>{children}{required && <span style={{
      color: B.red,
      marginLeft: 2
    }}>*</span>}</div>;
};
const Input = function Input({
  value,
  onChange,
  placeholder = "",
  type = "text",
  style = {},
  ...rest
}) {
  return <input type={type} value={value == null ? "" : value} onChange={onChange} placeholder={placeholder} style={{
    width: "100%",
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid ".concat(B.cardBorder),
    fontSize: 13,
    color: B.textDark,
    background: B.white,
    outline: "none",
    boxSizing: "border-box",
    ...style
  }} {...rest} />;
};
const Sel = function Sel({
  value,
  onChange,
  children,
  style = {}
}) {
  return <select value={value} onChange={onChange} style={{
    width: "100%",
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid ".concat(B.cardBorder),
    fontSize: 13,
    color: B.textDark,
    background: B.white,
    outline: "none",
    boxSizing: "border-box",
    ...style
  }}>{children}</select>;
};
const Textarea = function Textarea({
  value,
  onChange,
  placeholder = "",
  rows = 3
}) {
  return <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{
    width: "100%",
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid ".concat(B.cardBorder),
    fontSize: 12,
    color: B.textDark,
    background: B.white,
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
    lineHeight: 1.5
  }} />;
};
const Checkbox = function Checkbox({
  checked,
  onChange,
  label
}) {
  return <label style={{
    display: "flex",
    alignItems: "center",
    gap: 7,
    cursor: "pointer",
    fontSize: 12,
    color: B.textMid
  }}>{<input type="checkbox" checked={checked} onChange={onChange} style={{
      width: 14,
      height: 14,
      accentColor: B.navy
    }} />}{label}</label>;
};
const Btn = function Btn({
  onClick,
  children,
  variant = "primary",
  small = false,
  style = {}
}) {
  const base = {
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: small ? 12 : 13,
    padding: small ? "6px 12px" : "9px 18px",
    ...style
  };
  const v = {
    primary: {
      background: B.navy,
      color: B.white
    },
    red: {
      background: B.red,
      color: B.white
    },
    ghost: {
      background: B.rowAlt,
      color: B.textMid,
      border: "1px solid ".concat(B.cardBorder)
    },
    danger: {
      background: "#fef2f2",
      color: B.alertRed,
      border: "1px solid ".concat(B.warnBorder)
    },
    green: {
      background: "#f0fdf4",
      color: B.green,
      border: "1px solid ".concat(B.okBorder)
    }
  };
  return <button onClick={onClick} style={{
    ...base,
    ...v[variant]
  }}>{children}</button>;
};
const Tag = function Tag({
  label,
  color,
  onRemove
}) {
  return <span style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "".concat(color, "18"),
    border: "1px solid ".concat(color, "44"),
    color: color,
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: 6
  }}>{label}{onRemove && <span onClick={onRemove} style={{
      cursor: "pointer",
      opacity: .6,
      marginLeft: 2
    }}>✕</span>}</span>;
};
const ColHead = function ColHead({
  label,
  color
}) {
  return <div style={{
    borderBottom: "3px solid ".concat(color),
    paddingBottom: 4
  }}>{<div style={{
      color: "rgba(255,255,255,.75)",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: .7,
      lineHeight: 1.3
    }}>{label}</div>}</div>;
};
const BuCell = function BuCell({
  value,
  color,
  dim = false,
  prefix = "",
  missing = false
}) {
  return <div style={{
    textAlign: "center",
    padding: "2px 0"
  }}>{missing ? <span style={{
      color: B.textLight,
      fontSize: 11,
      fontStyle: "italic"
    }}>No LY data</span> : value != null && value !== 0 ? <>{<span style={{
        color: dim ? "#7a90a8" : color,
        fontWeight: 700,
        fontSize: 15
      }}>{prefix}{fb(value)}</span>}{<span style={{
        color: B.textLight,
        fontSize: 10,
        marginLeft: 3
      }}>bu</span>}</> : <span style={{
      color: B.rowSep,
      fontSize: 16
    }}>—</span>}</div>;
};
const BuCellInv = function BuCellInv({
  value,
  dim = false,
  prefix = "",
  accent = false
}) {
  return <div style={{
    textAlign: "center"
  }}>{value != null && value !== 0 ? <>{<span style={{
        color: accent ? "#f87171" : dim ? "rgba(255,255,255,.5)" : B.white,
        fontWeight: 700,
        fontSize: 15
      }}>{prefix}{fb(value)}</span>}{<span style={{
        color: "rgba(255,255,255,.35)",
        fontSize: 10,
        marginLeft: 3
      }}>bu</span>}</> : <span style={{
      color: "rgba(255,255,255,.2)",
      fontSize: 15
    }}>—</span>}</div>;
};
const ChartToggle = function ChartToggle({
  show,
  onToggle,
  label
}) {
  return <button onClick={onToggle} style={{
    width: "100%",
    background: B.cardBg,
    border: "1px solid ".concat(B.cardBorder),
    color: B.textMid,
    padding: "11px 16px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 13,
    marginBottom: show ? 0 : 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    boxShadow: "0 1px 4px rgba(0,0,0,.05)"
  }}>{show ? "▲ Hide Chart" : "\u25BC ".concat(label)}</button>;
};

// ─── APP TABS ─────────────────────────────────────────────────────────────────
// ─── WEEK PACE TAB ────────────────────────────────────────────────────────────
// Live view: today→Friday committed vs last week same days
// Auto-refreshes every 5 minutes via direct Neon query
function WeekPaceTab({ enrichedLots, demandBySize, paceData, onRefresh }) {
  const today = todayISO();
  const todayDate = new Date(today + 'T12:00:00');

  // Friday of current week
  const fridayDate = new Date(todayDate);
  const fridayDow = fridayDate.getDay();
  const daysToFriday = fridayDow === 0 ? 5 : (fridayDow <= 5 ? 5 - fridayDow : 6);
  fridayDate.setDate(fridayDate.getDate() + daysToFriday);
  const fridayISO = fridayDate.toISOString().slice(0, 10);

  // Remaining ship days this week (today → Friday)
  const remainingDays = [];
  const cur = new Date(todayDate);
  while (cur <= fridayDate) {
    const dow = cur.getDay();
    if (dow >= 1 && dow <= 5) remainingDays.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }

  // Find clean comparison week (skip holiday weeks)
  function getComparisonWeekMonday() {
    let weeksBack = 1;
    let holidayName = null;
    while (weeksBack <= 6) {
      const candDate = new Date(new Date(mondayOfWeek(today) + 'T12:00:00'));
      candDate.setDate(candDate.getDate() - (7 * weeksBack));
      const candISO = candDate.toISOString().slice(0, 10);
      const holiday = getHolidayForWeek(candISO);
      if (!holiday) return { monday: candISO, weeksBack, holidayName };
      if (weeksBack === 1) holidayName = holiday.name;
      weeksBack++;
    }
    return { monday: null, weeksBack: 1, holidayName };
  }

  const { monday: compMonday, weeksBack, holidayName } = getComparisonWeekMonday();

  // Compute totals
  const thisTotal = Object.values(paceData.thisWeek).reduce(function(s, v) { return r2(s + v); }, 0);
  const lastTotal = Object.values(paceData.lastWeek).reduce(function(s, v) { return r2(s + v); }, 0);
  const pctDiff = lastTotal > 0 ? Math.round((thisTotal - lastTotal) / lastTotal * 100) : null;

  // Helper: is a size expiring this week?
  function sizeExpiringThisWeek(size) {
    return (enrichedLots || []).some(function(lot) {
      if (lot.size !== size || !lot.usable || !lot.steamDate) return false;
      const cutoff = (function() {
        const day9 = _addDays(lot.steamDate, 9);
        const dow = _dow(day9);
        if (dow === 6) return _addDays(day9, -1);
        if (dow === 0) return _addDays(day9, -2);
        return day9;
      })();
      return cutoff >= today && cutoff <= fridayISO;
    });
  }

  // Day range label
  const firstDay = remainingDays.length > 0 ? new Date(remainingDays[0] + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' }) : '';
  const lastDay = remainingDays.length > 1 ? new Date(remainingDays[remainingDays.length - 1] + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' }) : firstDay;
  const rangeLabel = firstDay === lastDay ? firstDay : firstDay + ' – ' + lastDay;
  const compLabel = weeksBack === 1 ? 'Last week' : weeksBack + ' weeks ago';
  const fridayLabel = fridayDate.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' });

  return <div style={{ paddingTop: 4 }}>
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 15, color: B.navy }}>Sales Pace — {rangeLabel}</div>
        <div style={{ fontSize: 11, color: B.textLight, marginTop: 2 }}>
          Steamed orders committed {rangeLabel} vs {compLabel} same period · auto-refreshes every 5 min
          {holidayName ? <span style={{ color: B.amber, marginLeft: 6 }}>⚠️ Last week was {holidayName} — using {weeksBack === 2 ? '2 weeks ago' : weeksBack + ' weeks ago'}</span> : null}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {paceData.lastUpdated && <div style={{ fontSize: 10, color: B.textLight }}>Updated {paceData.lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>}
        <button onClick={onRefresh} style={{ background: B.navy, color: '#fff', border: 'none', borderRadius: 7, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>↻ Refresh</button>
      </div>
    </div>

    {paceData.error && <div style={{ background: B.warnBg, border: '1px solid ' + B.warnBorder, borderRadius: 8, padding: '10px 14px', color: B.alertRed, fontSize: 12, marginBottom: 12 }}>Error loading data: {paceData.error}</div>}

    {/* Totals card */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
      <div style={{ background: B.infoBg, border: '1px solid ' + B.infoBorder, borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: B.textMid, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>This Week</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: B.navy }}>{paceData.loading ? '…' : fb(thisTotal)} <span style={{ fontSize: 13, fontWeight: 600, color: B.textMid }}>bu</span></div>
        <div style={{ fontSize: 11, color: B.textLight, marginTop: 2 }}>{rangeLabel}</div>
      </div>
      <div style={{ background: B.okBg, border: '1px solid ' + B.okBorder, borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: B.textMid, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{compLabel}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: B.green }}>{paceData.loading ? '…' : fb(lastTotal)} <span style={{ fontSize: 13, fontWeight: 600, color: B.textMid }}>bu</span></div>
        <div style={{ fontSize: 11, color: B.textLight, marginTop: 2 }}>same {remainingDays.length} day{remainingDays.length !== 1 ? 's' : ''}</div>
      </div>
    </div>

    {/* Pace signal */}
    {!paceData.loading && lastTotal > 0 && <div style={{
      background: pctDiff >= 0 ? B.okBg : B.warnBg,
      border: '1px solid ' + (pctDiff >= 0 ? B.okBorder : B.warnBorder),
      borderRadius: 8, padding: '10px 14px', marginBottom: 14,
      display: 'flex', alignItems: 'center', gap: 10
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: pctDiff >= 0 ? B.green : B.alertRed }}>
        {pctDiff >= 0 ? '+' : ''}{pctDiff}%
      </div>
      <div style={{ fontSize: 12, color: B.textDark }}>
        {pctDiff >= 10 ? 'Strong pace — dial back subs/upgrades'
          : pctDiff >= 5 ? 'Ahead of pace — monitor closely'
          : pctDiff <= -15 ? 'Significantly behind — push subs/upgrades now on expiring inventory'
          : pctDiff <= -5 ? 'Slightly behind pace — watch expiring sizes'
          : 'Tracking close to last week'}
      </div>
    </div>}

    {/* Size breakdown */}
    <div style={{ background: B.cardBg, border: '1px solid ' + B.cardBorder, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 70px', background: B.navy, padding: '8px 14px', gap: 8 }}>
        {['Size', 'This Week', compLabel, 'Diff'].map(function(h, i) {
          return <div key={i} style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,.7)', textAlign: i > 0 ? 'right' : 'left' }}>{h}</div>;
        })}
      </div>
      {SIZES.map(function({ size }, idx) {
        const tw = paceData.thisWeek[size] || 0;
        const lw = paceData.lastWeek[size] || 0;
        if (tw === 0 && lw === 0) return null;
        const diff = r2(tw - lw);
        const expiring = sizeExpiringThisWeek(size);
        const behind = diff < -0.5 && expiring;
        return <div key={size} style={{
          display: 'grid', gridTemplateColumns: '1fr 90px 90px 70px',
          padding: '9px 14px', gap: 8,
          background: idx % 2 === 0 ? '#fff' : B.rowAlt,
          borderTop: '1px solid ' + B.rowSep,
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: SIZE_COLORS[size] || B.textLight, flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: B.textDark }}>{size}</span>
            {expiring && <span style={{ fontSize: 9, fontWeight: 700, background: B.urgentBg, color: B.urgentText, borderRadius: 4, padding: '1px 5px' }}>EXPIRING</span>}
            {behind && <span style={{ fontSize: 9, fontWeight: 700, background: B.warnBg, color: B.alertRed, borderRadius: 4, padding: '1px 5px' }}>PUSH</span>}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: B.navy, textAlign: 'right' }}>{paceData.loading ? '…' : fb(tw)}</div>
          <div style={{ fontSize: 12, color: B.textMid, textAlign: 'right' }}>{paceData.loading ? '…' : fb(lw)}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: diff > 0 ? B.green : diff < 0 ? B.alertRed : B.textLight, textAlign: 'right' }}>
            {paceData.loading ? '…' : (diff > 0 ? '+' : '') + fb(diff)}
          </div>
        </div>;
      })}
      {/* Total row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 70px', padding: '10px 14px', gap: 8, background: B.rowAlt, borderTop: '2px solid ' + B.navy, alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: B.navy }}>Total</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: B.navy, textAlign: 'right' }}>{paceData.loading ? '…' : fb(thisTotal)}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: B.textMid, textAlign: 'right' }}>{paceData.loading ? '…' : fb(lastTotal)}</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: (thisTotal - lastTotal) > 0 ? B.green : B.alertRed, textAlign: 'right' }}>
          {paceData.loading ? '…' : ((thisTotal - lastTotal) > 0 ? '+' : '') + fb(r2(thisTotal - lastTotal))}
        </div>
      </div>
    </div>

    {/* Last updated footer */}
    <div style={{ textAlign: 'center', fontSize: 10, color: B.textLight, marginTop: 10 }}>
      Auto-refreshes every 5 min — {paceData.loading ? 'Loading…' : 'Last updated ' + (paceData.lastUpdated ? paceData.lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'never')}
    </div>
  </div>;
}

function AppTabs({
  tab,
  onChange
}) {
  return <div style={{
    display: "flex",
    gap: 4,
    background: B.rowAlt,
    borderRadius: 10,
    padding: 4,
    marginBottom: 20,
    border: "1px solid ".concat(B.cardBorder)
  }}>{[{
      val: "snapshot",
      label: "📊 Daily Snapshot"
    }, {
      val: "planner",
      label: "🧾 Purchasing Planner"
    }, {
      val: "forecaster",
      label: "📈 Sales Pace"
    }, {
      val: "live",
      label: "🦀 Live / Ungraded"
    }, {
      val: "takt",
      label: "📋 Hard Crab TAKT"
    }, {
      val: "ops",
      label: "📌 Daily Ops Planning"
    }].map(function (t) {
      return <button key={t.val} onClick={function onClick() {
        return onChange(t.val);
      }} style={{
        flex: 1,
        padding: "10px 0",
        border: "none",
        borderRadius: 8,
        cursor: "pointer",
        fontWeight: 700,
        fontSize: 13,
        background: tab === t.val ? B.navy : "transparent",
        color: tab === t.val ? B.white : B.textMid,
        boxShadow: tab === t.val ? "0 2px 8px rgba(26,46,74,.2)" : "none",
        transition: "all .15s"
      }}>{t.label}</button>;
    })}</div>;
}
function ViewToggle({
  mode,
  onChange
}) {
  return <div style={{
    display: "flex",
    gap: 3,
    background: B.rowAlt,
    borderRadius: 9,
    padding: 3,
    marginBottom: 18,
    border: "1px solid ".concat(B.cardBorder)
  }}>{[["📅 Weekly", "weekly"], ["📆 Daily", "daily"]].map(function ([l, v]) {
      return <button key={v} onClick={function onClick() {
        return onChange(v);
      }} style={{
        flex: 1,
        padding: "9px 0",
        border: "none",
        borderRadius: 7,
        cursor: "pointer",
        fontWeight: 700,
        fontSize: 12,
        background: mode === v ? B.navyLight : "transparent",
        color: mode === v ? B.white : B.textMid,
        transition: "all .15s"
      }}>{l}</button>;
    })}</div>;
}

// ─── FORECASTER UI ────────────────────────────────────────────────────────────
function Navigator({
  allKeys,
  sel,
  onChange,
  labelFn,
  helperText,
  countLabel
}) {
  const idx = allKeys.indexOf(sel);
  const nb = function nb(en, lbl, oc) {
    return <button onClick={oc} disabled={!en} style={{
      background: en ? B.navy : B.rowAlt,
      border: "1px solid ".concat(en ? B.navy : B.cardBorder),
      color: en ? B.white : B.rowSep,
      padding: "12px 16px",
      borderRadius: 10,
      cursor: en ? "pointer" : "default",
      fontSize: 22,
      fontWeight: 700,
      lineHeight: 1,
      flexShrink: 0,
      minWidth: 48
    }}>{lbl}</button>;
  };
  return <>{<div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 6
    }}>{nb(idx > 0, "‹", function () {
        return onChange(allKeys[idx - 1]);
      })}{<div style={{
        flex: 1,
        background: B.navy,
        borderRadius: 12,
        padding: "14px 16px",
        textAlign: "center",
        boxShadow: "0 3px 14px rgba(26,46,74,.18)"
      }}>{<div style={{
          color: B.white,
          fontWeight: 700,
          fontSize: 15,
          fontFamily: "Georgia,serif",
          lineHeight: 1.3
        }}>{labelFn(sel)}</div>}{<div style={{
          color: "rgba(255,255,255,.45)",
          fontSize: 11,
          marginTop: 4
        }}>{countLabel} {idx + 1} of {allKeys.length}</div>}</div>}{nb(idx < allKeys.length - 1, "›", function () {
        return onChange(allKeys[idx + 1]);
      })}</div>}{helperText && <div style={{
      color: B.textLight,
      fontSize: 11,
      textAlign: "center",
      marginBottom: 18,
      marginTop: 5,
      lineHeight: 1.5
    }}>{helperText}</div>}</>;
}
function SummaryBar({
  cols
}) {
  return <div style={{
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: 10,
    marginBottom: 22
  }}>{cols.map(function (c) {
      return <div key={c.label} style={{
        background: c.featured ? B.navy : B.cardBg,
        border: "1px solid ".concat(c.featured ? B.navy : B.cardBorder),
        borderRadius: 12,
        padding: "15px 10px",
        textAlign: "center",
        boxShadow: c.featured ? "0 4px 14px rgba(26,46,74,.2)" : "0 1px 5px rgba(0,0,0,.05)"
      }}>{<div style={{
          color: c.featured ? "rgba(255,255,255,.6)" : B.textLight,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: .9,
          marginBottom: 7,
          textTransform: "uppercase",
          lineHeight: 1.4
        }}>{c.label}</div>}{c.value != null && c.value !== 0 ? <>{<div style={{
            color: c.featured ? B.white : c.color,
            fontWeight: 800,
            fontSize: 21
          }}>{c.prefix || ""}{fb(c.value)}</div>}{<div style={{
            color: c.featured ? "rgba(255,255,255,.4)" : B.textLight,
            fontSize: 10,
            marginTop: 3
          }}>bushels</div>}</> : <div style={{
          color: c.featured ? "rgba(255,255,255,.25)" : B.rowSep,
          fontSize: 20,
          marginTop: 6
        }}>—</div>}</div>;
    })}</div>;
}
function WeeklySummaryBar({
  weeks,
  allWks,
  sel
}) {
  const t = weekTotalRow(weeks, allWks, sel);
  return <SummaryBar cols={[{
    label: "Selected Week",
    value: t.sel,
    color: B.colSel,
    featured: true
  }, {
    label: "4-Week Average",
    value: t.avg4,
    color: B.colAvg
  }, {
    label: "Same Week Last Year",
    value: t.swly,
    color: B.colLY
  }, {
    label: "Next Week Forecast",
    value: t.fc,
    color: B.colFC,
    prefix: "~"
  }]} />;
}
function DailySummaryBar({
  days,
  allDays,
  sel
}) {
  const t = dayTotalRow(days, allDays, sel);
  return <SummaryBar cols={[{
    label: "Selected Date",
    value: t.sel,
    color: B.colSel,
    featured: true
  }, {
    label: "7-Day Average",
    value: t.avg7,
    color: B.colAvg
  }, {
    label: "Same Day Last Year",
    value: t.sdly,
    color: B.colLY
  }, {
    label: "Next Day Forecast",
    value: t.fc,
    color: B.colFC,
    prefix: "~"
  }]} />;
}
function PlanTable({
  rows,
  totSel,
  totAvg,
  totLy,
  totFc,
  hdrs,
  hClr
}) {
  const C = ["28%", "18%", "18%", "18%", "18%"];
  return <Card style={{
    marginBottom: 22
  }}>{<div style={{
      display: "grid",
      gridTemplateColumns: C.join(" "),
      padding: "12px 18px",
      background: B.navy
    }}>{<div style={{
        color: "rgba(255,255,255,.45)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: .8,
        alignSelf: "flex-end"
      }}>SIZE</div>}{hdrs.map(function (h, i) {
        return <ColHead key={h} label={h} color={hClr[i]} />;
      })}</div>}{rows.map(function ({
      size,
      color,
      selBu,
      avg,
      lyBu,
      lyMissing,
      fc
    }, ri) {
      return <div key={size} style={{
        display: "grid",
        gridTemplateColumns: C.join(" "),
        padding: "15px 18px",
        background: ri % 2 === 0 ? B.cardBg : B.rowAlt,
        borderBottom: "1.5px solid ".concat(B.rowSep),
        alignItems: "center"
      }}>{<div style={{
          display: "flex",
          alignItems: "center",
          gap: 10
        }}>{<span style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
            display: "inline-block",
            boxShadow: "0 0 0 3px ".concat(color, "22")
          }} />}{<span style={{
            color: B.textDark,
            fontWeight: 700,
            fontSize: 14
          }}>{size}</span>}</div>}{<BuCell value={selBu} color={B.colSel} />}{<BuCell value={avg} color={B.colAvg} dim />}{<BuCell value={lyBu} color={B.colLY} dim missing={lyMissing} />}{<BuCell value={fc} color={B.colFC} prefix="~" />}</div>;
    })}{<div style={{
      display: "grid",
      gridTemplateColumns: C.join(" "),
      padding: "14px 18px",
      background: B.navy,
      alignItems: "center"
    }}>{<div style={{
        color: B.white,
        fontWeight: 800,
        fontSize: 13,
        letterSpacing: .5
      }}>TOTAL</div>}{<BuCellInv value={totSel} />}{<BuCellInv value={totAvg} dim />}{<BuCellInv value={totLy} dim />}{<BuCellInv value={totFc} prefix="~" accent />}</div>}</Card>;
}
function WeekPlanTable({
  weeks,
  allWks,
  sel
}) {
  const rows = SIZES.map(function ({
    size,
    color
  }) {
    let _swly$bu;
    const swly = getSWLY(weeks, sel, size);
    return {
      size: size,
      color: color,
      selBu: getBu(weeks, sel, size),
      avg: get4wkAvg(weeks, allWks, sel, size),
      lyBu: (_swly$bu = swly === null || swly === void 0 ? void 0 : swly.bu) !== null && _swly$bu !== void 0 ? _swly$bu : null,
      lyMissing: swly === null,
      fc: getWeekForecast(weeks, allWks, sel, size)
    };
  });
  const totSel = r2(rows.reduce(function (s, r) {
      return s + r.selBu;
    }, 0)),
    totAvg = r2(rows.reduce(function (s, r) {
      return s + r.avg;
    }, 0));
  const totLy = rows.some(function (r) {
    return r.lyBu != null;
  }) ? r2(rows.reduce(function (s, r) {
    return s + (r.lyBu || 0);
  }, 0)) : null;
  const totFc = rows.some(function (r) {
    return r.fc != null;
  }) ? r2(rows.reduce(function (s, r) {
    return s + (r.fc || 0);
  }, 0)) : null;
  return <PlanTable rows={rows} totSel={totSel} totAvg={totAvg} totLy={totLy} totFc={totFc} hdrs={["Selected Week", "4-Week Average", "Same Week Last Year", "Next Week Forecast"]} hClr={[B.colSel, B.colAvg, B.colLY, B.colFC]} />;
}
function DayPlanTable({
  days,
  allDays,
  sel
}) {
  const rows = SIZES.map(function ({
    size,
    color
  }) {
    let _sdly$bu;
    const sdly = getSDLY(days, sel, size);
    return {
      size: size,
      color: color,
      selBu: getBu(days, sel, size),
      avg: get7dayAvg(days, allDays, sel, size),
      lyBu: (_sdly$bu = sdly === null || sdly === void 0 ? void 0 : sdly.bu) !== null && _sdly$bu !== void 0 ? _sdly$bu : null,
      lyMissing: sdly === null,
      fc: getDayForecast(days, allDays, sel, size)
    };
  });
  const totSel = r2(rows.reduce(function (s, r) {
      return s + r.selBu;
    }, 0)),
    totAvg = r2(rows.reduce(function (s, r) {
      return s + r.avg;
    }, 0));
  const totLy = rows.some(function (r) {
    return r.lyBu != null;
  }) ? r2(rows.reduce(function (s, r) {
    return s + (r.lyBu || 0);
  }, 0)) : null;
  const totFc = rows.some(function (r) {
    return r.fc != null;
  }) ? r2(rows.reduce(function (s, r) {
    return s + (r.fc || 0);
  }, 0)) : null;
  return <PlanTable rows={rows} totSel={totSel} totAvg={totAvg} totLy={totLy} totFc={totFc} hdrs={["Selected Date", "7-Day Average", "Same Day Last Year", "Next Day Forecast"]} hClr={[B.colSel, B.colAvg, B.colLY, B.colFC]} />;
}
function TrendChart({
  data,
  title
}) {
  const _useState = useState(null),
    _useState2 = _useState,
    tooltip = _useState2[0],
    setTooltip = _useState2[1];
  const W = 560,
    H = 170,
    PAD = {
      top: 8,
      right: 8,
      bottom: 28,
      left: 38
    };
  const plotW = W - PAD.left - PAD.right,
    plotH = H - PAD.top - PAD.bottom;
  const maxVal = Math.max.apply(Math, [1].concat([...data.map(function (d) {
    return SIZES.reduce(function (s, {
      size
    }) {
      return s + (d[size] || 0);
    }, 0);
  })]));
  const yTicks = [0, r2(maxVal * .25), r2(maxVal * .5), r2(maxVal * .75), r2(maxVal)];
  const barW = Math.max(4, plotW / data.length - 3);
  const barX = function barX(i) {
    return PAD.left + i * (plotW / data.length) + (plotW / data.length - barW) / 2;
  };
  const yPx = function yPx(v) {
    return PAD.top + plotH - v / maxVal * plotH;
  };
  return <Card style={{
    padding: "18px 16px",
    marginBottom: 16
  }}>{<div style={{
      color: B.textMid,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 1,
      marginBottom: 12,
      textTransform: "uppercase"
    }}>{title}</div>}{<div style={{
      overflowX: "auto"
    }}>{<svg viewBox={"0 0 ".concat(W, " ").concat(H)} style={{
        width: "100%",
        minWidth: 280,
        display: "block"
      }}>{yTicks.map(function (v, i) {
          const y = yPx(v);
          return <g key={i}>{<line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke={B.divider} strokeDasharray="3 3" strokeWidth={.8} />}{<text x={PAD.left - 4} y={y + 3} textAnchor="end" fontSize={8} fill={B.textLight}>{fb(v)}</text>}</g>;
        })}{data.map(function (d, i) {
          let yOff = 0;
          return <g key={i} onMouseEnter={function onMouseEnter() {
            return setTooltip({
              i: i,
              d: d,
              x: barX(i) + barW / 2,
              y: yPx(SIZES.reduce(function (s, {
                size
              }) {
                return s + (d[size] || 0);
              }, 0))
            });
          }} onMouseLeave={function onMouseLeave() {
            return setTooltip(null);
          }}>{SIZES.slice().reverse().map(function ({
              size,
              color
            }) {
              const v = d[size] || 0;
              if (!v) return null;
              const bh = v / maxVal * plotH;
              const by = yPx(yOff + v);
              yOff += v;
              return <rect key={size} x={barX(i)} y={by} width={barW} height={bh} fill={color} rx={1} />;
            })}{<text x={barX(i) + barW / 2} y={H - PAD.bottom + 10} textAnchor="middle" fontSize={8} fill={B.textLight}>{d.label}</text>}</g>;
        })}{tooltip && function () {
          const total = r2(SIZES.reduce(function (s, {
            size
          }) {
            return s + (tooltip.d[size] || 0);
          }, 0));
          const tx = Math.min(tooltip.x, W - PAD.right - 90);
          const ty = Math.max(PAD.top, tooltip.y - 8);
          return <g>{<rect x={tx} y={ty} width={88} height={14 + SIZES.length * 12} rx={4} fill={B.white} stroke={B.cardBorder} strokeWidth={1} />}{<text x={tx + 6} y={ty + 11} fontSize={8} fontWeight="700" fill={B.navy}>{tooltip.d.label} — {fb(total)} bu</text>}{SIZES.map(function ({
              size,
              color
            }, si) {
              return <text key={size} x={tx + 6} y={ty + 11 + (si + 1) * 12} fontSize={8} fill={color}>{size}: {fb(tooltip.d[size] || 0)}</text>;
            })}</g>;
        }()}</svg>}</div>}{<div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      marginTop: 10
    }}>{SIZES.map(function ({
        size,
        color
      }) {
        return <span key={size} style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 11,
          color: B.textMid
        }}>{<span style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: color,
            display: "inline-block",
            flexShrink: 0
          }} />}{size}</span>;
      })}</div>}</Card>;
}
function WeekTrendChart({
  weeks,
  allWks,
  sel
}) {
  const idx = allWks.indexOf(sel);
  const data = allWks.slice(Math.max(0, idx - 7), idx + 1).map(function (wk) {
    const row = {
      label: fmtShort(wk)
    };
    SIZES.forEach(function (s) {
      row[s.size] = getBu(weeks, wk, s.size);
    });
    return row;
  });
  return <TrendChart data={data} title="8-Week Trend by Size" />;
}
function DayTrendChart({
  days,
  allDays,
  sel
}) {
  const idx = allDays.indexOf(sel);
  const data = allDays.slice(Math.max(0, idx - 13), idx + 1).map(function (day) {
    const row = {
      label: fmtShort(day)
    };
    SIZES.forEach(function (s) {
      row[s.size] = getBu(days, day, s.size);
    });
    return row;
  });
  return <TrendChart data={data} title="14-Day Trend by Size" />;
}

// ─── DATE DEBUG PANEL ────────────────────────────────────────────────────────
// Full row-level diagnostic for any selected daily date in the Forecaster.
// Shows every included and excluded row with itemname, numitems, size, package,
// bushel math, final bushels, and reason. Per-size subtotals at bottom.

// Proper top-level component — hooks are legal here.
// Previously this block used React.useState() calls inside an IIFE inside App's
// render return, which is illegal under the Rules of Hooks and caused random crashes.

// ─── PHOTO IMPORT MODAL ───────────────────────────────────────────────────────
// PARSE_ENDPOINT — server path for image / PDF OCR parsing only
const PARSE_ENDPOINT = "/api/parse-crab-count";

// ═══════════════════════════════════════════════════════════════════════════════
// COUNT-SHEET PARSERS — all client-side except image/PDF (which need the server)
// ═══════════════════════════════════════════════════════════════════════════════

// ── Size name resolver ────────────────────────────────────────────────────────
const COUNT_SHEET_SIZE_ALIASES = {
  "Colossal": ["colossal", "col"],
  "Premium": ["premium", "prem"],
  "Select": ["select", "sel"],
  "Standard": ["standard", "std"],
  "Large Female": ["premium female", "premium fem", "select female", "select fem", "large female", "lg female", "large fem", "lgf", "lf", "large f", "larges"],
  "Regular Female": ["standard female", "standard fem", "regular female", "reg female", "regular fem", "rf", "regf", "reg f", "regulars"]
};
function resolveCountSheetSize(raw) {
  const n = String(raw || "").toLowerCase().trim().replace(/^\s*\*?\s*/, "");
  for (let _i3 = 0, _Object$entries = Object.entries(COUNT_SHEET_SIZE_ALIASES); _i3 < _Object$entries.length; _i3++) {
    let _Object$entries$_i = _Object$entries[_i3],
      canonical = _Object$entries$_i[0],
      aliases = _Object$entries$_i[1];
    if (aliases.some(function (a) {
      return n === a || n.startsWith(a);
    })) return canonical;
  }
  return null;
}

// ── Date string resolver: "3/11" or "3/11/25" or "2025-03-11" → "YYYY-MM-DD" ─
function resolveDate(raw) {
  const s = String(raw || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (m) {
    const yr = m[3] ? m[3].length === 2 ? "20" + m[3] : m[3] : new Date().getFullYear();
    return "".concat(yr, "-").concat(String(m[1]).padStart(2, "0"), "-").concat(String(m[2]).padStart(2, "0"));
  }
  return null;
}
function InventoryLotPanel({
  lots,
  onChange,
  planDate,
  onSnapshot
}) {
  const today = planDate || todayISO();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const _useState27 = useState(false),
    _useState28 = _useState27,
    showImport = _useState28[0],
    setShowImport = _useState28[1];
  const enriched = useMemo(function () {
    return lots.map(function (l) {
      return enrichLot(l, today);
    });
  }, [lots, today]);
  const totals = useMemo(function () {
    let total = 0,
      usable = 0,
      urgent = 0,
      expired = 0;
    enriched.forEach(function (l) {
      const bu = Number(l.bushels) || 0;
      total = r2(total + bu);
      if (l.usable) usable = r2(usable + bu);
      if (l.status === "URGENT") urgent = r2(urgent + bu);
      if (!l.usable) expired = r2(expired + bu);
    });
    return {
      total: total,
      usable: usable,
      urgent: urgent,
      expired: expired
    };
  }, [enriched]);

  // Group enriched lots by size, preserving SIZES order
  const bySize = useMemo(function () {
    const groups = {};
    SIZES.forEach(function ({
      size
    }) {
      groups[size] = [];
    });
    groups['_unassigned'] = [];
    enriched.forEach(function (l) {
      if (groups[l.size]) groups[l.size].push(l);
      else groups['_unassigned'].push(l);
    });
    return groups;
  }, [enriched]);

  // Per-size totals (usable only)
  const sizeTotals = useMemo(function () {
    const t = {};
    SIZES.forEach(function ({
      size
    }) {
      t[size] = {
        total: 0,
        usable: 0
      };
    });
    enriched.forEach(function (l) {
      const bu = Number(l.bushels) || 0;
      if (t[l.size]) {
        t[l.size].total = r2(t[l.size].total + bu);
        if (l.usable) t[l.size].usable = r2(t[l.size].usable + bu);
      }
    });
    return t;
  }, [enriched]);
  function addRow() {
    onChangeRef.current([].concat([...lots], [makeLot()]));
  }
  function clearAll() {
    if (window.confirm('Clear all inventory lots? This cannot be undone.')) {
      onChangeRef.current([]);
    }
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRow();
    }
  }
  function removeRow(id) {
    onChangeRef.current(lots.filter(function(l) { return l.id !== id; }));
  }
  function updateRow(id, f, v) {
    onChangeRef.current(lots.map(function(l) { return l.id === id ? { ...l, [f]: v } : l; }));
  }
  const [importRows, setImportRows] = useState([]);
  const [importError, setImportError] = useState('');

  function handleImportConfirm(newLots) {
    const clean = newLots.map(function (l) {
      return makeLot({
        size: l.size,
        steamDate: l.steamDate,
        bushels: l.bushels
      });
    });
    if (onSnapshot) onSnapshot(clean); // record snapshot BEFORE setState so data is consistent
    onChange(clean);
    setImportRows([]);
    setImportError('');
    setShowImport(false);
  }

  function handleFileInput(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setImportError('');
    const reader = new FileReader();
    reader.onload = function(ev) {
      try {
        const data = new Uint8Array(ev.target.result);
        // Try parsing as CSV if not xlsx
        const isCSV = file.name.toLowerCase().endsWith('.csv');
        let rows = [];
        if (isCSV) {
          const text = new TextDecoder().decode(data);
          const lines = text.split(/\r?\n/).filter(l => l.trim());
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          const sizeIdx = headers.findIndex(h => h.includes('size') || h.includes('grade'));
          const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('steam'));
          const buIdx = headers.findIndex(h => h.includes('bush') || h.includes('bu') || h.includes('qty') || h.includes('quantity'));
          if (sizeIdx < 0 || buIdx < 0) { setImportError('Could not find size/bushel columns. Expected headers: Size, Steam Date, Bushels'); return; }
          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(',');
            const size = resolveCountSheetSize(cols[sizeIdx]);
            const steamDate = dateIdx >= 0 ? resolveDate(cols[dateIdx]) : today;
            const bushels = parseFloat(cols[buIdx]) || 0;
            if (size && bushels > 0) rows.push({ size, steamDate: steamDate || today, bushels });
          }
        } else {
          // XLSX - load SheetJS dynamically if needed
          const loadAndParse = (XL) => {
            const wb = XL.read(data, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const json = XL.utils.sheet_to_json(ws, { header: 1, defval: null });
            if (json.length < 2) { setImportError('Spreadsheet appears empty'); return []; }

            // Detect HCD format: "Daily Hard Crab Count" in row 0, or
            // col A = size name, col I (index 8) = total bushels, cols D-H = date-qty entries
            const firstCell = String(json[0][0] || '').toLowerCase();
            const isHCDFormat = firstCell.includes('daily hard crab') ||
              (json.some(r => r && resolveCountSheetSize(r[0])) &&
               json.some(r => r && r[3] && String(r[3]).includes('-')));

            const parsed = [];
            if (isHCDFormat) {
              // HCD format: each size row has date-quantity entries like "4/10-19" in cols D+
              // Col A = size, Col I (idx 8) = total bushels for the lot
              // We create one lot per date entry within each size row
              for (let i = 0; i < json.length; i++) {
                const row = json[i];
                if (!row || !row[0]) continue;
                const size = resolveCountSheetSize(row[0]);
                if (!size) continue;
                // Scan cols 3-7 (D-H) for "date-qty" entries like "4/10-19" or "4/8-1.75"
                let foundDateEntries = false;
                for (let c = 3; c <= 7; c++) {
                  const cell = String(row[c] || '').trim();
                  if (!cell) continue;
                  // Match pattern: date-quantity e.g. "4/10-19" or "4/8-1.75"
                  const m = cell.match(/^(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)[\s\-–]+([\d.]+)$/);
                  if (m) {
                    const steamDate = resolveDate(m[1]) || today;
                    const bushels = parseFloat(m[2]) || 0;
                    if (bushels > 0) { parsed.push({ size, steamDate, bushels }); foundDateEntries = true; }
                  }
                }
                // If no date entries found, fall back to total bushels in col I
                if (!foundDateEntries) {
                  const totalBu = parseFloat(row[8]) || 0;
                  if (totalBu > 0) parsed.push({ size, steamDate: today, bushels: totalBu });
                }
              }
            } else {
              // Standard format: header row with Size, Steam Date, Bushels columns
              const headers = json[0].map(h => String(h || '').toLowerCase().trim());
              const sizeIdx = headers.findIndex(h => h.includes('size') || h.includes('grade'));
              const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('steam'));
              const buIdx = headers.findIndex(h => h.includes('bush') || h.includes('bu') || h.includes('qty'));
              if (sizeIdx < 0 || buIdx < 0) { setImportError('Could not find size/bushel columns'); return []; }
              for (let i = 1; i < json.length; i++) {
                const row = json[i];
                if (!row) continue;
                const size = resolveCountSheetSize(row[sizeIdx]);
                const rawDate = dateIdx >= 0 ? row[dateIdx] : null;
                let steamDate = today;
                if (rawDate) {
                  if (typeof rawDate === 'number') {
                    const d = new Date(Math.round((rawDate - 25569) * 86400 * 1000));
                    steamDate = d.toISOString().slice(0, 10);
                  } else { steamDate = resolveDate(String(rawDate)) || today; }
                }
                const bushels = parseFloat(row[buIdx]) || 0;
                if (size && bushels > 0) parsed.push({ size, steamDate, bushels });
              }
            }
            return parsed;
          };

          if (typeof window.XLSX !== 'undefined') {
            rows = loadAndParse(window.XLSX);
          } else {
            // Load SheetJS dynamically then re-run parsing
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = function() {
              try {
                const dynRows = loadAndParse(window.XLSX);
                if (dynRows.length === 0) { setImportError('No valid rows found. Check that Size and Bushels columns have data.'); return; }
                setImportRows(dynRows);
              } catch(err) { setImportError('Error reading file: ' + err.message); }
            };
            script.onerror = function() { setImportError('Could not load XLSX library. Please use a CSV file instead.'); };
            document.head.appendChild(script);
            return; // wait for script to load
          }
        }
        if (rows.length === 0) { setImportError('No valid rows found. Check that Size and Bushels columns have data.'); return; }
        setImportRows(rows);
      } catch(err) {
        setImportError('Error reading file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  // ── Shared lot row renderer (used in both the main table and import review)
  function LotRow({
    lot,
    onRemove,
    onUpdate,
    onKeyDown
  }) {
    const [localBu, setLocalBu] = React.useState(lot.bushels || '');
    const localBuRef = useRef(localBu);
    localBuRef.current = localBu;
    const buTimerRef = useRef(null);
    // Sync if lot.bushels changes externally (e.g. Neon load, import)
    React.useEffect(function() {
      setLocalBu(lot.bushels || '');
    }, [lot.id, lot.bushels]);
    const st = LOT_STATUS[lot.status] || LOT_STATUS.FRESH;
    const sc = SIZE_COLORS[lot.size] || B.navy;
    const isUrgent = lot.status === "URGENT";
    const isExpired = lot.status === "EXPIRED";
    return <div style={{
      display: "grid",
      gridTemplateColumns: "1.8fr 1.4fr 0.7fr auto 28px",
      gap: 8,
      alignItems: "center",
      padding: "9px 12px 9px 14px",
      borderBottom: "1px solid ".concat(B.rowSep),
      borderLeft: "4px solid ".concat(sc),
      background: isExpired ? "#fef2f2" : isUrgent ? "#fff7ed" : "#fff"
    }}>{<div>{<Sel value={lot.size} onChange={function onChange(e) {
          return onUpdate(lot.id, "size", e.target.value);
        }} style={{
          padding: "5px 7px",
          fontSize: 13,
          fontWeight: 700,
          color: sc,
          border: "1.5px solid ".concat(sc, "44"),
          borderRadius: 6
        }}>{[{size:'',label:'— select —'}].concat(SIZES.map(function(s){return {size:s.size,label:s.size};})).map(function (s) {
            return <option key={s.size} value={s.size}>{s.label||s.size}</option>;
          })}</Sel>}</div>}{<Input type="date" value={lot.steamDate} onChange={function onChange(e) {
        return onUpdate(lot.id, "steamDate", e.target.value);
      }} style={{
        padding: "5px 7px",
        fontSize: 12
      }} />}{<Input type="number" value={localBu} placeholder="0" onChange={function onChange(e) {
        var v = e.target.value;
        setLocalBu(v);
        if (buTimerRef.current) clearTimeout(buTimerRef.current);
        buTimerRef.current = setTimeout(function() { onUpdate(lot.id, "bushels", v); }, 600);
      }} onKeyDown={onKeyDown} style={{
        padding: "5px 7px",
        fontSize: 13,
        fontWeight: 700,
        textAlign: "center"
      }} />}{<div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 3,
        minWidth: 0
      }}>{lot.steamDate ? <>{<span style={{
            background: st.bg,
            border: "1px solid ".concat(st.border),
            color: st.color,
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 7px",
            borderRadius: 5,
            whiteSpace: "nowrap",
            letterSpacing: .3
          }}>{st.label} · day {lot.age}</span>}{lot.shipAlert && <span style={{
            fontSize: 10,
            color: st.color,
            fontWeight: 600,
            whiteSpace: "nowrap"
          }}>⚡ {lot.shipAlert}</span>}{isExpired && <span style={{
            fontSize: 10,
            color: "#b91c1c",
            fontWeight: 600
          }}>excluded</span>}</> : <span style={{
          color: B.textLight,
          fontSize: 11
        }}>—</span>}</div>}{<button onClick={function onClick() {
        return onRemove(lot.id);
      }} style={{
        background: "none",
        border: "none",
        color: B.textLight,
        cursor: "pointer",
        fontSize: 15,
        padding: 0,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>✕</button>}</div>;
  }
  return <Card style={{
    marginBottom: 16
  }}>{<div style={{
      padding: "13px 16px",
      background: B.navy,
      borderRadius: "10px 10px 0 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8
    }}>{<div>{<div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: .1
        }}>Inventory — Crab Count</div>}{<div style={{
          color: "rgba(255,255,255,.45)",
          fontSize: 11,
          marginTop: 2
        }}>{enriched.length > 0 ? "".concat(enriched.length, " lot").concat(enriched.length !== 1 ? "s" : "", " \xB7 ").concat(fb(totals.usable), " bu usable \xB7 planning date ").concat(today) : "No lots imported yet"}</div>}</div>}{<div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap"
      }}>{!showImport && <button onClick={function onClick() {
          return setShowImport(true);
        }} style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          background: "rgba(255,255,255,.15)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,.25)",
          borderRadius: 9,
          padding: "9px 15px",
          fontWeight: 700,
          fontSize: 13,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent"
        }}>{<span style={{
            fontSize: 16
          }}>📥</span>} Import Count Sheet</button>}{<button onClick={addRow} style={{
          background: "rgba(255,255,255,.1)",
          color: "rgba(255,255,255,.85)",
          border: "1px solid rgba(255,255,255,.2)",
          borderRadius: 9,
          padding: "9px 14px",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent"
        }}>+ Add Lot</button>}{lots.length > 0 && <button onClick={clearAll} style={{
          background: "rgba(255,255,255,.1)",
          color: "rgba(255,100,100,.9)",
          border: "1px solid rgba(255,100,100,.3)",
          borderRadius: 9,
          padding: "9px 14px",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          marginLeft: 6
        }}>Clear All</button>}</div>}</div>}{showImport && <div style={{
      margin: "12px 0",
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      padding: 16
    }}>{<div style={{ fontWeight: 700, fontSize: 13, color: "#00395C", marginBottom: 10 }}>📥 Import Count Sheet</div>}{importRows.length === 0 ? <div>{<div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>Upload a CSV or Excel file with columns: Size, Steam Date, Bushels</div>}{<input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileInput} style={{ fontSize: 13 }} />}{importError && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 8 }}>{importError}</div>}{<button onClick={function onClick() { setShowImport(false); setImportError(''); }} style={{ marginTop: 10, fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer" }}>Cancel</button>}</div> : <div>{<div style={{ fontSize: 12, color: "#374151", marginBottom: 8 }}>Found {importRows.length} rows — review and confirm:</div>}{<div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 10 }}>{importRows.map(function(r, i) { return <div key={i} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 0.7fr", gap: 8, padding: "6px 10px", borderBottom: "1px solid #f3f4f6", fontSize: 12 }}>{<span style={{ fontWeight: 600, color: SIZE_COLORS[r.size] || "#374151" }}>{r.size}</span>}{<span>{r.steamDate}</span>}{<span style={{ fontWeight: 700 }}>{r.bushels} bu</span>}</div>; })}</div>}{<div style={{ display: "flex", gap: 8 }}>{<button onClick={function onClick() { handleImportConfirm(importRows); }} style={{ background: "#00395C", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>✓ Confirm Import ({importRows.length} lots)</button>}{<button onClick={function onClick() { setImportRows([]); setImportError(''); setShowImport(false); }} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 7, padding: "8px 12px", fontSize: 12, cursor: "pointer", color: "#6b7280" }}>Cancel</button>}</div>}</div>}</div>}}{totals.expired > 0 && <div style={{
      margin: "0 0 0",
      padding: "10px 16px",
      background: "#fef2f2",
      borderBottom: "1px solid #fecaca",
      display: "flex",
      alignItems: "flex-start",
      gap: 10
    }}>{<span style={{
        fontSize: 16,
        flexShrink: 0
      }}>⛔</span>}{<div>{<div style={{
          color: "#b91c1c",
          fontWeight: 700,
          fontSize: 13
        }}>Expired Inventory</div>}{<div style={{
          color: "#7f1d1d",
          fontSize: 12,
          marginTop: 1,
          lineHeight: 1.5
        }}>{fb(totals.expired)} bu is past day 9 — excluded from buy recommendation.</div>}</div>}</div>}{totals.urgent > 0 && <div style={{
      padding: "10px 16px",
      background: "#fff7ed",
      borderBottom: "1px solid #fed7aa",
      display: "flex",
      alignItems: "flex-start",
      gap: 10
    }}>{<span style={{
        fontSize: 16,
        flexShrink: 0
      }}>⚡</span>}{<div>{<div style={{
          color: "#c2540a",
          fontWeight: 700,
          fontSize: 13
        }}>Urgent — Shipping Action Required</div>}{<div style={{
          color: "#92400e",
          fontSize: 12,
          marginTop: 1,
          lineHeight: 1.5
        }}>{fb(totals.urgent)} bu is day 8–9.{enriched.filter(function (l) {
            return l.age === 8;
          }).length > 0 && " Day-8 lots: ship today via 2-day service."}{enriched.filter(function (l) {
            return l.age === 9;
          }).length > 0 && " Day-9 lots: last day — next-day shipping only."}</div>}</div>}</div>}{lots.length === 0 ? <div style={{
      padding: "32px 16px",
      textAlign: "center",
      color: B.textLight,
      fontSize: 13
    }}>No inventory lots. {<span style={{
        color: B.navy,
        fontWeight: 700,
        cursor: "pointer"
      }} onClick={function onClick() {
        return setShowImport(true);
      }}>Import Count Sheet</span>} or {<span style={{
        color: B.green,
        fontWeight: 700,
        cursor: "pointer"
      }} onClick={addRow}>+ Add Lot</span>}.</div> : <>{<div style={{
        display: "grid",
        gridTemplateColumns: "1.8fr 1.4fr 0.7fr auto 28px",
        gap: 8,
        padding: "7px 12px 6px 18px",
        background: B.rowAlt,
        borderBottom: "1px solid ".concat(B.rowSep)
      }}>{["Size", "Steam Date", "Bu", "Status / Action", ""].map(function (h, i) {
          return <div key={i} style={{
            color: B.textLight,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: .6,
            textTransform: "uppercase",
            textAlign: i === 2 ? "center" : "left"
          }}>{h}</div>;
        })}</div>}{SIZES.map(function ({
        size
      }) {
        const sizeLots = bySize[size] || [];
        if (sizeLots.length === 0) return null;
        const sc = SIZE_COLORS[size];
        const sbg = SIZE_BG[size] || "#fafafa";
        const st = sizeTotals[size] || {
          total: 0,
          usable: 0
        };
        const hasUrgent = sizeLots.some(function (l) {
          return l.status === "URGENT";
        });
        const hasExpired = sizeLots.some(function (l) {
          return l.status === "EXPIRED";
        });
        return <div key={size}>{<div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "6px 14px 6px 14px",
            background: sbg,
            borderBottom: "1px solid ".concat(sc, "22"),
            borderLeft: "4px solid ".concat(sc)
          }}>{<div style={{
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>{<div style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: sc,
                flexShrink: 0
              }} />}{<span style={{
                fontWeight: 700,
                fontSize: 13,
                color: sc
              }}>{size}</span>}{hasUrgent && <span style={{
                fontSize: 10,
                color: "#c2540a",
                fontWeight: 700
              }}>⚡ URGENT</span>}{hasExpired && <span style={{
                fontSize: 10,
                color: "#b91c1c",
                fontWeight: 700
              }}>⛔ EXPIRED</span>}</div>}{<div style={{
              display: "flex",
              alignItems: "center",
              gap: 12
            }}>{st.total !== st.usable && <span style={{
                fontSize: 11,
                color: B.textLight,
                textDecoration: "line-through"
              }}>{fb(st.total)} bu</span>}{<span style={{
                fontSize: 13,
                fontWeight: 800,
                color: sc,
                minWidth: 55,
                textAlign: "right"
              }}>{fb(st.usable)} {<span style={{
                  fontWeight: 400,
                  fontSize: 11,
                  color: B.textLight
                }}>bu usable</span>}</span>}</div>}</div>}{sizeLots.map(function (lot) {
            return <LotRow key={lot.id} lot={lot} onRemove={removeRow} onUpdate={updateRow} onKeyDown={handleKeyDown} />;
          })}</div>;
      })}{bySize['_unassigned'] && bySize['_unassigned'].length > 0 && <div style={{ padding: '8px 0 0 0' }}>{bySize['_unassigned'].map(function(lot) { return <LotRow key={lot.id} lot={lot} onRemove={removeRow} onUpdate={updateRow} onKeyDown={handleKeyDown} />; })}</div>}{<div style={{
        padding: "12px 16px",
        background: B.navy,
        borderRadius: "0 0 10px 10px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: 10
      }}>{[{
          label: "Total",
          value: totals.total,
          color: "#fff"
        }, {
          label: "Usable",
          value: totals.usable,
          color: "#86efac"
        }, {
          label: "Urgent",
          value: totals.urgent,
          color: "#fdba74"
        }, {
          label: "Expired",
          value: totals.expired,
          color: "#fca5a5"
        }].map(function (c) {
          return <div key={c.label} style={{
            textAlign: "center"
          }}>{<div style={{
              color: "rgba(255,255,255,.4)",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: .7,
              textTransform: "uppercase",
              marginBottom: 4
            }}>{c.label}</div>}{<div style={{
              color: c.value > 0 ? c.color : "rgba(255,255,255,.15)",
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1
            }}>{c.value > 0 ? fb(c.value) : "—"}{c.value > 0 && <span style={{
                fontSize: 10,
                fontWeight: 400,
                color: "rgba(255,255,255,.3)",
                marginLeft: 3
              }}>bu</span>}</div>}</div>;
        })}</div>}</>}</Card>;
}

// ─── PLANNING INPUTS PANEL ────────────────────────────────────────────────────
function PlanningInputsPanel({
  planningInputs,
  onChange: _onChange,
  liveSupplyByGrade
}) {
  const GRADE_COLORS = {
    "1s": "#1d6fa8",
    "2s": "#a16207",
    "3s": "#c2540a"
  };
  const GRADE_LABELS = {
    "1s": "Colossal / Premium",
    "2s": "Select / Standard",
    "3s": "Lg Female / Reg Female"
  };
  const hasLive = liveSupplyByGrade && Object.values(liveSupplyByGrade).some(function (v) {
    return v > 0;
  });
  return <Card style={{
    marginBottom: 16
  }}>{<CardHeader>Planning Inputs</CardHeader>}{<div style={{
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 14
    }}>{<div style={{
        background: hasLive ? "#f0fdf4" : B.rowAlt,
        border: "1.5px solid ".concat(hasLive ? "#bbf7d0" : B.cardBorder),
        borderRadius: 10,
        padding: "11px 14px"
      }}>{<div style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: hasLive ? 9 : 0
        }}>{<span style={{
            fontSize: 13
          }}>{hasLive ? "🟢" : "⬜"}</span>}{<span style={{
            fontWeight: 700,
            fontSize: 12,
            color: hasLive ? "#166534" : B.textMid
          }}>Live / Ungraded Supply</span>}{<span style={{
            fontSize: 10,
            color: B.textLight,
            marginLeft: "auto"
          }}>auto-read from Live / Ungraded Tracker</span>}</div>}{hasLive ? <>{<div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 8
          }}>{["1s", "2s", "3s"].map(function (grade) {
              const boxes = liveSupplyByGrade[grade] || 0;
              const col = GRADE_COLORS[grade];
              return <div key={grade} style={{
                background: "#fff",
                borderRadius: 7,
                padding: "7px 10px",
                border: "1px solid ".concat(col, "33"),
                textAlign: "center"
              }}>{<div style={{
                  fontWeight: 800,
                  fontSize: 13,
                  color: col
                }}>{grade}</div>}{<div style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: boxes > 0 ? col : B.textLight,
                  margin: "2px 0"
                }}>{boxes > 0 ? "".concat(boxes, " box").concat(boxes !== 1 ? "es" : "") : "—"}</div>}{<div style={{
                  fontSize: 10,
                  color: B.textLight
                }}>{GRADE_LABELS[grade]}</div>}</div>;
            })}</div>}{<div style={{
            color: "#166534",
            fontSize: 10,
            marginTop: 8,
            lineHeight: 1.6
          }}>Remaining-to-grade boxes across all steam dates reduce the buy recommendation automatically.</div>}</> : <div style={{
          color: B.textLight,
          fontSize: 12,
          marginTop: 6,
          lineHeight: 1.6
        }}>No live/ungraded entries for today. Add entries in the Live / Ungraded tab to see available internal supply here.</div>}</div>}{<div>{<Label>Planning Date</Label>}{<Input type="date" value={planningInputs.planDate} onChange={function onChange(e) {
          return _onChange({
            ...planningInputs,
            planDate: e.target.value
          });
        }} />}{<div style={{
          color: B.textLight,
          fontSize: 10,
          marginTop: 3
        }}>Used for inventory age calculations</div>}</div>}</div>}</Card>;
}

// ─── DEMAND PICTURE TABLE ─────────────────────────────────────────────────────
function DemandPictureTable({
  demandPicture,
  inventoryBySize,
  demandOverrides,
  onChange: _onChange2
}) {
  const bySize = demandPicture.bySize;
  return <Card style={{
    marginBottom: 16
  }}>{<CardHeader sub="True Demand minus Usable Inventory = Net to Buy. Override if demand was suppressed by supply constraints.">Projected Demand by Finished Size</CardHeader>}{<div style={{
      overflowX: "auto"
    }}>{<table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13
      }}>{<thead>{<tr style={{
            background: B.navy
          }}>{<th style={{
              padding: "10px 14px",
              textAlign: "left",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6,
              width: "20%"
            }}>SIZE</th>}{<th style={{
              padding: "10px 10px",
              textAlign: "center",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6
            }}>FORECAST{<br />}{<span style={{
                fontSize: 9,
                opacity: .6
              }}>observed</span>}</th>}{<th style={{
              padding: "10px 10px",
              textAlign: "center",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6
            }}>TRUE DEMAND{<br />}{<span style={{
                fontSize: 9,
                opacity: .6
              }}>your estimate</span>}</th>}{<th style={{
              padding: "10px 10px",
              textAlign: "center",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6
            }}>USABLE INV{<br />}{<span style={{
                fontSize: 9,
                opacity: .6
              }}>≤9 days old</span>}</th>}{<th style={{
              padding: "10px 10px",
              textAlign: "center",
              color: "#86efac",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6
            }}>NET TO BUY</th>}{<th style={{
              padding: "10px 10px",
              textAlign: "center",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6
            }}>OVERRIDE</th>}{<th style={{
              padding: "10px 10px",
              textAlign: "left",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6
            }}>FLAGS</th>}</tr>}</thead>}{<tbody>{SIZES.map(function ({
            size,
            color
          }, ri) {
            const d = bySize[size] || {},
              inv = (inventoryBySize === null || inventoryBySize === void 0 ? void 0 : inventoryBySize[size]) || {};
            const netToBuy = r2(Math.max(0, (d.trueDemand || 0) - (inv.usable || 0)));
            return <tr key={size} style={{
              background: ri % 2 === 0 ? B.cardBg : B.rowAlt,
              borderBottom: "1px solid ".concat(B.rowSep)
            }}>{<td style={{
                padding: "11px 14px"
              }}>{<div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}>{<span style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: color,
                    display: "inline-block",
                    flexShrink: 0
                  }} />}{<span style={{
                    color: B.textDark,
                    fontWeight: 700,
                    fontSize: 13
                  }}>{size}</span>}</div>}{<div style={{
                  color: B.textLight,
                  fontSize: 10,
                  marginLeft: 18,
                  marginTop: 1
                }}>via {SIZE_TO_GRADE[size]}</div>}</td>}{<td style={{
                padding: "11px 10px",
                textAlign: "center"
              }}>{<span style={{
                  color: B.textMid,
                  fontWeight: 600,
                  fontSize: 13
                }}>{(d.observed || 0) > 0 ? "".concat(fb(d.observed), " bu") : <span style={{
                    color: B.textLight
                  }}>—</span>}</span>}</td>}{<td style={{
                padding: "11px 10px",
                textAlign: "center"
              }}>{<span style={{
                  color: B.navy,
                  fontWeight: 700,
                  fontSize: 14
                }}>{(d.trueDemand || 0) > 0 ? "".concat(fb(d.trueDemand), " bu") : <span style={{
                    color: B.textLight,
                    fontSize: 13
                  }}>—</span>}</span>}</td>}{<td style={{
                padding: "11px 10px",
                textAlign: "center"
              }}>{(inv.usable || 0) > 0 ? <div>{<span style={{ color: B.green, fontWeight: 700, fontSize: 13 }}>{fb(inv.usable) + " bu"}</span>}{(function() {  var db = inv.dayBuckets || {};  var rows = [];  if (db[9] > 0) rows.push(<div key="d9" style={{ color:"#b91c1c", fontSize:10, fontWeight:700, marginTop:2 }}>{"\uD83D\uDD34 D9: " + fb(db[9]) + " bu \u2014 last day"}</div>);  if (db[8] > 0) rows.push(<div key="d8" style={{ color:"#c2540a", fontSize:10, fontWeight:700, marginTop:2 }}>{"\uD83D\uDEA8 D8: " + fb(db[8]) + " bu \u2014 last day for 2-day"}</div>);  if (db[7] > 0) rows.push(<div key="d7" style={{ color:"#92400e", fontSize:10, marginTop:2 }}>{"\u26A0 D7: " + fb(db[7]) + " bu"}</div>);  if (db[6] > 0) rows.push(<div key="d6" style={{ color:"#92400e", fontSize:10, marginTop:2 }}>{"\u26A0 D6: " + fb(db[6]) + " bu"}</div>);  var fresh = [5,4,3,2,1].reduce(function(s,d){ return s + (db[d]||0); }, 0);  if (fresh > 0) rows.push(<div key="fresh" style={{ color:B.textLight, fontSize:10, marginTop:2 }}>{"D1\u20135: " + fb(fresh) + " bu"}</div>);  return rows;})()} </div> : <span style={{ color: B.textLight, fontSize: 13 }}>\u2014</span>}</td>}{<td style={{
                padding: "11px 10px",
                textAlign: "center"
              }}>{<span style={{
                  color: netToBuy > 0 ? B.colFC : B.green,
                  fontWeight: 800,
                  fontSize: 14
                }}>{netToBuy > 0 ? "".concat(fb(netToBuy), " bu") : <span style={{
                    color: B.green,
                    fontSize: 12
                  }}>✓ covered</span>}</span>}</td>}{<td style={{
                padding: "7px 10px",
                textAlign: "center"
              }}>{<input type="number" placeholder="—" value={demandOverrides[size] || ""} onChange={function onChange(e) {
                  return _onChange2({
                    ...demandOverrides,
                    ...{ [size]: e.target.value }
                  });
                }} style={{
                  width: 68,
                  padding: "5px 6px",
                  borderRadius: 6,
                  border: "1px solid ".concat(B.cardBorder),
                  fontSize: 12,
                  textAlign: "center",
                  color: B.textDark,
                  outline: "none"
                }} />}{demandOverrides[size] && <div style={{
                  fontSize: 9,
                  color: B.colAvg,
                  marginTop: 2
                }}>override</div>}</td>}{<td style={{
                padding: "7px 10px"
              }}>{(inv.expired || 0) > 0 && <div style={{
                  color: B.expiredText,
                  fontSize: 10,
                  fontWeight: 600,
                  marginTop: 3
                }}>⛔ {fb(inv.expired)} bu expired</div>}</td>}</tr>;
          })}</tbody>}{<tfoot>{<tr style={{
            background: B.navy
          }}>{<td style={{
              padding: "10px 14px",
              color: B.white,
              fontWeight: 800,
              fontSize: 12
            }}>TOTAL</td>}{<td style={{
              padding: "10px 10px",
              textAlign: "center",
              color: "rgba(255,255,255,.6)",
              fontWeight: 700
            }}>{demandPicture.totalObserved > 0 ? "".concat(fb(demandPicture.totalObserved), " bu") : "—"}</td>}{<td style={{
              padding: "10px 10px",
              textAlign: "center",
              color: B.white,
              fontWeight: 800,
              fontSize: 14
            }}>{demandPicture.totalTrueDemand > 0 ? "".concat(fb(demandPicture.totalTrueDemand), " bu") : "—"}</td>}{<td colSpan={4} />}</tr>}</tfoot>}</table>}</div>}</Card>;
}

// ─── GRADE BUY PANEL ──────────────────────────────────────────────────────────
function GradeBuyPanel({
  gradeBuy,
  gradeOverrides,
  onChange: _onChange3,
  timingPlan
}) {
  const _ref65 = gradeBuy || {},
    _ref65$byGrade = _ref65.byGrade,
    byGrade = _ref65$byGrade === void 0 ? {} : _ref65$byGrade,
    _ref65$totalIdealBu = _ref65.totalIdealBu,
    totalIdealBu = _ref65$totalIdealBu === void 0 ? 0 : _ref65$totalIdealBu,
    _ref65$totalIdealBoxe = _ref65.totalIdealBoxes,
    totalIdealBoxes = _ref65$totalIdealBoxe === void 0 ? 0 : _ref65$totalIdealBoxe,
    _ref65$totalRealistic = _ref65.totalRealisticBoxes,
    totalRealisticBoxes = _ref65$totalRealistic === void 0 ? 0 : _ref65$totalRealistic;
  return <Card style={{
    marginBottom: 16
  }}>{<CardHeader sub="Net demand after inventory. Realistic = what you can actually get.">Grade Buy Recommendation</CardHeader>}{<div style={{
      padding: "14px 16px"
    }}>{<div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: 8,
        marginBottom: 8,
        paddingBottom: 8,
        borderBottom: "1px solid ".concat(B.rowSep)
      }}>{["GRADE", "IDEAL BUY", "REALISTIC / OVERRIDE", "FINISHED SIZES"].map(function (h) {
          return <div key={h} style={{
            color: B.textLight,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: .6
          }}>{h}</div>;
        })}</div>}{GRADES.map(function ({
        grade,
        color,
        primarySizes
      }) {
        const g = byGrade[grade] || {};
        return <div key={grade} style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: 8,
          alignItems: "center",
          padding: "10px 0",
          borderBottom: "1px solid ".concat(B.rowAlt)
        }}>{<div style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>{<span style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: color,
              display: "inline-block",
              flexShrink: 0
            }} />}{<span style={{
              color: B.textDark,
              fontWeight: 700,
              fontSize: 14
            }}>{grade}</span>}</div>}{<div style={{
            textAlign: "center"
          }}>{<span style={{
              color: B.navy,
              fontWeight: 800,
              fontSize: 20
            }}>{g.idealBoxes || 0}</span>}{<span style={{
              color: B.textLight,
              fontSize: 11,
              marginLeft: 5
            }}>boxes</span>}{<div style={{
              color: B.textLight,
              fontSize: 10
            }}>{fb(g.idealBu || 0)} bu net</div>}</div>}{<div style={{
            textAlign: "center"
          }}>{<input type="number" placeholder={g.idealBoxes || 0} value={gradeOverrides[grade] || ""} onChange={function onChange(e) {
              return _onChange3({
                ...gradeOverrides,
                ...{ [grade]: e.target.value }
              });
            }} style={{
              width: 72,
              padding: "7px 8px",
              borderRadius: 7,
              border: "1.5px solid ".concat(gradeOverrides[grade] ? B.colAvg : B.cardBorder),
              fontSize: 14,
              fontWeight: 700,
              textAlign: "center",
              color: B.navy,
              outline: "none"
            }} />}{gradeOverrides[grade] && Number(gradeOverrides[grade]) < (g.idealBoxes || 0) && <div style={{
              color: B.alertAmber,
              fontSize: 10,
              marginTop: 2
            }}>↓ {(g.idealBoxes || 0) - Number(gradeOverrides[grade])} short of ideal</div>}</div>}{<div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4
          }}>{primarySizes.map(function (s) {
              return <span key={s} style={{
                background: "".concat(SIZE_COLORS[s], "18"),
                border: "1px solid ".concat(SIZE_COLORS[s], "44"),
                color: SIZE_COLORS[s],
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 4
              }}>{s}</span>;
            })}</div>}</div>;
      })}{<div style={{
        marginTop: 14,
        padding: "14px 16px",
        background: B.navy,
        borderRadius: 10,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 16
      }}>{[{
          label: "TOTAL BUSHELS NEEDED",
          value: "".concat(fb(totalIdealBu), " bu")
        }, {
          label: "IDEAL BUY (ALL GRADES)",
          value: "".concat(totalIdealBoxes, " boxes")
        }, {
          label: "REALISTIC BUY",
          value: "".concat(totalRealisticBoxes, " boxes"),
          sub: totalRealisticBoxes < totalIdealBoxes ? "".concat(totalIdealBoxes - totalRealisticBoxes, " short of ideal") : null,
          accent: totalRealisticBoxes < totalIdealBoxes
        }].map(function (c) {
          return <div key={c.label} style={{
            textAlign: "center"
          }}>{<div style={{
              color: "rgba(255,255,255,.5)",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: .8,
              marginBottom: 4
            }}>{c.label}</div>}{<div style={{
              color: c.accent ? "#f87171" : B.white,
              fontWeight: 800,
              fontSize: 22
            }}>{c.value}</div>}{c.sub && <div style={{
              color: "#fca5a5",
              fontSize: 10,
              marginTop: 2
            }}>{c.sub}</div>}</div>;
        })}</div>}{timingPlan && <div style={{
        marginTop: 12
      }}>{timingPlan.needsSupplemental && <div style={{
          background: B.warnBg,
          border: "1.5px solid ".concat(B.warnBorder),
          borderRadius: 8,
          padding: "10px 14px",
          marginBottom: 10
        }}>{<div style={{
            color: B.alertRed,
            fontWeight: 700,
            fontSize: 13
          }}>⚠ Supplemental Buy Needed Before Monday</div>}{<div style={{
            color: B.alertRed,
            fontSize: 12,
            marginTop: 4,
            lineHeight: 1.6
          }}>At ~{fb(timingPlan.dailyBurnBu)} bu/day you'll run short by ~{<strong>{timingPlan.shortfallBoxes} box{timingPlan.shortfallBoxes !== 1 ? "es" : ""}</strong>} before the next Monday window.</div>}</div>}{<div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10
        }}>{timingPlan.windows.map(function (w) {
            const isMon = w.label === "Monday";
            return <div key={w.label} style={{
              background: isMon ? B.navy : B.rowAlt,
              border: "1.5px solid ".concat(isMon ? B.navy : B.cardBorder),
              borderRadius: 10,
              padding: 14,
              boxShadow: isMon ? "0 4px 14px rgba(26,46,74,.2)" : "none"
            }}>{<div style={{
                color: isMon ? B.white : B.navy,
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "Georgia,serif"
              }}>{w.label} Buy</div>}{<div style={{
                color: isMon ? "rgba(255,255,255,.5)" : B.textLight,
                fontSize: 11,
                marginTop: 2
              }}>Louisiana · {fmtDate(w.date)} · in {timingPlan.daysToMon} day{timingPlan.daysToMon !== 1 ? "s" : ""}</div>}{isMon && <>{<div style={{
                  color: "rgba(255,255,255,.5)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: .7,
                  marginTop: 10,
                  marginBottom: 4
                }}>TOTAL TO ORDER</div>}{<div style={{
                  color: B.white,
                  fontWeight: 800,
                  fontSize: 26
                }}>{timingPlan.monBuyBoxes}{<span style={{
                    color: "rgba(255,255,255,.35)",
                    fontSize: 12,
                    marginLeft: 5
                  }}>boxes</span>}</div>}{<div style={{
                  color: "rgba(255,255,255,.3)",
                  fontSize: 10,
                  marginTop: 4
                }}>Covers ~{timingPlan.coverDays} days · +10% buffer · based on usable inventory only</div>}</>}{!isMon && <div style={{
                color: B.textLight,
                fontSize: 12,
                marginTop: 8
              }}>Size Wednesday order after Monday delivery is received.</div>}</div>;
          })}</div>}</div>}</div>}</Card>;
}

// ─── PURCHASE FORM ────────────────────────────────────────────────────────────
function PurchaseForm({
  purchase,
  onChange: _onChange4,
  vendors,
  onSave,
  onCancel
}) {
  return <Card style={{
    marginBottom: 16,
    border: "1.5px solid ".concat(B.navy)
  }}>{<CardHeader>{purchase._editing ? "Edit Purchase" : "Log a Purchase"}</CardHeader>}{<div style={{
      padding: "14px 16px"
    }}>{<div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gap: 10,
        marginBottom: 12
      }}>{<div>{<Label required>Date</Label>}{<Input type="date" value={purchase.date} onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              date: e.target.value
            });
          }} />}</div>}{<div>{<Label required>Vendor</Label>}{<Sel value={purchase.vendorId} onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              vendorId: e.target.value
            });
          }}>{<option value="">— Select —</option>}{vendors.map(function (v) {
              return <option key={v.id} value={v.id}>{v.name}</option>;
            })}{<option value="__new__">+ Add new…</option>}</Sel>}{purchase.vendorId === "__new__" && <Input style={{
            marginTop: 6
          }} placeholder="New vendor name" value={purchase._newVendorName || ""} onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              _newVendorName: e.target.value
            });
          }} />}</div>}{<div>{<Label>Source</Label>}{<Sel value={purchase.sourceType} onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              sourceType: e.target.value
            });
          }}>{SOURCE_TYPES.map(function (s) {
              return <option key={s} value={s}>{s}</option>;
            })}</Sel>}</div>}{<div>{<Label>Window</Label>}{<Sel value={purchase.buyWindow} onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              buyWindow: e.target.value
            });
          }}>{BUY_WINDOWS.map(function (w) {
              return <option key={w} value={w}>{w}</option>;
            })}</Sel>}</div>}</div>}{<div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
        gap: 10,
        marginBottom: 12
      }}>{<div>{<Label>Boxes Req.</Label>}{<Input type="number" value={purchase.boxesRequested} placeholder="0" onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              boxesRequested: e.target.value
            });
          }} />}</div>}{<div>{<Label>Boxes Rcvd.</Label>}{<Input type="number" value={purchase.boxesReceived} placeholder="0" onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              boxesReceived: e.target.value
            });
          }} />}{purchase.boxesRequested && purchase.boxesReceived && Number(purchase.boxesReceived) < Number(purchase.boxesRequested) && <div style={{
            color: B.alertRed,
            fontSize: 10,
            marginTop: 3
          }}>⚠ Short {Number(purchase.boxesRequested) - Number(purchase.boxesReceived)}</div>}</div>}{GRADES.map(function ({
          grade,
          color
        }) {
          return <div key={grade}>{<Label>{grade}</Label>}{<input type="number" value={purchase.gradeBreakdown[grade] || ""} placeholder="—" onChange={function onChange(e) {
              return _onChange4({
                ...purchase,
                gradeBreakdown: {
                  ...purchase.gradeBreakdown,
                  ...{ [grade]: e.target.value }
                }
              });
            }} style={{
              width: "100%",
              padding: "8px 8px",
              borderRadius: 7,
              border: "1px solid ".concat(color, "55"),
              fontSize: 13,
              textAlign: "center",
              color: B.navy,
              outline: "none",
              background: "".concat(color, "08")
            }} />}</div>;
        })}</div>}{<div style={{
        marginBottom: 12
      }}>{<Checkbox checked={purchase.femalesShort} onChange={function onChange(e) {
          return _onChange4({
            ...purchase,
            femalesShort: e.target.checked
          });
        }} label="Females were short / tight on this buy" />}</div>}{<div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        marginBottom: 14
      }}>{<div>{<Label>Raw Inbound Note</Label>}{<Textarea value={purchase.rawNote} rows={3} placeholder={'e.g. "8-7-2 (17 boxes total). Asked Pomes for 5-0-5…"'} onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              rawNote: e.target.value
            });
          }} />}{<div style={{
            color: B.textLight,
            fontSize: 10,
            marginTop: 3
          }}>Paste original note exactly as written</div>}</div>}{<div>{<Label>Notes / Shortage Details</Label>}{<Textarea value={purchase.notes} rows={3} placeholder="Vendor said females tight. Short on 2s. JT had extras…" onChange={function onChange(e) {
            return _onChange4({
              ...purchase,
              notes: e.target.value
            });
          }} />}</div>}</div>}{<div style={{
        display: "flex",
        gap: 10,
        justifyContent: "flex-end"
      }}>{onCancel && <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>}{<Btn variant="primary" onClick={onSave}>Save Purchase</Btn>}</div>}</div>}</Card>;
}

// ─── PURCHASE LOG TABLE ───────────────────────────────────────────────────────
function PurchaseLogTable({
  purchases,
  vendors,
  onDelete,
  onEdit
}) {
  const _useState29 = useState(null),
    _useState30 = _useState29,
    expandId = _useState30[0],
    setExpandId = _useState30[1];
  const vendorMap = Object.fromEntries(vendors.map(function (v) {
    return [v.id, v];
  }));
  if (!purchases.length) return <div style={{
    color: B.textLight,
    fontSize: 13,
    padding: "24px 0",
    textAlign: "center"
  }}>No purchases logged yet.</div>;
  return <div>{[...purchases].reverse().map(function (p) {
      const vendor = vendorMap[p.vendorId],
        isExp = expandId === p.id;
      const _short = p.boxesRequested && p.boxesReceived && Number(p.boxesReceived) < Number(p.boxesRequested);
      return <div key={p.id} style={{
        marginBottom: 8,
        border: "1px solid ".concat(B.cardBorder),
        borderRadius: 10,
        overflow: "hidden",
        background: B.white
      }}>{<div style={{
          padding: "11px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
          background: isExp ? B.rowAlt : B.white
        }} onClick={function onClick() {
          return setExpandId(isExp ? null : p.id);
        }}>{<div style={{
            flex: 1
          }}>{<div style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              flexWrap: "wrap"
            }}>{<span style={{
                color: B.navy,
                fontWeight: 700,
                fontSize: 13
              }}>{fmtDate(p.date)}</span>}{<span style={{
                background: B.infoBg,
                color: B.colAvg,
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 7px",
                borderRadius: 5
              }}>{p.sourceType}</span>}{<span style={{
                background: B.rowAlt,
                color: B.textMid,
                fontSize: 11,
                padding: "2px 7px",
                borderRadius: 5
              }}>{p.buyWindow}</span>}{p.femalesShort && <span style={{
                background: "#fff7ed",
                color: B.alertAmber,
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 5
              }}>⚠ Females short</span>}{_short && <span style={{
                background: B.warnBg,
                color: B.alertRed,
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 5
              }}>⚠ Short {Number(p.boxesRequested) - Number(p.boxesReceived)} boxes</span>}</div>}{<div style={{
              color: B.textLight,
              fontSize: 11,
              marginTop: 3
            }}>{(vendor === null || vendor === void 0 ? void 0 : vendor.name) || "Unknown vendor"}  · {GRADES.map(function ({
                grade
              }) {
                let _p$gradeBreakdown;
                return (_p$gradeBreakdown = p.gradeBreakdown) !== null && _p$gradeBreakdown !== void 0 && _p$gradeBreakdown[grade] ? "".concat(p.gradeBreakdown[grade], " ").concat(grade) : null;
              }).filter(Boolean).join(" / ") || "".concat(p.boxesRequested || "?", "/").concat(p.boxesReceived || "?", " boxes")}</div>}</div>}{<span style={{
            color: B.textLight,
            fontSize: 12
          }}>{isExp ? "▲" : "▼"}</span>}</div>}{isExp && <div style={{
          padding: "12px 14px",
          borderTop: "1px solid ".concat(B.divider),
          background: B.rowAlt
        }}>{<div style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 10
          }}>{GRADES.map(function ({
              grade,
              color
            }) {
              let _p$gradeBreakdown2;
              const qty = (_p$gradeBreakdown2 = p.gradeBreakdown) === null || _p$gradeBreakdown2 === void 0 ? void 0 : _p$gradeBreakdown2[grade];
              if (!qty) return null;
              return <span key={grade} style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                background: B.white,
                border: "1px solid ".concat(color, "44"),
                borderRadius: 6,
                padding: "4px 9px",
                fontSize: 12
              }}>{<span style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: color,
                  display: "inline-block"
                }} />}{grade}: {<strong>{qty}</strong>}</span>;
            })}</div>}{p.rawNote && <div style={{
            marginBottom: 10
          }}>{<div style={{
              color: B.textLight,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6,
              marginBottom: 4
            }}>RAW NOTE</div>}{<div style={{
              background: B.white,
              border: "1px solid ".concat(B.cardBorder),
              borderRadius: 7,
              padding: "8px 10px",
              fontSize: 12,
              color: B.textMid,
              fontFamily: "monospace",
              lineHeight: 1.5
            }}>{p.rawNote}</div>}</div>}{p.notes && <div style={{
            marginBottom: 10
          }}>{<div style={{
              color: B.textLight,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6,
              marginBottom: 4
            }}>NOTES</div>}{<div style={{
              fontSize: 12,
              color: B.textMid,
              lineHeight: 1.6
            }}>{p.notes}</div>}</div>}{<div style={{
            display: "flex",
            gap: 8
          }}>{<Btn small variant="ghost" onClick={function onClick() {
              return onEdit(p);
            }}>Edit</Btn>}{<Btn small variant="danger" onClick={function onClick() {
              return onDelete(p.id);
            }}>Delete</Btn>}</div>}</div>}</div>;
    })}</div>;
}

// ─── VENDOR MANAGER ───────────────────────────────────────────────────────────
function VendorManager({
  vendors,
  onAdd,
  onUpdate,
  onDelete
}) {
  const _useState31 = useState(false),
    _useState32 = _useState31,
    open = _useState32[0],
    setOpen = _useState32[1];
  const _useState33 = useState(null),
    _useState34 = _useState33,
    form = _useState34[0],
    setForm = _useState34[1];
  const _useState35 = useState(null),
    _useState36 = _useState35,
    editId = _useState36[0],
    setEditId = _useState36[1];
  const cancel = function cancel() {
    setForm(null);
    setEditId(null);
  };
  const save = function save() {
    let _form$name;
    if (!(form !== null && form !== void 0 && (_form$name = form.name) !== null && _form$name !== void 0 && _form$name.trim())) return;
    if (editId) onUpdate(form);else onAdd(form);
    cancel();
  };
  return <Card style={{
    marginTop: 16
  }}>{<button onClick={function onClick() {
      return setOpen(function (o) {
        return !o;
      });
    }} style={{
      width: "100%",
      background: "transparent",
      border: "none",
      padding: "14px 16px",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>{<span style={{
        color: B.navy,
        fontWeight: 700,
        fontSize: 14
      }}>{open ? "▲" : "▼"}  Vendor List ({vendors.length})</span>}{<Btn small variant="green" onClick={function onClick(e) {
        e.stopPropagation();
        setForm(makeVendor());
        setEditId(null);
        setOpen(true);
      }}>+ Add Vendor</Btn>}</button>}{open && <div style={{
      borderTop: "1.5px solid ".concat(B.rowSep),
      padding: "14px 16px"
    }}>{form && <Card style={{
        marginBottom: 14,
        border: "1.5px solid ".concat(B.navy)
      }}>{<div style={{
          padding: "12px 14px"
        }}>{<div style={{
            color: B.navy,
            fontWeight: 700,
            fontSize: 13,
            marginBottom: 12
          }}>{editId ? "Edit Vendor" : "New Vendor"}</div>}{<div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
            marginBottom: 10
          }}>{<div>{<Label required>Name</Label>}{<Input value={form.name} onChange={function onChange(e) {
                return setForm({
                  ...form,
                  name: e.target.value
                });
              }} placeholder="e.g. Pomes, JT" />}</div>}{<div>{<Label>Source Type</Label>}{<Sel value={form.sourceType} onChange={function onChange(e) {
                return setForm({
                  ...form,
                  sourceType: e.target.value
                });
              }}>{SOURCE_TYPES.map(function (s) {
                  return <option key={s} value={s}>{s}</option>;
                })}</Sel>}</div>}{<div>{<Label>Typical Window</Label>}{<Sel value={form.typicalWindow} onChange={function onChange(e) {
                return setForm({
                  ...form,
                  typicalWindow: e.target.value
                });
              }}>{BUY_WINDOWS.map(function (w) {
                  return <option key={w} value={w}>{w}</option>;
                })}</Sel>}</div>}</div>}{<div style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 10
          }}>{<Checkbox checked={form.reliable} onChange={function onChange(e) {
              return setForm({
                ...form,
                reliable: e.target.checked
              });
            }} label="Generally reliable" />}{<Checkbox checked={form.goodForFemales} onChange={function onChange(e) {
              return setForm({
                ...form,
                goodForFemales: e.target.checked
              });
            }} label="Good for females" />}{<Checkbox checked={form.emergencyFillIn} onChange={function onChange(e) {
              return setForm({
                ...form,
                emergencyFillIn: e.target.checked
              });
            }} label="Good for emergency fill-in" />}</div>}{<div style={{
            marginBottom: 10
          }}>{<Label>Notes</Label>}{<Textarea value={form.notes} rows={2} onChange={function onChange(e) {
              return setForm({
                ...form,
                notes: e.target.value
              });
            }} placeholder="Notes about this vendor…" />}</div>}{<div style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end"
          }}>{<Btn small variant="ghost" onClick={cancel}>Cancel</Btn>}{<Btn small variant="primary" onClick={save}>{editId ? "Save Changes" : "Add Vendor"}</Btn>}</div>}</div>}</Card>}{vendors.length === 0 && !form && <div style={{
        color: B.textLight,
        fontSize: 13,
        padding: "12px 0",
        textAlign: "center"
      }}>No vendors added yet.</div>}{vendors.map(function (v) {
        return <div key={v.id} style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "10px 0",
          borderBottom: "1px solid ".concat(B.divider)
        }}>{<div style={{
            flex: 1
          }}>{<div style={{
              color: B.textDark,
              fontWeight: 700,
              fontSize: 13
            }}>{v.name}</div>}{<div style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginTop: 4
            }}>{<span style={{
                background: B.infoBg,
                color: B.colAvg,
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 4
              }}>{v.sourceType}</span>}{<span style={{
                background: B.rowAlt,
                color: B.textMid,
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 4
              }}>Buys {v.typicalWindow}</span>}{v.reliable && <span style={{
                background: B.okBg,
                color: B.green,
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 4
              }}>✓ Reliable</span>}{v.goodForFemales && <span style={{
                background: "#fff7ed",
                color: B.orange,
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 4
              }}>♀ Good for females</span>}{v.emergencyFillIn && <span style={{
                background: "#faf5ff",
                color: "#7c3aed",
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 4
              }}>⚡ Emergency fill-in</span>}</div>}{v.notes && <div style={{
              color: B.textLight,
              fontSize: 11,
              marginTop: 4
            }}>{v.notes}</div>}</div>}{<div style={{
            display: "flex",
            gap: 6
          }}>{<Btn small variant="ghost" onClick={function onClick() {
              setForm({
                ...v
              });
              setEditId(v.id);
            }}>Edit</Btn>}{<Btn small variant="danger" onClick={function onClick() {
              return onDelete(v.id);
            }}>✕</Btn>}</div>}</div>;
      })}</div>}</Card>;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── SNAPSHOT HISTORY PANEL ──────────────────────────────────────────────────
function SnapshotHistoryPanel({
  snapshots
}) {
  const _useState37 = useState(false),
    _useState38 = _useState37,
    open = _useState38[0],
    setOpen = _useState38[1];
  const _useState39 = useState(null),
    _useState40 = _useState39,
    expandId = _useState40[0],
    setExpandId = _useState40[1];
  if (!snapshots || snapshots.length === 0) return null;

  // Newest first
  const sorted = [...snapshots].reverse();
  function fmtTakenAt(iso) {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric"
      }) + " · " + d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (e) {
      return iso;
    }
  }
  const GRADE_SIZES = {
    "1s": ["Colossal", "Premium"],
    "2s": ["Select", "Standard"],
    "3s": ["Large Female", "Regular Female"]
  };
  return <Card style={{
    marginBottom: 16,
    overflow: "hidden"
  }}>{<button onClick={function onClick() {
      return setOpen(function (o) {
        return !o;
      });
    }} style={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      background: B.rowAlt,
      border: "none",
      cursor: "pointer",
      borderBottom: open ? "1px solid ".concat(B.rowSep) : "none",
      WebkitTapHighlightColor: "transparent"
    }}>{<div style={{
        display: "flex",
        alignItems: "center",
        gap: 9
      }}>{<span style={{
          fontSize: 16
        }}>📋</span>}{<div style={{
          textAlign: "left"
        }}>{<div style={{
            color: B.navy,
            fontWeight: 700,
            fontSize: 13
          }}>Snapshot History</div>}{<div style={{
            color: B.textLight,
            fontSize: 11,
            marginTop: 1
          }}>{sorted.length} saved snapshot{sorted.length !== 1 ? "s" : ""} — view only</div>}</div>}</div>}{<span style={{
        color: B.textLight,
        fontSize: 13,
        fontWeight: 700
      }}>{open ? "▲" : "▼"}</span>}</button>}{open && <div>{sorted.map(function (snap, idx) {
        const isExp = expandId === snap.id;
        const t = snap.totals;
        const isFirst = idx === 0;
        return <div key={snap.id} style={{
          borderBottom: idx < sorted.length - 1 ? "1px solid ".concat(B.rowSep) : "none"
        }}>{<button onClick={function onClick() {
            return setExpandId(isExp ? null : snap.id);
          }} style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
            padding: "11px 16px",
            background: isFirst ? "#f0f9ff" : "#fff",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            WebkitTapHighlightColor: "transparent"
          }}>{<div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flex: 1,
              minWidth: 0
            }}>{isFirst && <span style={{
                background: B.navy,
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: .5,
                padding: "2px 6px",
                borderRadius: 4,
                flexShrink: 0
              }}>LATEST</span>}{<div style={{
                minWidth: 0
              }}>{<div style={{
                  color: B.textDark,
                  fontWeight: 700,
                  fontSize: 13
                }}>{fmtTakenAt(snap.takenAt)}</div>}{<div style={{
                  color: B.textLight,
                  fontSize: 11,
                  marginTop: 1
                }}>{snap.lots.length} lot{snap.lots.length !== 1 ? "s" : ""} · planning date {snap.date}</div>}</div>}</div>}{<div style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexShrink: 0
            }}>{[{
                label: "Usable",
                v: t.usable,
                c: "#166534"
              }, {
                label: "Urgent",
                v: t.urgent,
                c: "#c2540a"
              }, {
                label: "Expired",
                v: t.expired,
                c: "#b91c1c"
              }].map(function ({
                label,
                v,
                c
              }) {
                return v > 0 ? <div key={label} style={{
                  textAlign: "right"
                }}>{<div style={{
                    fontSize: 9,
                    color: B.textLight,
                    letterSpacing: .4,
                    textTransform: "uppercase"
                  }}>{label}</div>}{<div style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: c
                  }}>{fb(v)}{<span style={{
                      fontSize: 9,
                      fontWeight: 400,
                      color: B.textLight,
                      marginLeft: 2
                    }}>bu</span>}</div>}</div> : null;
              })}{<span style={{
                color: B.textLight,
                fontSize: 12,
                marginLeft: 4
              }}>{isExp ? "▲" : "▼"}</span>}</div>}</button>}{isExp && <div style={{
            padding: "12px 16px 16px",
            background: "#fafaf9",
            borderTop: "1px solid ".concat(B.rowSep)
          }}>{<div style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 12
            }}>{["1s", "2s", "3s"].map(function (grade) {
                const col = {
                  "1s": "#1d6fa8",
                  "2s": "#a16207",
                  "3s": "#c2540a"
                }[grade];
                const sizes = GRADE_SIZES[grade];
                const gradeUsable = r2(sizes.reduce(function (s, sz) {
                  let _snap$bySize;
                  return s + (((_snap$bySize = snap.bySize) === null || _snap$bySize === void 0 || (_snap$bySize = _snap$bySize[sz]) === null || _snap$bySize === void 0 ? void 0 : _snap$bySize.usable) || 0);
                }, 0));
                const gradeTotal = r2(sizes.reduce(function (s, sz) {
                  let _snap$bySize2;
                  return s + (((_snap$bySize2 = snap.bySize) === null || _snap$bySize2 === void 0 || (_snap$bySize2 = _snap$bySize2[sz]) === null || _snap$bySize2 === void 0 ? void 0 : _snap$bySize2.total) || 0);
                }, 0));
                if (!gradeTotal) return null;
                return <div key={grade} style={{
                  flex: "1 1 90px",
                  border: "1.5px solid ".concat(col, "33"),
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#fff"
                }}>{<div style={{
                    background: col,
                    padding: "4px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: 5
                  }}>{<span style={{
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 12
                    }}>{grade}</span>}</div>}{<div style={{
                    padding: "7px 10px"
                  }}>{<div style={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: col
                    }}>{fb(gradeUsable)}{<span style={{
                        fontSize: 10,
                        fontWeight: 400,
                        color: B.textLight,
                        marginLeft: 3
                      }}>bu usable</span>}</div>}{gradeTotal !== gradeUsable && <div style={{
                      fontSize: 10,
                      color: B.textLight,
                      textDecoration: "line-through"
                    }}>{fb(gradeTotal)} bu total</div>}</div>}</div>;
              })}</div>}{<div style={{
              fontSize: 11,
              color: B.textLight,
              fontWeight: 700,
              letterSpacing: .5,
              textTransform: "uppercase",
              marginBottom: 6
            }}>Lots</div>}{<div style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              border: "1px solid ".concat(B.cardBorder),
              borderRadius: 8,
              overflow: "hidden"
            }}>{snap.lots.map(function (lot, li) {
                const sc = SIZE_COLORS[lot.size] || B.navy;
                return <div key={li} style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1fr",
                  gap: 8,
                  padding: "7px 12px",
                  borderBottom: li < snap.lots.length - 1 ? "1px solid ".concat(B.rowSep) : "none",
                  background: "#fff",
                  borderLeft: "3px solid ".concat(sc)
                }}>{<span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: sc
                  }}>{lot.size}</span>}{<span style={{
                    fontSize: 12,
                    color: B.textMid
                  }}>{lot.steamDate || "—"}</span>}{<span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: B.textDark,
                    textAlign: "right"
                  }}>{lot.bushels || 0} bu</span>}</div>;
              })}</div>}</div>}</div>;
      })}</div>}</Card>;
}

// ─── BUYER SUMMARY CARD ──────────────────────────────────────────────────────
// Clean, collapsible planner summary. Light background, calm styling.

// ─── COLLAPSIBLE SECTION WRAPPER ───────────────────────────────────────
function CollapsibleSection({ title, badge, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen === true);
  return <Card style={{ marginBottom: 14 }}>
    <div onClick={function(e) { if (e.target.closest('button,input,select,a')) return; setOpen(function(v) { return !v; }); }} style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "11px 14px", cursor: "pointer", userSelect: "none",
      background: open ? "transparent" : B.rowAlt,
      borderRadius: open ? "10px 10px 0 0" : 10,
      borderBottom: open ? "1px solid " + B.cardBorder : "none"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: B.textDark }}>{title}</span>
        {badge && <span style={{ fontSize: 11, color: B.textLight, background: B.rowAlt, border: "1px solid " + B.cardBorder, borderRadius: 5, padding: "1px 7px" }}>{badge}</span>}
      </div>
      <span style={{ fontSize: 11, color: B.textLight, fontWeight: 600 }}>{open ? "\u25B2 Hide" : "\u25BC Show"}</span>
    </div>
    {open && children}
  </Card>;
}

function BuyerSummaryCard({
  invTotals,
  demandPicture,
  inboundByGrade,
  totalInboundBu,
  gradeBuy,
  liveSupplyByGrade,
  weekPaceSignal,
  lastWeekActualBu
}) {
  const _useState41 = useState(true),
    _useState42 = _useState41,
    open = _useState42[0],
    setOpen = _useState42[1];
  const _ref76 = gradeBuy || {},
    _ref76$byGrade = _ref76.byGrade,
    byGrade = _ref76$byGrade === void 0 ? {} : _ref76$byGrade,
    _ref76$totalIdealBu = _ref76.totalIdealBu,
    totalIdealBu = _ref76$totalIdealBu === void 0 ? 0 : _ref76$totalIdealBu,
    _ref76$totalLiveCover = _ref76.totalLiveCoverage,
    totalLiveCoverage = _ref76$totalLiveCover === void 0 ? 0 : _ref76$totalLiveCover;
  const totalDemand = (demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.totalTrueDemand) || 0;
  const hasUrgent = invTotals.urgent > 0;
  const hasExpired = invTotals.expired > 0;
  const netNeed = totalIdealBu; // from computeGradeBuy — live already subtracted

  // Totals from new summarize shape
  const totalLiveBoxes = r2(["1s", "2s", "3s"].reduce(function (s, g) {
    const x = (liveSupplyByGrade === null || liveSupplyByGrade === void 0 ? void 0 : liveSupplyByGrade[g]) || {};
    return s + (x.liveBu || 0);
  }, 0));
  const totalUngradedBoxes = r2(["1s", "2s", "3s"].reduce(function (s, g) {
    const x = (liveSupplyByGrade === null || liveSupplyByGrade === void 0 ? void 0 : liveSupplyByGrade[g]) || {};
    return s + (x.ungradedBu || 0);
  }, 0));
  const hasLive = totalLiveCoverage > 0;
  const GRADE_META = {
    "1s": {
      color: "#1d6fa8",
      label: "1s",
      sub: "Colossal / Premium"
    },
    "2s": {
      color: "#a16207",
      label: "2s",
      sub: "Select / Standard"
    },
    "3s": {
      color: "#c2540a",
      label: "3s",
      sub: "Lg Female / Reg Female"
    }
  };

  // Collapsed view — one row summary
  function CollapsedBar() {
    return <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      padding: "10px 14px"
    }}>{<div style={{
        display: "flex",
        gap: 14,
        flex: 1,
        flexWrap: "wrap",
        alignItems: "center"
      }}>{<span style={{
          fontSize: 12,
          color: B.textLight
        }}>Usable: {<strong style={{
            color: B.textDark
          }}>{fb(invTotals.usable)} bu</strong>}</span>}{totalDemand > 0 && <span style={{
          fontSize: 12,
          color: B.textLight
        }}>Demand: {<strong style={{
            color: B.textDark
          }}>{fb(totalDemand)} bu/wk</strong>}</span>}{hasLive && <span style={{
          fontSize: 12,
          color: "#166534"
        }}>Internal: {<strong>~{fb(totalLiveCoverage)} bu</strong>}</span>}{netNeed > 0 ? <span style={{
          fontSize: 12,
          color: "#b45309"
        }}>Need to buy: {<strong>{fb(netNeed)} bu</strong>}</span> : totalDemand > 0 ? <span style={{
          fontSize: 12,
          color: "#166534",
          fontWeight: 700
        }}>✓ Covered</span> : null}{hasUrgent && <span style={{
          fontSize: 11,
          color: "#c2540a",
          fontWeight: 700
        }}>⚡ {fb(invTotals.urgent)} bu urgent</span>}</div>}{<button onClick={function onClick() {
        return setOpen(true);
      }} style={{
        background: B.rowAlt,
        border: "1px solid ".concat(B.cardBorder),
        borderRadius: 7,
        padding: "5px 12px",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 700,
        color: B.textMid,
        WebkitTapHighlightColor: "transparent",
        flexShrink: 0
      }}>Show Details ▾</button>}</div>;
  }
  return <Card style={{
    marginBottom: 16
  }}>{<div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 14px",
      borderBottom: open ? "1px solid ".concat(B.rowSep) : "none"
    }}>{<div style={{
        fontWeight: 700,
        fontSize: 13,
        color: B.navy
      }}>Buyer Summary</div>}{open ? <button onClick={function onClick() {
        return setOpen(false);
      }} style={{
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 600,
        color: B.textLight,
        WebkitTapHighlightColor: "transparent"
      }}>Hide ▴</button> : <CollapsedBar />}</div>}{open && <div style={{
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 14
    }}>{hasUrgent && <div style={{
        background: "#fff7ed",
        border: "1px solid #fed7aa",
        borderRadius: 8,
        padding: "7px 12px",
        display: "flex",
        alignItems: "center",
        gap: 8
      }}>{<span style={{
          fontSize: 13
        }}>⚡</span>}{<span style={{
          color: "#c2540a",
          fontSize: 12,
          fontWeight: 700
        }}>{fb(invTotals.urgent)} bu urgent — day 8–9, shipping action required</span>}</div>}{hasExpired && <div style={{
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: 8,
        padding: "7px 12px",
        display: "flex",
        alignItems: "center",
        gap: 8
      }}>{<span style={{
          fontSize: 13
        }}>⛔</span>}{<span style={{
          color: "#b91c1c",
          fontSize: 12,
          fontWeight: 700
        }}>{fb(invTotals.expired)} bu expired — excluded from buy recommendation</span>}</div>}{<div>{<div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: .6,
          color: B.textLight,
          textTransform: "uppercase",
          marginBottom: 8
        }}>Inventory Position</div>}{<div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 8
        }}>{[{
            label: "Total",
            val: invTotals.total,
            color: B.textMid
          }, {
            label: "Usable",
            val: invTotals.usable,
            color: "#166534",
            bold: true
          }, {
            label: "Urgent",
            val: invTotals.urgent,
            color: hasUrgent ? "#c2540a" : B.textLight
          }, {
            label: "Expired",
            val: invTotals.expired,
            color: hasExpired ? "#b91c1c" : B.textLight
          }].map(function ({
            label,
            val,
            color,
            bold
          }) {
            return <div key={label} style={{
              background: B.rowAlt,
              borderRadius: 8,
              padding: "8px 10px",
              border: "1px solid ".concat(B.cardBorder)
            }}>{<div style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: .5,
                color: B.textLight,
                textTransform: "uppercase",
                marginBottom: 3
              }}>{label}</div>}{<div style={{
                fontSize: bold ? 18 : 14,
                fontWeight: bold ? 800 : 700,
                color: val > 0 ? color : B.textLight,
                lineHeight: 1
              }}>{val > 0 ? fb(val) : "—"}{val > 0 && <span style={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: B.textLight,
                  marginLeft: 3
                }}>bu</span>}</div>}</div>;
          })}</div>}</div>}{<div style={{
        height: 1,
        background: B.rowSep
      }} />}{totalDemand > 0 && <div>{<div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: .6,
          color: B.textLight,
          textTransform: "uppercase",
          marginBottom: 6
        }}>Weekly Demand</div>}{<div style={{
          fontSize: 18,
          fontWeight: 800,
          color: B.textDark,
          lineHeight: 1
        }}>{fb(totalDemand)}{<span style={{
            fontSize: 11,
            fontWeight: 400,
            color: B.textLight,
            marginLeft: 4
          }}>bu / week</span>}</div>}</div>}{hasLive && <>{<div style={{
          height: 1,
          background: B.rowSep
        }} />}{<div>{<div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: .6,
            color: B.textLight,
            textTransform: "uppercase",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6
          }}>{<span style={{
              color: "#166534"
            }}>●</span>}Internal Coverage — Live / Ungraded</div>}{<div style={{
            display: "flex",
            flexDirection: "column",
            gap: 7
          }}>{["1s", "2s", "3s"].map(function (g) {
              let _byGrade$g, _gInfo$primarySizes;
              const supply = (liveSupplyByGrade === null || liveSupplyByGrade === void 0 ? void 0 : liveSupplyByGrade[g]) || {};
              const lBoxes = supply.liveBu || 0;
              const uBoxes = supply.ungradedBu || 0;
              const cov = ((_byGrade$g = byGrade[g]) === null || _byGrade$g === void 0 ? void 0 : _byGrade$g.liveCoverage) || 0;
              if (!lBoxes && !uBoxes) return null;
              const m = GRADE_META[g];
              const gInfo = GRADES.find(function (x) {
                return x.grade === g;
              });
              const sizes = (gInfo === null || gInfo === void 0 || (_gInfo$primarySizes = gInfo.primarySizes) === null || _gInfo$primarySizes === void 0 ? void 0 : _gInfo$primarySizes.join(" / ")) || g;
              return <div key={g} style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderLeft: "3px solid ".concat(m.color),
                borderRadius: 7,
                padding: "8px 11px"
              }}>{<div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 5
                }}>{<div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6
                  }}>{<span style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      background: m.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 9,
                      fontWeight: 800,
                      color: "#fff",
                      flexShrink: 0
                    }}>{g}</span>}{<span style={{
                      fontSize: 11,
                      color: B.textMid,
                      fontWeight: 600
                    }}>{sizes}</span>}</div>}{<span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#166534"
                  }}>~{fb(cov)} bu coverage</span>}</div>}{<div style={{
                  display: "flex",
                  gap: 10,
                  fontFamily: "'Courier New',monospace",
                  fontSize: 10,
                  color: B.textLight
                }}>{lBoxes > 0 && <span>{lBoxes} live box{lBoxes !== 1 ? "es" : ""} × 100%</span>}{lBoxes > 0 && uBoxes > 0 && <span style={{
                    color: B.rowSep
                  }}>|</span>}{uBoxes > 0 && <span>{uBoxes} steamed box{uBoxes !== 1 ? "es" : ""} × 75%</span>}</div>}{<div style={{
                  fontSize: 10,
                  color: "#166534",
                  marginTop: 4,
                  fontWeight: 600
                }}>→ Grade {g} first to cover {sizes} demand</div>}</div>;
            })}{<div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 10px",
              background: B.rowAlt,
              borderRadius: 6,
              border: "1px solid ".concat(B.cardBorder)
            }}>{<span style={{
                fontSize: 10,
                color: B.textLight,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: .4
              }}>Total internal coverage:</span>}{<span style={{
                fontSize: 13,
                fontWeight: 800,
                color: "#166534"
              }}>~{fb(totalLiveCoverage)} bu</span>}</div>}</div>}</div>}</>}{<div style={{
        height: 1,
        background: B.rowSep
      }} />}{<div style={{
        display: "grid",
        gridTemplateColumns: "1fr 2fr",
        gap: 12,
        alignItems: "start"
      }}>{<div>{<div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: .6,
            color: B.textLight,
            textTransform: "uppercase",
            marginBottom: 6
          }}>Net Buy Need</div>}{<div style={{
            fontSize: 26,
            fontWeight: 800,
            lineHeight: 1,
            color: netNeed > 0 ? B.textDark : "#166534"
          }}>{netNeed > 0 ? fb(netNeed) : "0"}{<span style={{
              fontSize: 11,
              fontWeight: 400,
              color: B.textLight,
              marginLeft: 4
            }}>bu</span>}</div>}{netNeed === 0 && totalDemand > 0 && <div style={{
            color: "#166534",
            fontSize: 11,
            marginTop: 4,
            fontWeight: 600
          }}>Inventory covered ✓</div>}</div>}{<div>{<div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: .6,
            color: B.textLight,
            textTransform: "uppercase",
            marginBottom: 6
          }}>Recommended Buy</div>}{<div style={{
            display: "flex",
            flexDirection: "column",
            gap: 6
          }}>{["1s", "2s", "3s"].map(function (g) {
              const rec = byGrade[g] || {};
              const boxes = rec.realisticBoxes || 0;
              const bu = rec.idealBu || 0;
              const m = GRADE_META[g];
              return <div key={g} style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 10px",
                borderRadius: 7,
                background: boxes > 0 ? B.rowAlt : "transparent",
                border: boxes > 0 ? "1px solid ".concat(B.cardBorder) : "none",
                borderLeft: boxes > 0 ? "3px solid ".concat(m.color) : "none"
              }}>{<span style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  background: boxes > 0 ? m.color : B.rowSep,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 9,
                  fontWeight: 800,
                  color: boxes > 0 ? "#fff" : B.textLight,
                  flexShrink: 0
                }}>{g}</span>}{<div style={{
                  flex: 1
                }}>{<span style={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: boxes > 0 ? B.textDark : B.textLight
                  }}>{boxes > 0 ? boxes : "—"}{boxes > 0 && <span style={{
                      fontSize: 10,
                      fontWeight: 400,
                      color: B.textLight,
                      marginLeft: 4
                    }}>boxes</span>}</span>}{boxes > 0 && <span style={{
                    fontSize: 10,
                    color: B.textLight,
                    marginLeft: 8
                  }}>~{fb(bu)} bu{rec.overridden && <span style={{
                      color: "#c2540a",
                      marginLeft: 5
                    }}>overridden</span>}</span>}</div>}{<span style={{
                  fontSize: 10,
                  color: B.textLight,
                  flexShrink: 0
                }}>{m.sub}</span>}</div>;
            })}</div>}</div>}</div>}</div>}</Card>;
}
const STORAGE_KEY = "hcp_v1"; // bump suffix to force a clean reset on schema changes
const STORAGE_META = "hcp_v1_meta";
function loadStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
function saveStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(STORAGE_META, JSON.stringify({
      savedAt: new Date().toISOString()
    }));
  } catch (e) {
    // Quota exceeded or private-browsing restriction — fail silently
  }
}
function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_META);
  } catch (e) {}
}
function loadMeta() {
  try {
    const raw = localStorage.getItem(STORAGE_META);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

// Validate and sanitize a loaded inventory lot — strips any File objects
// that should never have been stored and ensures required fields exist.
function sanitizeLot(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (typeof raw.size !== "string" || !raw.size) return null;
  return makeLot({
    id: typeof raw.id === "string" ? raw.id : undefined,
    size: raw.size,
    steamDate: typeof raw.steamDate === "string" ? raw.steamDate : "",
    bushels: raw.bushels != null ? String(raw.bushels) : "0"
  });
}
function sanitizePurchase(raw) {
  if (!raw || typeof raw !== "object") return null;
  const p = makePurchase();
  return {
    ...p,
    id: typeof raw.id === "string" ? raw.id : p.id,
    date: typeof raw.date === "string" ? raw.date : p.date,
    vendorId: typeof raw.vendorId === "string" ? raw.vendorId : "",
    size: typeof raw.size === "string" ? raw.size : "",
    grade: typeof raw.grade === "string" ? raw.grade : "",
    boxes: raw.boxes != null ? String(raw.boxes) : "",
    bushels: raw.bushels != null ? String(raw.bushels) : "",
    pricePerBu: raw.pricePerBu != null ? String(raw.pricePerBu) : "",
    sourceType: typeof raw.sourceType === "string" ? raw.sourceType : "supplier",
    buyWindow: raw.buyWindow != null ? String(raw.buyWindow) : "",
    notes: typeof raw.notes === "string" ? raw.notes : ""
  };
}
function sanitizeVendor(raw) {
  if (!raw || typeof raw !== "object") return null;
  return makeVendor({
    id: typeof raw.id === "string" ? raw.id : undefined,
    name: typeof raw.name === "string" ? raw.name : "Unknown",
    sourceType: typeof raw.sourceType === "string" ? raw.sourceType : "supplier",
    typicalWindow: raw.typicalWindow != null ? String(raw.typicalWindow) : "",
    notes: typeof raw.notes === "string" ? raw.notes : ""
  });
}

// ─── SNAPSHOT HISTORY ────────────────────────────────────────────────────────
const SNAPSHOTS_KEY = "hcp_v1_snapshots";
const MAX_SNAPSHOTS = 30;

// Build a snapshot from a list of raw lots at a point in time
function makeSnapshot(lots, takenAt) {
  const today = takenAt || todayISO();
  const enriched = lots.map(function (l) {
    return enrichLot(l, today);
  });
  const bySize = {};
  SIZES.forEach(function ({
    size
  }) {
    bySize[size] = {
      total: 0,
      usable: 0
    };
  });
  let usable = 0,
    urgent = 0,
    expired = 0,
    total = 0;
  enriched.forEach(function (l) {
    const bu = Number(l.bushels) || 0;
    total = r2(total + bu);
    if (l.usable) {
      usable = r2(usable + bu);
      if (bySize[l.size]) bySize[l.size].usable = r2(bySize[l.size].usable + bu);
    }
    if (l.status === "URGENT") urgent = r2(urgent + bu);
    if (!l.usable) expired = r2(expired + bu);
    if (bySize[l.size]) bySize[l.size].total = r2(bySize[l.size].total + bu);
  });
  return {
    id: "snap-".concat(Date.now()),
    takenAt: new Date().toISOString(),
    date: today,
    lots: lots.map(function (l) {
      return {
        size: l.size,
        steamDate: l.steamDate,
        bushels: l.bushels
      };
    }),
    bySize: bySize,
    totals: {
      total: total,
      usable: usable,
      urgent: urgent,
      expired: expired
    }
  };
}
function sanitizeSnapshot(raw) {
  let _raw$totals, _raw$totals2, _raw$totals3, _raw$totals4;
  if (!raw || typeof raw !== "object" || !raw.id) return null;
  return {
    id: String(raw.id),
    takenAt: typeof raw.takenAt === "string" ? raw.takenAt : new Date().toISOString(),
    date: typeof raw.date === "string" ? raw.date : todayISO(),
    lots: Array.isArray(raw.lots) ? raw.lots.map(sanitizeLot).filter(Boolean) : [],
    bySize: raw.bySize && typeof raw.bySize === "object" ? raw.bySize : {},
    totals: {
      total: Number((_raw$totals = raw.totals) === null || _raw$totals === void 0 ? void 0 : _raw$totals.total) || 0,
      usable: Number((_raw$totals2 = raw.totals) === null || _raw$totals2 === void 0 ? void 0 : _raw$totals2.usable) || 0,
      urgent: Number((_raw$totals3 = raw.totals) === null || _raw$totals3 === void 0 ? void 0 : _raw$totals3.urgent) || 0,
      expired: Number((_raw$totals4 = raw.totals) === null || _raw$totals4 === void 0 ? void 0 : _raw$totals4.expired) || 0
    }
  };
}
function loadSnapshots() {
  try {
    const raw = localStorage.getItem(SNAPSHOTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map(sanitizeSnapshot).filter(Boolean);
  } catch (e) {
    return [];
  }
}
function saveSnapshots(snaps) {
  try {
    // Keep newest MAX_SNAPSHOTS only
    const trimmed = snaps.slice(-MAX_SNAPSHOTS);
    localStorage.setItem(SNAPSHOTS_KEY, JSON.stringify(trimmed));
  } catch (e) {}
}
function clearSnapshots() {
  try {
    localStorage.removeItem(SNAPSHOTS_KEY);
  } catch (e) {}
}
function hydrateState(saved) {
  let _saved$planningInputs, _saved$planningInputs2, _saved$planningInputs3, _saved$planningInputs4, _saved$planningInputs5, _saved$planningInputs6, _saved$planningInputs7, _saved$planningInputs8, _saved$planningInputs9;
  return {
    inventoryLots: Array.isArray(saved.inventoryLots) ? saved.inventoryLots.map(sanitizeLot).filter(Boolean) : [],
    planningInputs: {
      // Migrate old single inboundBu to new grade fields (one-time backwards compat)
      inbound1s: (_saved$planningInputs = (_saved$planningInputs2 = saved.planningInputs) === null || _saved$planningInputs2 === void 0 ? void 0 : _saved$planningInputs2.inbound1s) !== null && _saved$planningInputs !== void 0 ? _saved$planningInputs : "",
      inbound2s: (_saved$planningInputs3 = (_saved$planningInputs4 = saved.planningInputs) === null || _saved$planningInputs4 === void 0 ? void 0 : _saved$planningInputs4.inbound2s) !== null && _saved$planningInputs3 !== void 0 ? _saved$planningInputs3 : "",
      inbound3s: (_saved$planningInputs5 = (_saved$planningInputs6 = saved.planningInputs) === null || _saved$planningInputs6 === void 0 ? void 0 : _saved$planningInputs6.inbound3s) !== null && _saved$planningInputs5 !== void 0 ? _saved$planningInputs5 : "",
      weeklyOverride: (_saved$planningInputs7 = (_saved$planningInputs8 = saved.planningInputs) === null || _saved$planningInputs8 === void 0 ? void 0 : _saved$planningInputs8.weeklyOverride) !== null && _saved$planningInputs7 !== void 0 ? _saved$planningInputs7 : "",
      planDate: todayISO()
    },
    demandOverrides: saved.demandOverrides && typeof saved.demandOverrides === "object" ? saved.demandOverrides : {},
    gradeOverrides: saved.gradeOverrides && typeof saved.gradeOverrides === "object" ? saved.gradeOverrides : {},
    purchases: Array.isArray(saved.purchases) ? saved.purchases.map(sanitizePurchase).filter(Boolean) : [],
    vendors: Array.isArray(saved.vendors) ? saved.vendors.map(sanitizeVendor).filter(Boolean) : []
  };
}
function fmtSavedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit"
    }) + " · " + d.toLocaleDateString([], {
      month: "short",
      day: "numeric"
    });
  } catch (e) {
    return null;
  }
}

// ─── RECOMMENDATION EXPLANATION PANEL ────────────────────────────────────────
// Reads already-computed values and writes them out in plain operational language.
// No new math — this is purely a transparency / explanation layer.
function RecommendationExplanation({
  invTotals,
  demandPicture,
  inboundByGrade,
  totalInboundBu,
  gradeBuy,
  timingPlan,
  weekPaceSignal,
  lastWeekActualBu
}) {
  const totalDemand = (demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.totalTrueDemand) || 0;
  const _ref80 = gradeBuy || {},
    _ref80$byGrade = _ref80.byGrade,
    byGrade = _ref80$byGrade === void 0 ? {} : _ref80$byGrade,
    _ref80$totalIdealBu = _ref80.totalIdealBu,
    totalIdealBu = _ref80$totalIdealBu === void 0 ? 0 : _ref80$totalIdealBu;
  const hasDemand = totalDemand > 0;
  const hasInventory = invTotals.total > 0;
  const hasInbound = totalInboundBu > 0;
  const hasAnything = hasDemand || hasInventory;

  // Grade helpers
  const GRADE_COLOR = {
    "1s": "#1d6fa8",
    "2s": "#a16207",
    "3s": "#c2540a"
  };
  const GRADE_LABEL = {
    "1s": "1s (Colossal/Premium)",
    "2s": "2s (Select/Standard)",
    "3s": "3s (Female)"
  };
  function GradePill({
    grade,
    children
  }) {
    return <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      background: "".concat(GRADE_COLOR[grade], "18"),
      border: "1px solid ".concat(GRADE_COLOR[grade], "44"),
      color: GRADE_COLOR[grade],
      fontWeight: 700,
      fontSize: 12,
      padding: "1px 7px",
      borderRadius: 4,
      whiteSpace: "nowrap"
    }}>{children}</span>;
  }

  // One explanation row: icon + bold label + plain-text sentence
  function Row({
    icon,
    label,
    detail,
    accent,
    indent
  }) {
    return <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "8px 0",
      borderBottom: "1px solid ".concat(B.rowSep),
      paddingLeft: indent ? 24 : 0
    }}>{<span style={{
        fontSize: 16,
        flexShrink: 0,
        lineHeight: 1.4
      }}>{icon}</span>}{<div style={{
        flex: 1,
        minWidth: 0
      }}>{<span style={{
          fontWeight: 700,
          fontSize: 13,
          color: accent || B.textDark
        }}>{label} </span>}{<span style={{
          fontSize: 13,
          color: B.textMid,
          lineHeight: 1.6
        }}>{detail}</span>}</div>}</div>;
  }

  // Empty state
  if (!hasAnything) {
    return <Card style={{
      marginBottom: 16
    }}>{<CardHeader>Why This Recommendation</CardHeader>}{<div style={{
        padding: "16px",
        color: B.textLight,
        fontSize: 13,
        lineHeight: 1.7
      }}>No demand or inventory data yet. Enter a weekly demand override below to enable the recommendation engine.</div>}</Card>;
  }
  return <Card style={{
    marginBottom: 16
  }}>{<CardHeader sub="Step-by-step breakdown of how this week's buy was calculated">Why This Recommendation</CardHeader>}{<div style={{
      padding: "0 16px 4px"
    }}>{<div style={{
        padding: "10px 0 4px",
        borderBottom: "2px solid ".concat(B.rowSep),
        marginBottom: 2
      }}>{<div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: .7,
          color: B.textLight,
          textTransform: "uppercase",
          marginBottom: 6
        }}>Step 1 — How much do we need this week?</div>}{hasDemand ? <div style={{ marginBottom: 4 }}><Row icon="\uD83D\uDCC8" label={fb(totalDemand) + " bu / week (2-wk avg)"} detail="2-week trailing average of completed Mon\u2013Fri weeks. Used as the buying baseline." /><Row icon="\uD83D\uDDD3" label={"Last week: " + fb(lastWeekActualBu || 0) + " bu actual"} indent detail="Actual bushels shipped last completed week (Mon\u2013Fri only)." />{weekPaceSignal && <Row icon={weekPaceSignal.pct >= 0 ? "\uD83D\uDD3C" : "\uD83D\uDD3D"} label={"This week pace: " + (weekPaceSignal.pct >= 0 ? "+" : "") + weekPaceSignal.pct + "% vs last week"} accent={weekPaceSignal.pct >= 15 ? "#c2540a" : weekPaceSignal.pct <= -15 ? "#1d6fa8" : undefined} indent detail={"Through " + ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][weekPaceSignal.dow] + ": " + fb(weekPaceSignal.thisWkBu) + " bu this week vs " + fb(weekPaceSignal.lastWkBu) + " bu last week at same point."}/> }{weekPaceSignal && weekPaceSignal.pct >= 15 && <Row icon="\u26A0\uFE0F" label={"Running " + weekPaceSignal.pct + "% above avg \u2014 consider buying closer to " + fb(r2(totalDemand * (1 + weekPaceSignal.pct / 100))) + " bu if pace holds"} accent="#c2540a" indent detail="Not automatic \u2014 use your judgment based on order flow." />}{weekPaceSignal && weekPaceSignal.pct <= -15 && <Row icon="\uD83D\uDCC9" label={"Running " + Math.abs(weekPaceSignal.pct) + "% below avg \u2014 pace is soft this week"} accent="#1d6fa8" indent detail="Buy recommendation uses 2-wk avg as floor." />}</div>: <Row icon="\u26A0\uFE0F" label="No demand set" accent="#b91c1c" detail="Set a weekly demand override in Planning Inputs to enable the recommendation engine." />}{timingPlan && <Row icon="📅" label={"Remaining demand this week: " + fb(timingPlan.remainingThisWeekDemandBu) + " bu"} detail={"Weekends excluded from burn (no shipping Sat/Sun). " + timingPlan.shippingDaysToMon + " shipping day" + (timingPlan.shippingDaysToMon !== 1 ? "s" : "") + " to Monday at " + fb(timingPlan.dailyBurnBu) + " bu/day. Monday Louisiana buy is the primary buy \u2014 arrives Wednesday. Sized to cover remainder of this week plus all of next week."} indent />}</div>}{<div style={{
        padding: "10px 0 4px",
        borderBottom: "2px solid ".concat(B.rowSep),
        marginBottom: 2
      }}>{<div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: .7,
          color: B.textLight,
          textTransform: "uppercase",
          marginBottom: 6
        }}>Step 2 — What do we have on hand?</div>}{hasInventory ? <>{<Row icon="✅" label={"".concat(fb(invTotals.usable), " bu usable inventory")} accent="#166534" detail="Inventory within shelf life (day 0–9). This is subtracted directly from demand before calculating what to buy." />}{invTotals.urgent > 0 && <Row icon="⚡" label={"".concat(fb(invTotals.urgent), " bu urgent (day 8\u20139)")} accent="#c2540a" detail="Counted as usable but needs to ship immediately. Already included in the usable total above." indent />}{invTotals.expired > 0 && <Row icon="⛔" label={"".concat(fb(invTotals.expired), " bu expired (day 10+)")} accent="#b91c1c" detail="Past shelf life. Not counted toward usable inventory and does not reduce the buy recommendation." indent />}</> : <Row icon="📦" label="No inventory on hand" detail="Import a crab count to load current inventory. Without it, the full demand figure is used as the buy target." />}{hasInbound && <Row icon="🚚" label={"".concat(fb(totalInboundBu), " bu already inbound")} accent="#1d6fa8" detail={<span>Purchased and en route — subtracted by grade: {["1s", "2s", "3s"].filter(function (g) {
            return inboundByGrade[g] > 0;
          }).map(function (g, i, arr) {
            return <span key={g}>{<GradePill grade={g}>{g}: {fb(inboundByGrade[g])} bu</GradePill>}{i < arr.length - 1 ? " " : ""}</span>;
          })}</span>} />}</div>}{<div style={{
        padding: "10px 0 4px",
        borderBottom: "2px solid ".concat(B.rowSep),
        marginBottom: 2
      }}>{<div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: .7,
          color: B.textLight,
          textTransform: "uppercase",
          marginBottom: 6
        }}>Step 3 — What's the shortfall?</div>}{hasDemand ? <>{<Row icon="🧮" label="Net need by grade" detail="For each grade, we subtract usable inventory and inbound from the projected demand. The remainder is what needs to be purchased." />}{["1s", "2s", "3s"].map(function (g) {
            const rec = byGrade[g] || {};
            const bu = rec.idealBu || 0;
            if (!bu && !hasDemand) return null;
            return <Row key={g} icon="→" label={<GradePill grade={g}>{GRADE_LABEL[g]}</GradePill>} detail={bu > 0 ? "".concat(fb(bu), " bu net demand after subtracting inventory and inbound. Rounds up to ").concat(rec.idealBoxes, " box").concat(rec.idealBoxes !== 1 ? "es" : "", ".").concat(rec.overridden ? " (manual override applied)" : "") : "Fully covered by inventory and inbound — no buy needed."} indent />;
          })}</> : <Row icon="—" label="Cannot calculate shortfall" detail="Set a demand figure to see the breakdown." />}{timingPlan && timingPlan.shortfallBu > 0 && <Row icon="⚠️" label={"".concat(fb(timingPlan.shortfallBu), " bu shortfall before Monday")} accent="#b91c1c" detail={"Current inventory (".concat(fb(timingPlan.totalAvail), " bu) will not cover demand across ").concat(timingPlan.shippingDaysToMon, " shipping days to Monday. A Wednesday supplemental buy (arrives Friday) may be needed to bridge this gap.")} />}{timingPlan && timingPlan.shortfallBu === 0 && invTotals.usable > 0 && <Row icon="✅" label="Inventory covers demand to Monday" accent="#166534" detail={"".concat(fb(timingPlan.totalAvail), " bu on hand will cover the estimated ").concat(fb(timingPlan.demandToMonBu), " bu needed before the next buy window.")} />}</div>}{<div style={{
        padding: "10px 0 12px"
      }}>{<div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: .7,
          color: B.textLight,
          textTransform: "uppercase",
          marginBottom: 6
        }}>Step 4 — What to order</div>}{totalIdealBu > 0 ? <>{<Row icon="🦀" label={"".concat(fb(totalIdealBu), " bu total to buy")} accent={B.navy} detail="Sum of all grade shortfalls. This is the minimum needed to meet projected weekly demand." />}{["1s", "2s", "3s"].map(function (g) {
            const rec = byGrade[g] || {};
            if (!rec.idealBoxes) return null;
            return <Row key={g} icon="📦" label={<GradePill grade={g}>{rec.realisticBoxes} boxes {g}</GradePill>} detail={"~".concat(fb(rec.idealBu), " bu \xB7 ").concat(rec.overridden ? "Manual override applied — this grade's box count was set by you, not calculated." : "Calculated from ".concat(fb(rec.idealBu), " bu net demand."))} indent />;
          })}{timingPlan && (timingPlan.noBuyNeeded ? <Row icon="\u2705" label={"Inventory covers the " + timingPlan.buyLabel + " buy window \u2014 no buy needed"} accent="#166534" detail={"Net on hand at arrival: " + fb(timingPlan.netOnHandAtArrival) + " bu covers " + fb(timingPlan.windowDemandBu) + " bu of projected demand across " + timingPlan.sellableDays + " shipping days (" + timingPlan.arrivalDate + " \u2013 " + timingPlan.expiryDate + ")."} /> : <Row icon="📅" label={timingPlan.buyBoxes + " boxes recommended for " + timingPlan.buyLabel + " buy"} accent={B.navy} detail={"Buy window: " + timingPlan.buyLabel + " " + timingPlan.buyDate + " \u2014 crabs arrive " + timingPlan.arrivalDate + ", expire " + timingPlan.expiryDate + " (" + timingPlan.sellableDays + " shipping days). "+ "Window demand: " + fb(timingPlan.windowDemandBu) + " bu (" + fb(timingPlan.dailyBurnBu) + " bu/day \xD7 " + timingPlan.sellableDays + " days). "+ "Net on hand at arrival: ~" + fb(timingPlan.netOnHandAtArrival) + " bu (current " + fb(timingPlan.totalAvail) + " bu minus ~" + fb(timingPlan.committedBeforeArrival) + " bu committed before arrival). "+ "Raw buy need: " + fb(timingPlan.rawBuyNeedBu) + " bu + 10% buffer = " + fb(timingPlan.bufferedBuyBu) + " bu."+ (timingPlan.upcomingHoliday ? " \u26A0 " + timingPlan.upcomingHoliday.name + " in " + timingPlan.upcomingHoliday.daysAway + " days \u2014 plan your " + timingPlan.upcomingHoliday.buyLabel + " buy accordingly." : "")} />)}</> : hasDemand ? <Row icon="✅" label="No buy needed this week" accent="#166534" detail="Current usable inventory plus inbound fully covers projected weekly demand for all grades." /> : <Row icon="—" label="Add demand data to see recommendation" detail="Enter a manual demand override." />}</div>}</div>}</Card>;
}

// ─── LIVE / UNGRADED CRAB TRACKER ────────────────────────────────────────────
const LIVE_CRABS_KEY = "hcp_v1_live_crabs";

// ── Tag definitions — keyed by display name ──────────────────────────────────
// bg / text / border / emoji(optional) used for the colored badge in table view
// Print sheet always shows plain text (BW-friendly), so no print styles needed here.
const TAGS = [
// Solid color tags
{
  name: "Checker",
  bg: "#1a1a1a",
  text: "#fff",
  border: "none",
  emoji: "",
  pattern: "checker"
}, {
  name: "Black",
  bg: "#1a1a1a",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Orange",
  bg: "#f97316",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Blue",
  bg: "#3b82f6",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Purple",
  bg: "#a855f7",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Silver",
  bg: "#9ca3af",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Smiley Face",
  bg: "#fbbf24",
  text: "#1a1a1a",
  border: "none",
  emoji: "🙂"
}, {
  name: "Zebra",
  bg: "#1a1a1a",
  text: "#fff",
  border: "none",
  emoji: "",
  pattern: "zebra"
}, {
  name: "Music Notes",
  bg: "#6366f1",
  text: "#fff",
  border: "none",
  emoji: "🎵"
}, {
  name: "Green",
  bg: "#22c55e",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Flowers",
  bg: "#ec4899",
  text: "#fff",
  border: "none",
  emoji: "🌸"
}, {
  name: "Red",
  bg: "#ef4444",
  text: "#fff",
  border: "none",
  emoji: ""
}, {
  name: "Yellow",
  bg: "#eab308",
  text: "#1a1a1a",
  border: "none",
  emoji: ""
}, {
  name: "Pink",
  bg: "#f9a8d4",
  text: "#9d174d",
  border: "none",
  emoji: ""
}, {
  name: "Blue Stars",
  bg: "#2563eb",
  text: "#fff",
  border: "none",
  emoji: "⭐"
}, {
  name: "Yellow Sports",
  bg: "#eab308",
  text: "#1a1a1a",
  border: "none",
  emoji: "⚽"
}, {
  name: "Snowflakes",
  bg: "#bae6fd",
  text: "#0c4a6e",
  border: "none",
  emoji: "❄️"
}, {
  name: "Cancer Awareness",
  bg: "#f9a8d4",
  text: "#831843",
  border: "none",
  emoji: "🎀"
}, {
  name: "Happy Birthday",
  bg: "#fde68a",
  text: "#78350f",
  border: "none",
  emoji: "🎂"
}, {
  name: "Pool Pass",
  bg: "#38bdf8",
  text: "#0c4a6e",
  border: "none",
  emoji: "🏊"
}, {
  name: "Rainbow",
  bg: "linear-gradient(90deg,#ef4444,#f97316,#eab308,#22c55e,#3b82f6,#a855f7)",
  text: "#fff",
  border: "none",
  emoji: "🌈"
}, {
  name: "Visitor",
  bg: "#d1d5db",
  text: "#111827",
  border: "none",
  emoji: "🏷"
}];
const TAG_COLORS = TAGS.map(function (t) {
  return t.name;
});
const TAG_MAP = Object.fromEntries(TAGS.map(function (t) {
  return [t.name, t];
}));
// Legacy aliases — kept so any data saved with old names still renders
const TAG_COLOR_HEX = Object.fromEntries(TAGS.map(function (t) {
  return [t.name, t.bg];
}));
const TAG_TEXT_HEX = Object.fromEntries(TAGS.map(function (t) {
  return [t.name, t.text];
}));

// Render a tag badge inline — handles solid colors, patterns, and emoji
function TagBadge({
  name,
  style: extraStyle
}) {
  const tag = TAG_MAP[name] || {
    bg: "#e5e7eb",
    text: "#374151",
    emoji: "",
    border: "1px solid #ccc"
  };
  const isChecker = tag.pattern === "checker";
  const isZebra = tag.pattern === "zebra";
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    fontWeight: 700,
    padding: "3px 9px",
    borderRadius: 5,
    whiteSpace: "nowrap",
    lineHeight: 1.4,
    border: tag.border || "none",
    color: tag.text,
    position: "relative",
    overflow: "hidden",
    ...extraStyle
  };
  if (isChecker) {
    // CSS checkerboard: two-tone 6px squares
    return <span style={{
      ...baseStyle,
      background: "repeating-conic-gradient(#333 0% 25%, #aaa 0% 50%) 0 0 / 8px 8px",
      color: "#fff",
      textShadow: "0 1px 2px rgba(0,0,0,.7)"
    }}>{name}</span>;
  }
  if (isZebra) {
    return <span style={{
      ...baseStyle,
      background: "repeating-linear-gradient(45deg,#1a1a1a 0px,#1a1a1a 4px,#e5e7eb 4px,#e5e7eb 8px)",
      color: "#1a1a1a",
      textShadow: "0 1px 0 rgba(255,255,255,.5)"
    }}>{name}</span>;
  }
  return <span style={{
    ...baseStyle,
    background: tag.bg
  }}>{tag.emoji && <span style={{
      fontSize: 12
    }}>{tag.emoji}</span>}{name}</span>;
}
function makeEntry() {
  const o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    id: "lc-".concat(Date.now(), "-").concat(Math.random().toString(36).slice(2, 6)),
    date: todayISO(),
    vendor: "",
    tagColor: "Checker",
    status: "live",
    // "live" | "steamed" — drives planning factor
    breakdown: "",
    // received — e.g. "2-2-2"
    graded: "",
    // graded today — e.g. "1-0-1"
    notes: "",
    ...o
  };
}

// Parse a "2-2-2" style string into an array of numbers.
// Returns [] if the string is blank; each segment defaults to 0 if non-numeric.
function parseBreakdown(s) {
  const str = String(s || "").trim();
  if (!str) return [];
  return str.split("-").map(function (p) {
    const n = parseInt(p, 10);
    return isNaN(n) ? 0 : n;
  });
}

// Compute remaining = received - graded, per position, clamped to 0.
// Returns { parts, warning } where parts is the number array and
// warning is true if any graded segment exceeds received.
function calcRemaining(received, graded) {
  const rec = parseBreakdown(received);
  const grd = parseBreakdown(graded);
  if (!rec.length) return {
    parts: [],
    warning: false,
    str: ""
  };
  const len = Math.max(rec.length, grd.length);
  let warning = false;
  const parts = [];
  for (let i = 0; i < len; i++) {
    let r = rec[i] || 0;
    let g = grd[i] || 0;
    if (g > r) warning = true;
    parts.push(Math.max(0, r - g));
  }
  return {
    parts: parts,
    warning: warning,
    str: parts.join("-")
  };
}
function loadLiveCrabs() {
  try {
    const raw = localStorage.getItem(LIVE_CRABS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

// ── Live/ungraded → grade-level supply summary ─────────────────────────────
// Breakdown "a-b-c" positions map to buying grades: [0]=1s, [1]=2s, [2]=3s
// Returns { "1s":{boxes,vendors:[]}, "2s":{...}, "3s":{...} }
// Summarize remaining-to-grade boxes by buying grade across ALL live crab entries.
// Splits by status so the planner can apply different yield factors:
//   "live"    → liveBu    (will be counted at 100% in planning)
//   "steamed" → ungradedBu (will be counted at 75% in planning)
// Pass filterDate only when you explicitly need a single day (e.g. email recap).
function summarizeLiveRemaining(liveCrabs, filterDate) {
  const mk = function mk() {
    return {
      boxes: 0,
      liveBu: 0,
      ungradedBu: 0,
      vendors: []
    };
  };
  const out = {
    "1s": mk(),
    "2s": mk(),
    "3s": mk()
  };
  const gradeKeys = ["1s", "2s", "3s"];
  const entries = filterDate ? liveCrabs.filter(function (e) {
    return e.date === filterDate;
  }) : liveCrabs;
  entries.forEach(function (e) {
    const rem = calcRemaining(e.breakdown, e.graded);
    const isLive = (e.status || "live") === "live";
    if (!rem.parts.length) return;
    rem.parts.forEach(function (boxes, idx) {
      const grade = gradeKeys[idx];
      if (!grade || !boxes) return;
      const g = out[grade];
      g.boxes += boxes;
      if (isLive) g.liveBu += boxes; // 1 box = 1 bu for live
      else g.ungradedBu += boxes; // 1 box = 1 bu raw (factor applied in computeGradeBuy)
      if (boxes > 0) g.vendors.push({
        vendor: e.vendor || "(unknown)",
        tag: e.tagColor || "",
        steamDate: e.date || "",
        status: e.status || "live",
        boxes: boxes
      });
    });
  });
  return out;
}

// ── Grade-first recommendations ─────────────────────────────────────────────
// Detects shortages (demand > usable inventory) per size group, then checks
// whether live/ungraded crab of the matching buying grade could help cover it.
// Returns array of recommendation objects for rendering.
function buildGradeFirstRecs(liveByGrade, demandBySize, enrichedLots) {
  const recs = [];
  GRADES.forEach(function ({
    grade,
    primarySizes
  }) {
    const live = liveByGrade[grade];
    if (!live || live.boxes === 0) return; // no live supply for this grade

    // Check each sellable size in this grade for a shortage
    const shortSizes = [];
    primarySizes.forEach(function (size) {
      let _demandBySize$size;
      const demand = (demandBySize === null || demandBySize === void 0 || (_demandBySize$size = demandBySize[size]) === null || _demandBySize$size === void 0 ? void 0 : _demandBySize$size.trueDemand) || 0;
      if (!demand) return;
      const usable = enrichedLots.filter(function (l) {
        return l.size === size && l.usable;
      }).reduce(function (s, l) {
        return s + (parseFloat(l.bushels) || 0);
      }, 0);
      const dailyDemand = r2(demand / 7);
      if (usable < dailyDemand) shortSizes.push({
        size: size,
        usable: usable,
        dailyDemand: dailyDemand,
        shortfall: r2(dailyDemand - usable)
      });
    });
    if (!shortSizes.length) return; // no shortage in this grade's sizes

    const shortNames = shortSizes.map(function (s) {
      return s.size;
    }).join(" and ");
    const totalShort = r2(shortSizes.reduce(function (s, x) {
      return s + x.shortfall;
    }, 0));
    const vendorList = [...new Set(live.vendors.map(function (v) {
      const dateShort = v.steamDate ? new Date(v.steamDate + "T12:00:00").toLocaleDateString([], {
        month: "numeric",
        day: "numeric"
      }) : "";
      return "".concat(v.vendor).concat(v.tag ? " [" + v.tag + "]" : "").concat(dateShort ? " (" + dateShort + ")" : "");
    }))].join(", ");
    const gradeLabel = grade === "1s" ? "1s (Colossal/Premium)" : grade === "2s" ? "2s (Select/Standard)" : "3s (Large Female/Regular Female)";
    recs.push({
      id: "gf-".concat(grade),
      grade: grade,
      primarySizes: primarySizes,
      shortSizes: shortSizes,
      liveBoxes: live.boxes,
      vendors: live.vendors,
      confidence: totalShort > 2 ? "high" : totalShort > 0.5 ? "moderate" : "low",
      urgLabel: "🟢",
      action: "Grade ".concat(grade, " live crabs first \u2014 shortage detected in ").concat(shortNames),
      detail: "".concat(live.boxes, " box").concat(live.boxes !== 1 ? "es" : "", " of ungraded ").concat(gradeLabel, " remain (").concat(vendorList, "). Grading these now should yield ").concat(shortNames, " and help cover today's shortage."),
      explanation: "".concat(shortNames, " ").concat(shortSizes.length > 1 ? "are" : "is", " short by ~").concat(fb(totalShort), " bu today. ") + "Live/ungraded ".concat(grade, " crabs (").concat(live.boxes, " boxes from ").concat(vendorList, ") ") + "will grade out primarily into ".concat(shortNames, ". ") + "Prioritizing these now can reduce or eliminate the shortage before orders are pulled."
    });
  });
  return recs;
}
function saveLiveCrabs(entries) {
  try {
    localStorage.setItem(LIVE_CRABS_KEY, JSON.stringify(entries));
  } catch (e) {}
}
function clearLiveCrabs() {
  try {
    localStorage.removeItem(LIVE_CRABS_KEY);
  } catch (e) {}
}

// ── EntryForm lifted to module scope ─────────────────────────────────────────
// MUST be outside LiveUngradedTab so it has a stable identity across renders.
// When defined inside LiveUngradedTab, every keystroke re-renders the parent,
// which creates a new function reference — React treats it as a different
// component, unmounts + remounts it, and the focused input loses focus.
function LiveEntryForm({
  form,
  editId,
  onSetForm,
  onSave,
  onCancel
}) {
  const remaining = calcRemaining(form.breakdown, form.graded);
  const hasBreakdown = String(form.breakdown || "").trim().length > 0;
  const hasGraded = String(form.graded || "").trim().length > 0;
  return <Card style={{
    marginBottom: 16,
    border: "2px solid ".concat(B.navy)
  }}>{<div style={{
      background: B.navy,
      padding: "10px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    }}>{<div style={{
        color: "#fff",
        fontWeight: 700,
        fontSize: 14
      }}>{editId ? "Edit Entry" : "Add Entry"}</div>}{<button onClick={onCancel} style={{
        background: "rgba(255,255,255,.15)",
        border: "none",
        color: "#fff",
        borderRadius: 7,
        width: 30,
        height: 30,
        cursor: "pointer",
        fontSize: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>✕</button>}</div>}{<div style={{
      padding: "14px 16px",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12
    }}>{<div>{<Label required>Steam Date</Label>}{<Input type="date" value={form.date} onChange={function onChange(e) {
          return onSetForm(function (f) {
            return {
              ...f,
              date: e.target.value
            };
          });
        }} />}{<div style={{
          color: B.textLight,
          fontSize: 10,
          marginTop: 3
        }}>Used for FIFO labeling</div>}</div>}{<div>{<Label required>Vendor</Label>}{<Input type="text" value={form.vendor} placeholder="e.g. J&T" onChange={function onChange(e) {
          return onSetForm(function (f) {
            return {
              ...f,
              vendor: e.target.value
            };
          });
        }} />}</div>}{<div>{<Label>Tag</Label>}{<div style={{
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>{<Sel value={form.tagColor} onChange={function onChange(e) {
            return onSetForm(function (f) {
              return {
                ...f,
                tagColor: e.target.value
              };
            });
          }}>{TAG_COLORS.map(function (c) {
              return <option key={c} value={c}>{c}</option>;
            })}</Sel>}{<TagBadge name={form.tagColor} />}</div>}</div>}{<div>{<Label>Crab Status</Label>}{<Sel value={form.status || "live"} onChange={function onChange(e) {
          return onSetForm(function (f) {
            return {
              ...f,
              status: e.target.value
            };
          });
        }}>{<option value="live">Live (in boxes)</option>}{<option value="steamed">Steamed / Ungraded</option>}</Sel>}{<div style={{
          color: B.textLight,
          fontSize: 10,
          marginTop: 3
        }}>{(form.status || "live") === "live" ? "Live boxes count at 100% for planning" : "Steamed counts at 75% for planning"}</div>}</div>}{<div>{<Label>Received Breakdown</Label>}{<Input type="text" value={form.breakdown} placeholder="e.g. 2-2-2" style={{
          fontFamily: "monospace"
        }} onChange={function onChange(e) {
          return onSetForm(function (f) {
            return {
              ...f,
              breakdown: e.target.value
            };
          });
        }} />}{<div style={{
          color: B.textLight,
          fontSize: 10,
          marginTop: 3
        }}>Boxes received by grade</div>}</div>}{<div>{<Label style={{ opacity: (form.status || "live") === "live" ? 0.4 : 1 }}>Graded Today</Label>}{<Input type="text" value={form.graded} placeholder="e.g. 1-0-1" disabled={(form.status || "live") === "live"} style={{
          fontFamily: "monospace",
          borderColor: remaining.warning ? "#f97316" : undefined,
          opacity: (form.status || "live") === "live" ? 0.4 : 1,
          cursor: (form.status || "live") === "live" ? "not-allowed" : "text"
        }} onChange={function onChange(e) {
          return onSetForm(function (f) {
            return {
              ...f,
              graded: e.target.value
            };
          });
        }} />}{<div style={{
          color: B.textLight,
          fontSize: 10,
          marginTop: 3
        }}>{(form.status || "live") === "live" ? "Switch to Steamed to enter graded counts" : "Leave blank if none graded yet"}</div>}</div>}{<div style={{
        gridColumn: "1/-1"
      }}>{<div style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 14px",
          borderRadius: 9,
          background: remaining.warning ? "#fff7ed" : remaining.str ? "#f0fdf4" : B.rowAlt,
          border: "1.5px solid ".concat(remaining.warning ? "#fed7aa" : remaining.str ? "#bbf7d0" : B.cardBorder)
        }}>{<div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: .4,
            color: B.textLight,
            textTransform: "uppercase",
            flexShrink: 0
          }}>Remaining to Grade</div>}{hasBreakdown ? <div style={{
            fontFamily: "monospace",
            fontSize: 16,
            fontWeight: 800,
            color: remaining.warning ? "#c2540a" : remaining.str ? "#166534" : B.textMid,
            flex: 1
          }}>{remaining.str || "—"}</div> : <div style={{
            fontSize: 12,
            color: B.textLight,
            flex: 1
          }}>Enter received breakdown above</div>}{remaining.warning && <div style={{
            fontSize: 11,
            color: "#c2540a",
            fontWeight: 700,
            flexShrink: 0
          }}>⚠ Graded exceeds received</div>}</div>}</div>}{<div style={{
        gridColumn: "1/-1"
      }}>{<Label>Notes</Label>}{<Textarea rows={2} value={form.notes} placeholder="Optional — vendor notes, arrival details, etc." onChange={function onChange(e) {
          return onSetForm(function (f) {
            return {
              ...f,
              notes: e.target.value
            };
          });
        }} />}</div>}{<div style={{
        gridColumn: "1/-1",
        display: "flex",
        gap: 10,
        justifyContent: "flex-end"
      }}>{<button onClick={onCancel} style={{
          padding: "9px 18px",
          background: "#fff",
          border: "1px solid ".concat(B.cardBorder),
          borderRadius: 9,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          color: B.textMid
        }}>Cancel</button>}{<button onClick={onSave} disabled={!form.vendor.trim()} style={{
          padding: "9px 20px",
          background: form.vendor.trim() ? B.navy : "#94a3b8",
          border: "none",
          borderRadius: 9,
          fontSize: 13,
          fontWeight: 700,
          cursor: form.vendor.trim() ? "pointer" : "default",
          color: "#fff"
        }}>{editId ? "Save Changes" : "Add Entry"}</button>}</div>}</div>}</Card>;
}
function LiveUngradedTab({
  entries,
  onEntriesChange
}) {
  const _useState43 = useState(null),
    _useState44 = _useState43,
    form = _useState44[0],
    setForm = _useState44[1];
  const _useState45 = useState(null),
    _useState46 = _useState45,
    editId = _useState46[0],
    setEditId = _useState46[1];

  // Persist whenever entries change (entries is now App-level state)
  useEffect(function () {
    // saveLiveCrabs removed — liveCrabEntries syncs via Neon through App state
  }, [entries]);
  function openAdd() {
    setForm(makeEntry());
    setEditId(null);
  }
  function openEdit(entry) {
    setForm({
      ...entry
    });
    setEditId(entry.id);
  }
  function cancelForm() {
    setForm(null);
    setEditId(null);
  }
  function saveForm() {
    if (!form.vendor.trim()) return;
    if (editId) {
      onEntriesChange(function (es) {
        return es.map(function (e) {
          return e.id === editId ? {
            ...form
          } : e;
        });
      });
    } else {
      onEntriesChange(function (es) {
        return [form].concat([...es]);
      });
    }
    setForm(null);
    setEditId(null);
  }
  function deleteEntry(id) {
    if (!window.confirm("Remove this entry?")) return;
    onEntriesChange(function (es) {
      return es.filter(function (e) {
        return e.id !== id;
      });
    });
  }

  // ── Print floor sheet ─────────────────────────────────────────────────────
  // Uses a hidden iframe injected into the current document — avoids popup
  // blockers (window.open in async context) and works on mobile Safari.
  function printFloorSheet() {
    const byDate = {};
    entries.forEach(function (e) {
      if (!byDate[e.date]) byDate[e.date] = [];
      byDate[e.date].push(e);
    });
    const dates = Object.keys(byDate).sort().reverse();
    const esc = function esc(s) {
      return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    };
    const rows = dates.map(function (date) {
      const dayEntries = byDate[date];
      const dateLabel = new Date(date + "T12:00:00").toLocaleDateString([], {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric"
      });
      return "\n        <tr class=\"date-row\">\n          <td colspan=\"6\"><span class=\"date-label\">Steam Date:</span> ".concat(dateLabel, "</td>\n        </tr>\n        ").concat(dayEntries.map(function (e) {
        const rem = calcRemaining(e.breakdown, e.graded);
        return "\n            <tr class=\"entry-row\">\n              <td>".concat(esc(e.vendor) || "\u2014", "</td>\n              <td>").concat(esc(e.tagColor) || "\u2014", "</td>\n              <td class=\"mono\">").concat(esc(e.breakdown) || "\u2014", "</td>\n              <td class=\"mono\"></td>\n              <td class=\"death-cell\"></td>\n            </tr>");
      }).join(""), "\n      ");
    }).join("");
    const printContent = "\n      <!DOCTYPE html><html><head>\n      <meta charset=\"UTF-8\"/>\n      <title>Live Crab Floor Sheet</title>\n      <style>\n        *{box-sizing:border-box;margin:0;padding:0;}\n        body{font-family:Arial,sans-serif;font-size:14px;color:#000;background:#fff;padding:20px;}\n        h1{font-size:20px;font-weight:700;margin-bottom:3px;}\n        .sub{font-size:11px;color:#555;margin-bottom:18px;}\n        table{width:100%;border-collapse:collapse;}\n        th{background:#222;color:#fff;font-size:10px;font-weight:700;\n           letter-spacing:.5px;text-transform:uppercase;\n           padding:8px 10px;text-align:left;border:2px solid #222;}\n        td{padding:0 10px;border:1.5px solid #888;vertical-align:middle;}\n        .date-row td{background:#eee;font-weight:700;font-size:13px;\n                     padding:6px 10px;border:2px solid #333;}\n        .date-label{font-size:10px;font-weight:700;letter-spacing:.5px;\n                    text-transform:uppercase;color:#555;margin-right:6px;}\n        .entry-row{height:64px;}\n        .entry-row td{font-size:14px;}\n        .mono{font-family:monospace;font-size:14px;}\n        .warn{color:#c2540a;font-weight:700;}\n        .death-cell{min-width:18%;}\n      </style>\n      </head><body>\n      <h1>Live Crab Floor Sheet</h1>\n      <div class=\"sub\">\n        Printed: ".concat(new Date().toLocaleString(), " &nbsp;\xB7&nbsp;\n        Both columns are write-in\n      </div>\n      <table>\n        <thead>\n          <tr>\n            <th style=\"width:22%\">Vendor</th>\n            <th style=\"width:14%\">Tag Color</th>\n            <th style=\"width:20%\">Received</th>\n            <th style=\"width:20%\">\# of Tags (write in)</th>\n            <th style=\"width:24%\">Death Loss (write in)</th>\n          </tr>\n        </thead>\n        <tbody>\n          ").concat(rows || "<tr><td colspan=\"6\" style=\"padding:20px;text-align:center;color:#999;\">No entries to print</td></tr>", "\n        </tbody>\n      </table>\n      </body></html>");
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;width:0;height:0;border:none;top:0;left:0;";
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(printContent);
    iframe.contentDocument.close();
    iframe.onload = function () {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        setTimeout(function () {
          document.body.removeChild(iframe);
        }, 1000);
      }
    };
  }

  // ── Group entries by date for display, newest date first ─────────────────
  const grouped = useMemo(function () {
    const byDate = {};
    entries.forEach(function (e) {
      if (!byDate[e.date]) byDate[e.date] = [];
      byDate[e.date].push(e);
    });
    return Object.keys(byDate).sort().reverse().map(function (date) {
      return {
        date: date,
        label: new Date(date + "T12:00:00").toLocaleDateString([], {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric"
        }),
        rows: byDate[date]
      };
    });
  }, [entries]);
  return <div>{<div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 10,
      marginBottom: 16
    }}>{<div>{<div style={{
          color: B.navy,
          fontWeight: 700,
          fontSize: 15
        }}>Live / Ungraded Tracker</div>}{<div style={{
          color: B.textLight,
          fontSize: 12,
          marginTop: 2
        }}>Log vendor arrivals · print floor sheet for the hard crab room</div>}</div>}{<div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap"
      }}>{entries.length > 0 && <button onClick={printFloorSheet} style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "9px 16px",
          background: "#fff",
          border: "1.5px solid ".concat(B.cardBorder),
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          color: B.textDark,
          WebkitTapHighlightColor: "transparent"
        }}>🖨 Print Floor Sheet</button>}{<button onClick={openAdd} style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "9px 16px",
          background: B.navy,
          color: "#fff",
          border: "none",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent"
        }}>+ Add Entry</button>}</div>}</div>}{form && <LiveEntryForm form={form} editId={editId} onSetForm={setForm} onSave={saveForm} onCancel={cancelForm} />}{entries.length === 0 && !form ? <Card style={{
      padding: "32px 16px",
      textAlign: "center"
    }}>{<div style={{
        fontSize: 32,
        marginBottom: 12
      }}>🦀</div>}{<div style={{
        color: B.textMid,
        fontWeight: 700,
        fontSize: 14,
        marginBottom: 6
      }}>No entries yet</div>}{<div style={{
        color: B.textLight,
        fontSize: 13,
        marginBottom: 18,
        lineHeight: 1.6
      }}>Log live crab arrivals here each evening. Print the floor sheet the next morning for use in the hard crab room.</div>}{<button onClick={openAdd} style={{
        padding: "10px 22px",
        background: B.navy,
        color: "#fff",
        border: "none",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer"
      }}>+ Add First Entry</button>}</Card> : grouped.map(function (group) {
      return <Card key={group.date} style={{
        marginBottom: 14,
        overflow: "hidden"
      }}>{<div style={{
          padding: "9px 16px",
          background: B.rowAlt,
          borderBottom: "1px solid ".concat(B.rowSep),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>{<div style={{
            fontWeight: 700,
            fontSize: 13,
            color: B.navy
          }}>{<span style={{
              fontSize: 10,
              fontWeight: 600,
              color: B.textLight,
              textTransform: "uppercase",
              letterSpacing: .5,
              marginRight: 6
            }}>Steam Date</span>}{group.label}</div>}{<div style={{
            fontSize: 11,
            color: B.textLight
          }}>{group.rows.length} entr{group.rows.length === 1 ? "y" : "ies"}</div>}</div>}{<div style={{
          display: "grid",
          gridTemplateColumns: "1.3fr 0.9fr 1fr 1fr 1fr 36px",
          gap: 0,
          background: B.navy
        }}>{["Vendor", "Tag", "Received", "Graded", "Remaining", ""].map(function (h, i) {
            return <div key={i} style={{
              padding: "7px 10px",
              color: "rgba(255,255,255,.5)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .6,
              textTransform: "uppercase"
            }}>{h}</div>;
          })}</div>}{group.rows.map(function (entry, ri) {
          const rem = calcRemaining(entry.breakdown, entry.graded);
          return <div key={entry.id} style={{
            display: "grid",
            gridTemplateColumns: "1.3fr 0.9fr 1fr 1fr 1fr 36px",
            gap: 0,
            alignItems: "center",
            borderBottom: ri < group.rows.length - 1 ? "1px solid ".concat(B.rowSep) : "none",
            background: ri % 2 === 0 ? "#fff" : B.rowAlt,
            cursor: "pointer"
          }} onClick={function onClick() {
            return openEdit(entry);
          }}>{<div style={{
              padding: "11px 10px",
              fontWeight: 700,
              fontSize: 13,
              color: B.textDark
            }}>{entry.vendor || "—"}</div>}{<div style={{
              padding: "11px 10px"
            }}>{<TagBadge name={entry.tagColor} />}</div>}{<div style={{
              padding: "11px 10px",
              fontSize: 13,
              fontFamily: "monospace",
              color: entry.breakdown ? B.textDark : B.textLight
            }}>{entry.breakdown || "—"}</div>}{<div style={{
              padding: "11px 10px",
              fontSize: 13,
              fontFamily: "monospace",
              color: entry.graded ? B.textDark : B.textLight
            }}>{entry.graded || "—"}</div>}{<div style={{
              padding: "11px 10px",
              fontSize: 13,
              fontFamily: "monospace",
              fontWeight: rem.str ? 700 : 400,
              color: rem.warning ? "#c2540a" : rem.str ? "#166534" : B.textLight
            }}>{entry.breakdown ? rem.warning ? <span title="Graded exceeds received">⚠ {rem.str}</span> : rem.str || "0" : "—"}</div>}{<div style={{
              padding: "0 6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>{<button onClick={function onClick(e) {
                e.stopPropagation();
                deleteEntry(entry.id);
              }} style={{
                background: "none",
                border: "none",
                color: B.textLight,
                cursor: "pointer",
                fontSize: 14,
                padding: 4,
                lineHeight: 1,
                borderRadius: 4
              }}>✕</button>}</div>}</div>;
        })}</Card>;
    })}{entries.length > 0 && <div style={{
      textAlign: "center",
      marginTop: 8
    }}>{<button onClick={function onClick() {
        if (window.confirm("Clear all live crab entries? This cannot be undone.")) {
          clearLiveCrabs();
          onEntriesChange([]);
        }
      }} style={{
        background: "transparent",
        border: "none",
        color: B.textLight,
        fontSize: 12,
        cursor: "pointer",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: 3
      }}>Clear all entries</button>}</div>}</div>;
}

// ─── HARD CRAB TAKT REPORT ────────────────────────────────────────────────────
const TAKT_KEY = "hcp_v1_takt";
const TAKT_HIST_KEY = "hcp_v1_takt_history"; // [{date,name,bushels,hours,gph}]

function loadTakt() {
  try {
    const r = localStorage.getItem(TAKT_KEY);
    return r ? JSON.parse(r) : {};
  } catch (e) {
    return {};
  }
}
function saveTakt(d) {
  try {
    localStorage.setItem(TAKT_KEY, JSON.stringify(d));
  } catch (e) {}
}
function clearTakt() {
  try {
    localStorage.removeItem(TAKT_KEY);
  } catch (e) {}
}
function loadTaktHist() {
  try {
    const r = localStorage.getItem(TAKT_HIST_KEY);
    return r ? JSON.parse(r) : [];
  } catch (e) {
    return [];
  }
}
function saveTaktHist(h) {
  try {
    localStorage.setItem(TAKT_HIST_KEY, JSON.stringify(h));
  } catch (e) {}
}
function clearTaktHist() {
  try {
    localStorage.removeItem(TAKT_HIST_KEY);
  } catch (e) {}
}
function makeTaktDay() {
  return {
    graders: [],
    sorting: {
      projBushels: "",
      actualBushels: "",
      projSPH: "",
      actualSPH: ""
    },
    team: {
      projBushels: "",
      projGPH: ""
    },
    notes: ""
  };
}
function makeTaktGrader() {
  const o = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    id: "tg-".concat(Date.now(), "-").concat(Math.random().toString(36).slice(2, 5)),
    name: "",
    bushels: "",
    hours: "",
    qcChecks: [],
    ...o
  };
}

// QC sample sizes by grade
const QC_SAMPLES = { Colossal: 48, Premium: 60, Select: 72 };

function makeQcCheck(size) {
  return {
    id: "qc-".concat(Date.now(), "-").concat(Math.random().toString(36).slice(2, 5)),
    size: size || 'Colossal',
    trash: '',
    downgrade: ''
  };
}

function calcQcScore(check) {
  var sample = QC_SAMPLES[check.size] || 48;
  var trash = parseFloat(check.trash) || 0;
  var downgrade = parseFloat(check.downgrade) || 0;
  var points = trash + downgrade * 0.5;
  var good = Math.max(0, sample - points);
  return r2((good / sample) * 100);
}

function calcGraderQcAvg(qcChecks) {
  if (!qcChecks || qcChecks.length === 0) return null;
  var valid = qcChecks.filter(function(c) {
    return (parseFloat(c.trash) >= 0 || parseFloat(c.downgrade) >= 0) &&
           (c.trash !== '' || c.downgrade !== '');
  });
  if (!valid.length) return null;
  var sum = valid.reduce(function(s, c) { return s + calcQcScore(c); }, 0);
  return r2(sum / valid.length);
}

// Print QC sheet
function printQCSheet(date) {
  var dateLabel = date ? new Date(date + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  var graderBlocks = '';
  for (var i = 0; i < 4; i++) {
    var rows = '';
    for (var j = 0; j < 5; j++) {
      rows += '<tr>' +
        (j === 0 ? '<td class="grader-name" rowspan="5"><div class="name-line"></div></td>' : '') +
        '<td><div class="fill-line size-line"></div></td>' +
        '<td><div class="fill-line"></div></td>' +
        '<td><div class="fill-line"></div></td>' +
        '<td><div class="fill-line score-line">____%</div></td>' +
        '</tr>';
    }
    graderBlocks += '<tbody class="grader-block">' + rows + '</tbody>';
  }
  var html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>QC Sheet</title><style>' +
    'body{font-family:Arial,sans-serif;margin:24px;font-size:13px}' +
    'h2{margin:0 0 4px;font-size:16px}' +
    '.date{color:#666;margin-bottom:16px;font-size:12px}' +
    'table{width:100%;border-collapse:collapse}' +
    'th{background:#1e3a5f;color:#fff;padding:8px 10px;font-size:11px;text-align:left;letter-spacing:.5px}' +
    'th.center{text-align:center}' +
    'td{padding:6px 10px;border-bottom:1px solid #e5e7eb;vertical-align:middle}' +
    '.grader-name{border-right:2px solid #e5e7eb;width:18%;font-weight:700}' +
    '.name-line{border-bottom:2px solid #333;height:24px;width:90%}' +
    '.fill-line{border-bottom:1px solid #999;height:20px}' +
    '.size-line{width:80px}' +
    '.score-line{text-align:center;color:#999;font-size:12px}' +
    '.grader-block td{border-bottom:1px solid #f0f0f0}' +
    '.grader-block:not(:last-child) td{border-bottom:none}' +
    '.grader-block:not(:last-child){border-bottom:3px solid #1e3a5f}' +
    '.ref{margin-top:16px;font-size:11px;color:#666;border-top:1px solid #ccc;padding-top:8px}' +
    '@media print{body{margin:12px}.no-print{display:none}}' +
    '</style></head><body>' +
    '<div class="no-print" style="margin-bottom:12px"><button onclick="window.print()" style="padding:8px 16px;background:#1e3a5f;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px">🖨 Print</button></div>' +
    '<h2>CrabPlace QC Sheet</h2>' +
    '<div class="date">' + dateLabel + '</div>' +
    '<table>' +
    '<thead><tr><th>GRADER</th><th>SIZE</th><th class="center">TRASH</th><th class="center">DOWNGRADE</th><th class="center">SCORE</th></tr></thead>' +
    graderBlocks +
    '</table>' +
    '<div class="ref">Colossal = 48 crabs &nbsp;|&nbsp; Premium = 60 crabs &nbsp;|&nbsp; Select = 72 crabs</div>' +
    '</body></html>';
  var w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}

// ── TaktGraderTable — module scope (stable identity, no focus-loss) ───────────
function TaktGraderTable({
  graders,
  onUpdate,
  onAdd,
  onRemove
}) {
  return <div>{graders.length > 0 && <div style={{
      overflowX: "auto"
    }}>{<div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1.2fr 1.2fr 1.2fr 36px",
        background: B.navy,
        borderRadius: "8px 8px 0 0"
      }}>{["Grader", "Bushels Graded", "Hours Worked", "GPH", ""].map(function (h, i) {
          return <div key={i} style={{
            padding: "8px 12px",
            color: "rgba(255,255,255,.5)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: .6,
            textTransform: "uppercase",
            textAlign: i >= 1 && i <= 3 ? "center" : "left"
          }}>{h}</div>;
        })}</div>}{graders.map(function (g, ri) {
        const bu = parseFloat(g.bushels) || 0,
          hr = parseFloat(g.hours) || 0;
        const gph = bu > 0 && hr > 0 ? (bu / hr).toFixed(2) : "—";
        return <div key={g.id}>{<div style={{
          display: "grid",
          gridTemplateColumns: "2fr 1.2fr 1.2fr 1.2fr 36px",
          alignItems: "center",
          background: ri % 2 === 0 ? "#fff" : B.rowAlt,
          border: "1px solid ".concat(B.cardBorder),
          borderTop: "none"
        }}>{<div style={{
            padding: "8px 10px"
          }}>{<Input value={g.name} placeholder="First and last name" list="floor-names" autoComplete="off" style={{
              fontSize: 13,
              padding: "6px 8px",
              ...(isShortFloorName(g.name) ? { borderColor: "#b45309" } : {})
            }} onChange={function onChange(e) {
              return onUpdate(g.id, "name", e.target.value);
            }} onBlur={function onBlur(e) {
              var n = canonicalFloorName(e.target.value);
              if (n !== e.target.value) onUpdate(g.id, "name", n);
            }} />}{isShortFloorName(g.name) && <div style={{
              fontSize: 10,
              color: "#b45309",
              marginTop: 2
            }}>Add the last name</div>}</div>}{<div style={{
            padding: "8px 8px"
          }}>{<Input type="number" value={g.bushels} placeholder="0" style={{
              fontSize: 13,
              padding: "6px 8px",
              textAlign: "center"
            }} onChange={function onChange(e) {
              return onUpdate(g.id, "bushels", e.target.value);
            }} />}</div>}{<div style={{
            padding: "8px 8px"
          }}>{<Input type="number" value={g.hours} placeholder="optional" style={{
              fontSize: 13,
              padding: "6px 8px",
              textAlign: "center"
            }} onChange={function onChange(e) {
              return onUpdate(g.id, "hours", e.target.value);
            }} />}</div>}{<div style={{
            padding: "8px 8px",
            textAlign: "center",
            fontWeight: 700,
            fontSize: 14,
            color: gph !== "—" ? B.navy : B.textLight
          }}>{gph}</div>}{<div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px"
          }}>{<button onClick={function onClick() {
              return onRemove(g.id);
            }} style={{
              background: "none",
              border: "none",
              color: B.textLight,
              cursor: "pointer",
              fontSize: 14,
              padding: 4,
              borderRadius: 4
            }}>✕</button>}</div>}</div>}{<GraderQcSection grader={g} onUpdate={onUpdate} />}</div>;
      })}</div>}{<button onClick={onAdd} style={{
      width: "100%",
      padding: "10px",
      background: "#fff",
      border: "2px dashed ".concat(B.cardBorder),
      borderRadius: graders.length ? "0 0 8px 8px" : 8,
      color: B.textMid,
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer"
    }}>+ Add Grader</button>}{<datalist id="floor-names">{floorNameOptions().map(function (n) {
        return <option key={n} value={n} />;
      })}</datalist>}</div>;
}

// ── QC Checks section per grader ─────────────────────────────────────────────
function GraderQcSection({ grader, onUpdate }) {
  const checks = grader.qcChecks || [];
  function addCheck() {
    onUpdate(grader.id, 'qcChecks', [...checks, makeQcCheck('Colossal')]);
  }
  function updateCheck(cid, field, val) {
    onUpdate(grader.id, 'qcChecks', checks.map(function(c) { return c.id === cid ? { ...c, [field]: val } : c; }));
  }
  function removeCheck(cid) {
    onUpdate(grader.id, 'qcChecks', checks.filter(function(c) { return c.id !== cid; }));
  }
  var avg = calcGraderQcAvg(checks);
  var qualifies = avg !== null && avg >= 90;
  return <div style={{ background: '#f8fafc', border: '1px solid ' + B.cardBorder, borderTop: 'none', padding: '8px 12px', borderRadius: '0 0 6px 6px', marginBottom: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: B.textMid, textTransform: 'uppercase', letterSpacing: '.5px' }}>QC Checks</div>
      {avg !== null && <div style={{ fontSize: 12, fontWeight: 700, color: qualifies ? '#16a34a' : '#dc2626' }}>{qualifies ? '✅' : '❌'} Avg: {avg}%</div>}
      <button onClick={addCheck} style={{ fontSize: 11, padding: '3px 8px', background: B.navy, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>+ Add Check</button>
    </div>
    {checks.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 28px', gap: 4, marginBottom: 4 }}>
      {['Size', 'Trash', 'Downgrade', 'Score', ''].map(function(h, i) {
        return <div key={i} style={{ fontSize: 10, color: B.textLight, fontWeight: 700, textTransform: 'uppercase', textAlign: i > 0 ? 'center' : 'left' }}>{h}</div>;
      })}
    </div>}
    {checks.map(function(c) {
      var score = calcQcScore(c);
      var hasData = c.trash !== '' || c.downgrade !== '';
      return <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr 28px', gap: 4, alignItems: 'center', marginBottom: 4 }}>
        <select value={c.size} onChange={function(e) { updateCheck(c.id, 'size', e.target.value); }} style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid ' + B.cardBorder, fontSize: 12 }}>
          {Object.keys(QC_SAMPLES).map(function(s) { return <option key={s} value={s}>{s}</option>; })}
        </select>
        <input type="number" value={c.trash} placeholder="0" min="0" onChange={function(e) { updateCheck(c.id, 'trash', e.target.value); }} style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid ' + B.cardBorder, fontSize: 12, textAlign: 'center', width: '100%' }} />
        <input type="number" value={c.downgrade} placeholder="0" min="0" onChange={function(e) { updateCheck(c.id, 'downgrade', e.target.value); }} style={{ padding: '4px 6px', borderRadius: 5, border: '1px solid ' + B.cardBorder, fontSize: 12, textAlign: 'center', width: '100%' }} />
        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 13, color: hasData ? (score >= 90 ? '#16a34a' : '#dc2626') : B.textLight }}>{hasData ? score + '%' : '—'}</div>
        <button onClick={function() { removeCheck(c.id); }} style={{ background: 'none', border: 'none', color: B.textLight, cursor: 'pointer', fontSize: 13 }}>✕</button>
      </div>;
    })}
    {checks.length === 0 && <div style={{ fontSize: 12, color: B.textLight, textAlign: 'center', padding: '4px 0' }}>No QC checks yet</div>}
  </div>;
}

// ── TaktMetricRow — module scope (stable identity, no focus-loss) ─────────────
// Bug fix: was defined INSIDE HardCrabTaktTab, causing remount on every keystroke.
function TaktMetricRow({
  label,
  projected,
  actual,
  onProjChange,
  onActChange,
  actualReadOnly
}) {
  const proj = parseFloat(projected) || 0;
  const act = parseFloat(actual) || 0;
  const pct = proj > 0 && act > 0 ? Math.round(act / proj * 100) : null;
  return <div style={{
    display: "grid",
    gridTemplateColumns: "1.8fr 1fr 1fr 0.8fr",
    gap: 8,
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid ".concat(B.rowSep)
  }}>{<div style={{
      fontSize: 13,
      color: B.textDark,
      fontWeight: 600
    }}>{label}</div>}{<Input type="number" value={projected} placeholder="—" style={{
      fontSize: 13,
      padding: "6px 8px",
      textAlign: "center"
    }} onChange={function onChange(e) {
      return onProjChange(e.target.value);
    }} />}{actualReadOnly ? <div style={{
      textAlign: "center",
      fontSize: 13,
      fontWeight: 700,
      color: B.textMid
    }}>{actual || "—"}</div> : <Input type="number" value={actual} placeholder="—" style={{
      fontSize: 13,
      padding: "6px 8px",
      textAlign: "center"
    }} onChange={function onChange(e) {
      return onActChange(e.target.value);
    }} />}{<div style={{
      textAlign: "center",
      fontSize: 13,
      fontWeight: 700,
      color: pct == null ? "rgba(0,0,0,.2)" : pct >= 100 ? "#166534" : "#b45309"
    }}>{pct != null ? "".concat(pct, "%") : "—"}</div>}</div>;
}

// ── GraderLeaderboard — module scope ─────────────────────────────────────────
function GraderLeaderboard({
  history,
  onClearAll,
  onClearDate,
  onDeleteEntry
}) {
  const _useState47 = useState("month"),
    _useState48 = _useState47,
    period = _useState48[0],
    setPeriod = _useState48[1]; // "month"|"ytd"|"all"|"YYYY-MM"
  const _useState49 = useState(false),
    _useState50 = _useState49,
    showClear = _useState50[0],
    setShowClear = _useState50[1];
  const [showMonthPicker, setShowMonthPicker] = React.useState(false);
  const now = new Date();

  // Build list of distinct months from history
  const availableMonths = React.useMemo(function() {
    var seen = {};
    (history || []).forEach(function(r) {
      if (!r.date) return;
      var key = r.date.slice(0, 7); // YYYY-MM
      if (!seen[key]) seen[key] = true;
    });
    return Object.keys(seen).sort().reverse(); // most recent first
  }, [history]);

  const filtered = history.filter(function (r) {
    // Only require date and name — gph may be missing from older entries
    if (!r.date || !r.name) return false;
    if (period === "all") return true;
    const d = new Date(r.date + "T12:00:00");
    if (period === "ytd") return d.getFullYear() === now.getFullYear();
    if (period === "month") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    // specific month: "YYYY-MM"
    if (period.length === 7) return r.date.slice(0, 7) === period;
    return true;
  });

  // Label for current period
  function periodLabel() {
    if (period === "month") return "This Month";
    if (period === "ytd") return "Year to Date";
    if (period === "all") return "All Time";
    if (period.length === 7) {
      var d = new Date(period + "-01T12:00:00");
      return d.toLocaleDateString([], { month: 'long', year: 'numeric' });
    }
    return period;
  }

  // Aggregate by grader name
  const byGrader = {};
  filtered.forEach(function (r) {
    const n = canonicalFloorName(r.name);
    if (!n) return;
    if (!byGrader[n]) byGrader[n] = {
      name: n,
      days: 0,
      totalBu: 0,
      totalHrs: 0,
      gphs: [],
      bestGPH: 0,
      qcScores: []
    };
    const g = byGrader[n];
    g.days++;
    g.totalBu = r2(g.totalBu + (parseFloat(r.bushels) || 0));
    g.totalHrs = r2(g.totalHrs + (parseFloat(r.hours) || 0));
    var entryGph = r.gph ? parseFloat(r.gph) : (r.bushels > 0 && r.hours > 0 ? r2(parseFloat(r.bushels) / parseFloat(r.hours)) : 0);
    if (entryGph > 0) g.gphs.push(entryGph);
    if ((parseFloat(r.gph) || 0) > g.bestGPH) g.bestGPH = parseFloat(r.gph) || 0;
    // Collect QC avg per session
    if (r.qcAvg !== undefined && r.qcAvg !== null) g.qcScores = (g.qcScores || []).concat([r.qcAvg]);
    // Also recalculate from raw checks if available
    else if (r.qcChecks && r.qcChecks.length > 0) {
      var avg = calcGraderQcAvg(r.qcChecks);
      if (avg !== null) g.qcScores = (g.qcScores || []).concat([avg]);
    }
  });
  const rows = Object.values(byGrader).map(function (g) {
    // QC avg across sessions for this grader in this period
    var qcScores = g.qcScores || [];
    var qcAvg = qcScores.length > 0 ? r2(qcScores.reduce(function(s,v){return s+v;},0) / qcScores.length) : null;
    return {
      ...g,
      avgGPH: g.gphs.length ? r2(g.gphs.reduce(function (s, v) {
        return s + v;
      }, 0) / g.gphs.length) : 0,
      overallGPH: g.totalBu > 0 && g.totalHrs > 0 ? r2(g.totalBu / g.totalHrs) : 0,
      qcAvg: qcAvg,
      qcQualifies: qcAvg !== null && qcAvg >= 90
    };
  }).sort(function (a, b) {
    return b.avgGPH - a.avgGPH;
  });
  const PERIOD_LABELS = {
    month: "This Month",
    ytd: "Year to Date",
    all: "All Time"
  };
  if (history.length === 0) return <Card style={{
    marginBottom: 16
  }}>{<CardHeader sub="Builds automatically as you log daily TAKT reports">Grader Performance History</CardHeader>}{<div style={{
      padding: "20px 16px",
      textAlign: "center",
      color: B.textLight,
      fontSize: 13
    }}>No history yet. Log at least one grader with bushels and hours to start tracking.</div>}</Card>;

  // Get unique dates for per-date delete
  const allDates = [...new Set(history.map(function (r) {
    return r.date;
  }))].sort().reverse();
  return <Card style={{
    marginBottom: 16
  }}>{<div style={{
      background: B.navy,
      padding: "10px 16px",
      borderRadius: "10px 10px 0 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8
    }}>{<div>{<div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 14
        }}>Grader Performance History</div>}{<div style={{
          color: "rgba(255,255,255,.45)",
          fontSize: 11,
          marginTop: 1
        }}>Monthly / yearly GPH tracking · leaderboard</div>}</div>}{<div style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        flexWrap: "wrap"
      }}>{<div style={{ position: 'relative' }}>{<button onClick={function() { setShowMonthPicker(function(v){return !v;}); setPeriod('month'); }} style={{
            padding: "5px 12px",
            border: "none",
            borderRadius: 7,
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 700,
            background: (period === 'month' || period.length === 7) ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.07)",
            color: (period === 'month' || period.length === 7) ? "#fff" : "rgba(255,255,255,.5)"
          }}>{periodLabel()} {availableMonths.length > 1 ? '▾' : ''}</button>}
          {showMonthPicker && availableMonths.length > 0 && <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 99, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.15)', minWidth: 160, marginTop: 4, overflow: 'hidden' }}>
            <div onClick={function() { setPeriod('month'); setShowMonthPicker(false); }} style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: period === 'month' ? '#1e3a5f' : '#374151', background: period === 'month' ? '#eff6ff' : '#fff' }}>This Month</div>
            {availableMonths.filter(function(m) { return !(m === new Date().toISOString().slice(0,7)); }).map(function(m) {
              var d = new Date(m + '-01T12:00:00');
              var lbl = d.toLocaleDateString([], { month: 'long', year: 'numeric' });
              return <div key={m} onClick={function() { setPeriod(m); setShowMonthPicker(false); }} style={{ padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: period === m ? '#1e3a5f' : '#374151', background: period === m ? '#eff6ff' : '#fff', fontWeight: period === m ? 700 : 400 }}>{lbl}</div>;
            })}
          </div>}
        </div>}{['ytd','all'].map(function(val) {
          var lbl = val === 'ytd' ? 'Year to Date' : 'All Time';
          return <button key={val} onClick={function() { setPeriod(val); setShowMonthPicker(false); }} style={{
            padding: "5px 12px",
            border: "none",
            borderRadius: 7,
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 700,
            background: period === val ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.07)",
            color: period === val ? "#fff" : "rgba(255,255,255,.5)"
          }}>{lbl}</button>;
        })}{<button onClick={function onClick() {
          return setShowClear(function (x) {
            return !x;
          });
        }} style={{
          padding: "5px 10px",
          borderRadius: 7,
          cursor: "pointer",
          fontSize: 10,
          fontWeight: 700,
          background: showClear ? "rgba(255,80,80,.3)" : "rgba(255,255,255,.07)",
          border: "1px solid rgba(255,255,255,.15)",
          color: showClear ? "#fca5a5" : "rgba(255,255,255,.4)"
        }}>{showClear ? "▲ Close" : "🗑 Clear"}</button>}</div>}</div>}{showClear && <div style={{
      padding: "12px 16px",
      background: "#fef2f2",
      borderBottom: "1px solid #fecaca"
    }}>{<div style={{
        color: "#b91c1c",
        fontWeight: 700,
        fontSize: 12,
        marginBottom: 8
      }}>Clear Grader History</div>}{<div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center"
      }}>{<button onClick={function onClick() {
          if (window.confirm("Delete ALL grader performance history? This cannot be undone.\n\nThis will NOT affect any TAKT reports or other planner data.")) {
            onClearAll === null || onClearAll === void 0 || onClearAll();
            setShowClear(false);
          }
        }} style={{
          padding: "7px 14px",
          background: "#b91c1c",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer"
        }}>Clear All History</button>}{<div style={{
          display: "flex",
          gap: 5,
          flexWrap: "wrap"
        }}>{allDates.slice(0, 10).map(function (d) {
            return <button key={d} onClick={function onClick() {
              if (window.confirm("Delete history for ".concat(d, "?"))) onClearDate === null || onClearDate === void 0 || onClearDate(d);
            }} style={{
              padding: "5px 10px",
              background: "#fff",
              color: "#b91c1c",
              border: "1px solid #fecaca",
              borderRadius: 7,
              fontSize: 11,
              cursor: "pointer"
            }}>{new Date(d + "T12:00:00").toLocaleDateString([], {
                month: "short",
                day: "numeric"
              })} ✕</button>;
          })}</div>}</div>}{<div style={{
        color: "#7f1d1d",
        fontSize: 11,
        marginTop: 7,
        lineHeight: 1.6
      }}>Clearing history only removes performance records. Current TAKT reports, inventory, and planner data are unaffected.</div>}</div>}{rows.length === 0 ? <div style={{
      padding: "16px",
      textAlign: "center",
      color: B.textLight,
      fontSize: 13
    }}>No data for {periodLabel().toLowerCase()}.</div> : <div style={{
      overflowX: "auto"
    }}>{<div style={{
        display: "grid",
        gridTemplateColumns: "32px 2fr 0.8fr 1fr 1fr 1fr 1fr 0.8fr 40px",
        background: "#f8f6f2",
        borderBottom: "1px solid ".concat(B.rowSep)
      }}>{["#", "Grader", "Days", "Avg GPH", "Best GPH", "Total Bu", "Total Hrs", "QC Avg", ""].map(function (h, i) {
          return <div key={i} style={{
            padding: "8px 10px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: .5,
            color: B.textLight,
            textTransform: "uppercase",
            textAlign: i >= 2 ? "center" : "left"
          }}>{h}</div>;
        })}</div>}{rows.map(function (g, i) {
        const medal = ["🥇", "🥈", "🥉"][i] || "";
        const isTop = i === 0;
        return <div key={g.name} style={{
          display: "grid",
          gridTemplateColumns: "32px 2fr 0.8fr 1fr 1fr 1fr 1fr 0.8fr 40px",
          alignItems: "center",
          background: isTop ? "#fefce8" : i % 2 === 0 ? "#fff" : B.rowAlt,
          borderBottom: "1px solid ".concat(B.rowSep),
          borderLeft: isTop ? "3px solid #eab308" : "3px solid transparent"
        }}>{<div style={{
            padding: "10px 8px 10px 6px",
            textAlign: "center",
            fontSize: 14
          }}>{medal || i + 1}</div>}{<div style={{
            padding: "10px 10px",
            fontWeight: 700,
            fontSize: 13,
            color: B.textDark
          }}>{g.name}</div>}{<div style={{
            padding: "10px 10px",
            textAlign: "center",
            fontSize: 12,
            color: B.textLight
          }}>{g.days}</div>}{<div style={{
            padding: "10px 10px",
            textAlign: "center"
          }}>{<span style={{
              fontWeight: 800,
              fontSize: 15,
              color: isTop ? "#a16207" : B.navy
            }}>{g.avgGPH}</span>}{<span style={{
              fontSize: 10,
              color: B.textLight,
              marginLeft: 3
            }}>bu/hr</span>}</div>}{<div style={{
            padding: "10px 10px",
            textAlign: "center"
          }}>{<span style={{
              fontWeight: 700,
              fontSize: 13,
              color: "#166534"
            }}>{g.bestGPH}</span>}{<span style={{
              fontSize: 10,
              color: B.textLight,
              marginLeft: 3
            }}>bu/hr</span>}</div>}{<div style={{
            padding: "10px 10px",
            textAlign: "center",
            fontSize: 13,
            color: B.textMid
          }}>{g.totalBu} bu</div>}{<div style={{
            padding: "10px 10px",
            textAlign: "center",
            fontSize: 13,
            color: B.textMid
          }}>{g.totalHrs} hrs</div>}{<div style={{
            padding: "10px 10px",
            textAlign: "center",
            fontWeight: 700,
            fontSize: 12,
            color: g.qcAvg !== null && g.qcAvg !== undefined ? (g.qcQualifies ? '#16a34a' : '#dc2626') : B.textLight
          }}>{g.qcAvg !== null && g.qcAvg !== undefined ? (g.qcQualifies ? '✅ ' + g.qcAvg + '%' : '❌ ' + g.qcAvg + '%') : '—'}</div>}{onDeleteEntry ? <div style={{
            padding: "6px 4px",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>{<button onClick={function onClick() {
              if (window.confirm("Remove all history entries for ".concat(g.name, "?\nThis only affects the leaderboard \u2014 grading reports are not changed."))) {
                onDeleteEntry(g.name);
              }
            }} style={{
              background: "none",
              border: "1px solid #fecaca",
              borderRadius: 5,
              color: "#b91c1c",
              fontSize: 10,
              fontWeight: 700,
              cursor: "pointer",
              padding: "2px 6px",
              lineHeight: 1.4
            }}>✕</button>}</div> : <div />}</div>;
      })}{<div style={{
        padding: "8px 14px",
        background: "#f8f6f2",
        fontSize: 11,
        color: B.textLight,
        borderTop: "1px solid ".concat(B.rowSep)
      }}>{filtered.length} session{filtered.length !== 1 ? "s" : ""} · {rows.length} grader{rows.length !== 1 ? "s" : ""} · {periodLabel()}</div>}</div>}</Card>;
}
function HardCrabTaktTab({ taktAll, onTaktAllChange, taktHistory, onHistoryChange, onSaveNow }) {
  // Use App-level taktAll (Neon-synced) instead of localStorage
  const allData = taktAll || {};
  function setAllData(updater) {
    const next = typeof updater === 'function' ? updater(allData) : updater;
    onTaktAllChange(next);
    saveTakt(next); // keep localStorage as backup
  }
  // Use Neon-synced history from App state
  const history = taktHistory || [];
  const historyRef = useRef(history);
  historyRef.current = history;
  function setHistory(updater) {
    const next = typeof updater === 'function' ? updater(historyRef.current) : updater;
    onHistoryChange(next);
    try { localStorage.setItem('hcp_v1_takt_history', JSON.stringify(next)); } catch(e) {}
  }
  const _useState55 = useState(todayISO),
    _useState56 = _useState55,
    selDate = _useState56[0],
    setSelDate = _useState56[1];

  // Persist on change — localStorage as backup, Neon via App state debounce
  useEffect(function () {
    saveTakt(allData); // localStorage backup
    if (onSaveNow) setTimeout(onSaveNow, 100); // trigger Neon sync
  }, [allData]);
  useEffect(function () {
    saveTaktHist(history); // localStorage backup
  }, [history]);
  const day = allData[selDate] || makeTaktDay();
  const sorting = day.sorting || {
    projBushels: "",
    actualBushels: "",
    projSPH: "",
    actualSPH: ""
  };
  const team = day.team || {
    projBushels: "",
    projGPH: ""
  };
  const graders = day.graders || [];
  function setDay(updater) {
    setAllData(function (all) {
      const cur = all[selDate] || makeTaktDay();
      const next = typeof updater === "function" ? updater(cur) : {
        ...cur,
        ...updater
      };
      return {
        ...all,
        ...{ [selDate]: next }
      };
    });
  }

  // Grader helpers
  function addGrader() {
    setDay(function (d) {
      return {
        ...d,
        graders: [].concat([...d.graders], [makeTaktGrader()])
      };
    });
  }
  function removeGrader(id) {
    setDay(function (d) {
      return {
        ...d,
        graders: d.graders.filter(function (g) {
          return g.id !== id;
        })
      };
    });
  }
  function updateGrader(id, f, v) {
    setDay(function (d) {
      return {
        ...d,
        graders: d.graders.map(function (g) {
          return g.id === id ? {
            ...g,
            ...{ [f]: v }
          } : g;
        })
      };
    });
  }

  // Metric field setters — direct field updates, stable references
  function setProjBushels(v) {
    setDay(function (d) {
      return {
        ...d,
        sorting: {
          ...d.sorting,
          projBushels: v
        }
      };
    });
  }
  function setActBushels(v) {
    setDay(function (d) {
      return {
        ...d,
        sorting: {
          ...d.sorting,
          actualBushels: v
        }
      };
    });
  }
  function setProjSPH(v) {
    setDay(function (d) {
      return {
        ...d,
        sorting: {
          ...d.sorting,
          projSPH: v
        }
      };
    });
  }
  function setActSPH(v) {
    setDay(function (d) {
      return {
        ...d,
        sorting: {
          ...d.sorting,
          actualSPH: v
        }
      };
    });
  }
  function setTeamProjBu(v) {
    setDay(function (d) {
      return {
        ...d,
        team: {
          ...d.team,
          projBushels: v
        }
      };
    });
  }
  function setTeamProjGPH(v) {
    setDay(function (d) {
      return {
        ...d,
        team: {
          ...d.team,
          projGPH: v
        }
      };
    });
  }

  // Derived
  const totalBu = graders.reduce(function (s, g) {
    return s + (parseFloat(g.bushels) || 0);
  }, 0);
  const totalHrs = graders.reduce(function (s, g) {
    return s + (parseFloat(g.hours) || 0);
  }, 0);
  // Team GPH = simple average of individual GPHs
  const individualGPHs = graders
    .map(function(g) {
      var bu = parseFloat(g.bushels) || 0;
      var hr = parseFloat(g.hours) || 0;
      return bu > 0 && hr > 0 ? r2(bu / hr) : null;
    })
    .filter(function(v) { return v !== null; });
  const teamGPH = individualGPHs.length > 0 ? r2(individualGPHs.reduce(function(s,v){return s+v;},0) / individualGPHs.length) : null;
  const numWithGPH = individualGPHs.length;
  const savedDates = Object.keys(allData).filter(function (d) {
    let _allData$d, _allData$d2;
    return ((_allData$d = allData[d]) === null || _allData$d === void 0 || (_allData$d = _allData$d.graders) === null || _allData$d === void 0 ? void 0 : _allData$d.length) > 0 || ((_allData$d2 = allData[d]) === null || _allData$d2 === void 0 ? void 0 : _allData$d2.notes);
  }).sort().reverse();

  // History is only written on explicit commit — never on onChange or blur.
  function commitGraderHistory() {
    const validGraders = graders.filter(function (g) {
      return g.name.trim() && (parseFloat(g.bushels) || 0) > 0 && (parseFloat(g.hours) || 0) > 0;
    });
    if (!validGraders.length) {
      window.alert("No graders with name, bushels, and hours filled in.");
      return;
    }
    // A first name or nickname would start a second row for the same person on
    // the leaderboard — see utils/floorNames.js.
    const short = validGraders.filter(function (g) {
      return isShortFloorName(g.name);
    }).map(function (g) {
      return g.name.trim();
    });
    if (short.length) {
      window.alert("Use the full first and last name for: ".concat(short.join(", "), ".\n\nPick the name from the list, or type both names."));
      return;
    }
    const newEntries = validGraders.map(function (g) {
      const bu = parseFloat(g.bushels) || 0;
      const hr = parseFloat(g.hours) || 0;
      return {
        id: "gh-".concat(Date.now(), "-").concat(Math.random().toString(36).slice(2, 5)),
        date: selDate,
        name: canonicalFloorName(g.name),
        bushels: bu,
        hours: hr,
        gph: r2(bu / hr),
        qcChecks: g.qcChecks || [],
        qcAvg: calcGraderQcAvg(g.qcChecks || [])
      };
    });
    setHistory(function (hist) {
      // Overwrite any previous entry for this grader+date so re-saves are idempotent
      const without = hist.filter(function (h) {
        return !(h.date === selDate && newEntries.some(function (e) {
          return e.name === canonicalFloorName(h.name);
        }));
      });
      return [].concat([...without], [...newEntries]);
    });
    if (onSaveNow) setTimeout(onSaveNow, 100); // immediate Neon sync after commit
    window.alert("Performance saved for ".concat(newEntries.length, " grader").concat(newEntries.length !== 1 ? "s" : "", "."));
  }
  return <div>{<Card style={{
      marginBottom: 16,
      padding: "12px 16px"
    }}>{<div style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        justifyContent: "space-between"
      }}>{<div>{<div style={{
            color: B.navy,
            fontWeight: 700,
            fontSize: 15
          }}>Hard Crab TAKT Report</div>}{<div style={{
            color: B.textLight,
            fontSize: 12,
            marginTop: 2
          }}>Daily grading performance recap</div>}</div>}{<div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap"
        }}>{<div>{<Label>Report Date</Label>}{<Input type="date" value={selDate} onChange={function onChange(e) {
              return setSelDate(e.target.value);
            }} style={{
              fontSize: 13,
              padding: "6px 10px",
              minWidth: 150
            }} />}</div>}{savedDates.length > 1 && <div>{<Label>Past Reports</Label>}{<Sel value={selDate} onChange={function onChange(e) {
              return setSelDate(e.target.value);
            }} style={{
              fontSize: 13,
              padding: "6px 10px"
            }}>{savedDates.map(function (d) {
                return <option key={d} value={d}>{new Date(d + "T12:00:00").toLocaleDateString([], {
                    weekday: "short",
                    month: "short",
                    day: "numeric"
                  })}</option>;
              })}</Sel>}</div>}</div>}</div>}</Card>}{<Card style={{
      marginBottom: 16
    }}>{<div style={{
        background: B.navy,
        padding: "10px 16px",
        borderRadius: "10px 10px 0 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>{<div>{<div style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 14
          }}>Grader Performance</div>}{<div style={{
            color: "rgba(255,255,255,.45)",
            fontSize: 11,
            marginTop: 1
          }}>Bushels graded per person · GPH auto-calculated</div>}</div>}{graders.length > 0 && <div style={{
          color: "rgba(255,255,255,.7)",
          fontSize: 12,
          textAlign: "right"
        }}>{<div style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#86efac"
          }}>{r2(totalBu)} bu</div>}{<div>total graded</div>}</div>}</div>}{<div style={{
        padding: "14px 16px"
      }}>{<TaktGraderTable graders={graders} onUpdate={updateGrader} onAdd={addGrader} onRemove={removeGrader} />}{graders.length > 0 && <div style={{
          marginTop: 10,
          padding: "10px 14px",
          background: B.navy,
          borderRadius: 8,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10
        }}>{[{
            label: "Total Graded",
            value: "".concat(r2(totalBu), " bu"),
            color: "#86efac"
          }, {
            label: "Total Hours",
            value: totalHrs > 0 ? "".concat(r2(totalHrs), " hrs") : "—",
            color: "#bfdbfe"
          }, {
            label: "Team GPH",
            value: teamGPH != null ? "".concat(teamGPH, " bu/hr") : "—",
            color: "#fdba74"
          }].map(function (c) {
            return <div key={c.label} style={{
              textAlign: "center"
            }}>{<div style={{
                color: "rgba(255,255,255,.4)",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: .7,
                textTransform: "uppercase",
                marginBottom: 4
              }}>{c.label}</div>}{<div style={{
                color: c.value === "—" ? "rgba(255,255,255,.2)" : c.color,
                fontWeight: 800,
                fontSize: 16
              }}>{c.value}</div>}</div>;
          })}</div>}</div>}</Card>}{<Card style={{
      marginBottom: 16
    }}>{<CardHeader sub="Projected vs. actual sort performance for the day">Sorting Metrics</CardHeader>}{<div style={{
        padding: "12px 16px"
      }}>{<div style={{
          display: "grid",
          gridTemplateColumns: "1.8fr 1fr 1fr 0.8fr",
          gap: 8,
          marginBottom: 4
        }}>{["Metric", "Projected", "Actual", "% of Goal"].map(function (h) {
            return <div key={h} style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .5,
              color: B.textLight,
              textTransform: "uppercase",
              textAlign: h !== "Metric" ? "center" : "left"
            }}>{h}</div>;
          })}</div>}{<TaktMetricRow label="Bushels to Sort" projected={sorting.projBushels} actual={sorting.actualBushels} onProjChange={setProjBushels} onActChange={setActBushels} />}{<TaktMetricRow label="Sort per Hour (SPH)" projected={sorting.projSPH} actual={sorting.actualSPH} onProjChange={setProjSPH} onActChange={setActSPH} />}</div>}</Card>}{<Card style={{
      marginBottom: 16
    }}>{<CardHeader sub="Team-wide grading targets vs. actuals">Team Grading Metrics</CardHeader>}{<div style={{
        padding: "12px 16px"
      }}>{<div style={{
          display: "grid",
          gridTemplateColumns: "1.8fr 1fr 1fr 0.8fr",
          gap: 8,
          marginBottom: 4
        }}>{["Metric", "Projected", "Actual", "% of Goal"].map(function (h) {
            return <div key={h} style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: .5,
              color: B.textLight,
              textTransform: "uppercase",
              textAlign: h !== "Metric" ? "center" : "left"
            }}>{h}</div>;
          })}</div>}{<TaktMetricRow label="Bushels to Grade" projected={team.projBushels} actual={totalBu > 0 ? String(r2(totalBu)) : ""} onProjChange={setTeamProjBu} onActChange={function onActChange() {}} actualReadOnly />}{<div style={{
          display: "grid",
          gridTemplateColumns: "1.8fr 1fr 1fr 0.8fr",
          gap: 8,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: "1px solid ".concat(B.rowSep)
        }}>{<div style={{
            fontSize: 13,
            color: B.textDark,
            fontWeight: 600
          }}>Team GPH{numWithGPH > 0 && <span style={{
              fontSize: 10,
              color: B.textLight,
              fontWeight: 400,
              marginLeft: 6
            }}>(auto from graders)</span>}</div>}{<Input type="number" value={team.projGPH} placeholder="—" style={{
            fontSize: 13,
            padding: "6px 8px",
            textAlign: "center"
          }} onChange={function onChange(e) {
            return setTeamProjGPH(e.target.value);
          }} />}{<div style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 14,
            color: teamGPH != null ? B.navy : B.textLight
          }}>{teamGPH != null ? teamGPH : "—"}</div>}{<div style={{
            textAlign: "center",
            fontSize: 13,
            fontWeight: 700
          }}>{team.projGPH && teamGPH != null ? function () {
              const pct = Math.round(teamGPH / parseFloat(team.projGPH) * 100);
              return <span style={{
                color: pct >= 100 ? "#166534" : "#b45309"
              }}>{pct}%</span>;
            }() : <span style={{
              color: "rgba(0,0,0,.2)"
            }}>—</span>}</div>}</div>}{numWithGPH === 0 && graders.length > 0 && <div style={{
          marginTop: 8,
          fontSize: 12,
          color: B.textLight,
          lineHeight: 1.6
        }}>Enter hours for each grader above to auto-calculate Team GPH.</div>}</div>}</Card>}{<Card style={{
      marginBottom: 16
    }}>{<CardHeader>Notes</CardHeader>}{<div style={{
        padding: "10px 16px 14px"
      }}>{<Textarea rows={3} value={day.notes || ""} placeholder="Operational notes for this grading day…" onChange={function onChange(e) {
          return setDay(function (d) {
            return {
              ...d,
              notes: e.target.value
            };
          });
        }} />}</div>}</Card>}{<GraderLeaderboard history={history} onClearAll={function onClearAll() {
      clearTaktHist();
      setHistory([]);
    }} onClearDate={function onClearDate(date) {
      return setHistory(function (h) {
        return h.filter(function (r) {
          return r.date !== date;
        });
      });
    }} onDeleteEntry={function onDeleteEntry(name) {
      setHistory(function (h) {
        return h.filter(function (r) {
          return canonicalFloorName(r.name) !== name;
        });
      });
    }} />}{<div style={{
      textAlign: "center",
      marginTop: 12,
      marginBottom: 8,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      alignItems: "center"
    }}>{<button onClick={commitGraderHistory} style={{
        background: B.navy,
        color: "#fff",
        border: "none",
        borderRadius: 9,
        padding: "11px 28px",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        width: "100%",
        maxWidth: 340
      }}>✓ Save Grader Performance to History</button>}{<button onClick={function() { printQCSheet(selDate); }} style={{
        background: "#fff",
        color: B.navy,
        border: "2px solid ".concat(B.navy),
        borderRadius: 9,
        padding: "11px 22px",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer"
      }}>🖨 Print QC Sheet</button>}{<button onClick={function onClick() {
        if (window.confirm("Clear all data for ".concat(selDate, "?"))) {
          setAllData(function (all) {
            const n = {
              ...all
            };
            delete n[selDate];
            return n;
          });
          // Note: grader performance history is NOT cleared here — it persists
          // independently so monthly/yearly award tracking remains intact.
        }
      }} style={{
        background: "transparent",
        border: "none",
        color: B.textLight,
        fontSize: 12,
        cursor: "pointer",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: 3
      }}>Clear this day's report</button>}</div>}</div>;
}

// ─── EMAIL SUMMARY MODAL ─────────────────────────────────────────────────────
// Generates a plain-text daily ops recap email.
// Priority order: Inventory Snapshot → Action Plan → Live/Ungraded → Grading → Sorting → Purchasing
function EmailSummaryModal({
  onClose,
  invTotals,
  demandPicture,
  inboundByGrade,
  gradeBuy,
  planningInputs,
  inventoryLots,
  purchases,
  enrichedLots,
  parsedDays,
  crabMeatEntries,
  taktAllData,
  liveCrabEntries
}) {
  const _useState57 = useState(false),
    _useState58 = _useState57,
    copied = _useState58[0],
    setCopied = _useState58[1];
  const _useStateEod = useState(false),
    eodMode = _useStateEod[0],
    setEodMode = _useStateEod[1];
  const today = (planningInputs === null || planningInputs === void 0 ? void 0 : planningInputs.planDate) || todayISO();
  const dateLabel = new Date(today + "T12:00:00").toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  });

  // Use App-level state (Neon-synced) — no localStorage reads needed
  const taktDay = (taktAllData && taktAllData[today]) || makeTaktDay();
  const allLive = liveCrabEntries || [];

  const _ref93 = gradeBuy || {},
    _ref93$byGrade = _ref93.byGrade,
    byGrade = _ref93$byGrade === void 0 ? {} : _ref93$byGrade,
    _ref93$totalIdealBu = _ref93.totalIdealBu,
    totalIdealBu = _ref93$totalIdealBu === void 0 ? 0 : _ref93$totalIdealBu,
    _ref93$totalRealistic = _ref93.totalRealisticBoxes,
    totalRealisticBoxes = _ref93$totalRealistic === void 0 ? 0 : _ref93$totalRealistic;
  const totalDemand = (demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.totalTrueDemand) || 0;
  const demandBySize = (demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.bySize) || {};

  // ── Enrich lots if not passed ─────────────────────────────────────────────
  const eLots = enrichedLots || (inventoryLots || []).map(function (l) {
    return enrichLot(l, today);
  });

  // ── Per-size inventory summary ────────────────────────────────────────────
  const sizeInv = {};
  SIZES.forEach(function ({
    size
  }) {
    sizeInv[size] = {
      usable: 0,
      d1: 0, d2: 0, d3: 0, d4: 0, d5: 0,
      d6: 0,
      d7: 0,
      d8: 0,
      d9: 0,
      alerts: []
    };
  });
  eLots.forEach(function (lot) {
    const s = sizeInv[lot.size];
    if (!s) return;
    const bu = parseFloat(lot.bushels) || 0;
    if (lot.usable) s.usable = r2(s.usable + bu);
    if (lot.age === 1) s.d1 = r2(s.d1 + bu);
    if (lot.age === 2) s.d2 = r2(s.d2 + bu);
    if (lot.age === 3) s.d3 = r2(s.d3 + bu);
    if (lot.age === 4) s.d4 = r2(s.d4 + bu);
    if (lot.age === 5) s.d5 = r2(s.d5 + bu);
    if (lot.age === 6) s.d6 = r2(s.d6 + bu);
    if (lot.age === 7) s.d7 = r2(s.d7 + bu);
    if (lot.age === 8) s.d8 = r2(s.d8 + bu);
    if (lot.age === 9) s.d9 = r2(s.d9 + bu);
    if (lot.shipAlert) s.alerts.push(lot.shipAlert);
  });

  // ── buildOpsEmailData: clean ops recap — only approved data sources ────────
  function buildOpsEmailData(eodMode) {
    var todayDow = new Date(today + 'T12:00:00').getDay();
    var isFriday = todayDow === 5;

    // ── ACT NOW: use shared computeExpiryRisk for consistent logic ────────────
    // If parsedDays not loaded yet, expiryRisks will be empty — email shows loading msg
    var expiryRisks = parsedDays ? computeExpiryRisk(eLots, parsedDays, today, eodMode) : {};
    // Build urgentBySize with all steam dates collected per size
    var urgentBySize = {};
    Object.values(expiryRisks).forEach(function(r) {
      urgentBySize[r.size] = { size: r.size, bu: r.expiring, age: 0, steamDates: [], cutoff: r.cutoff, cutoffLabel: r.cutoffLabel };
    });
    (eLots || []).forEach(function(lot) {
      if (!lot.usable || !urgentBySize[lot.size]) return;
      var u = urgentBySize[lot.size];
      if (lot.steamDate && u.steamDates.indexOf(lot.steamDate) === -1) u.steamDates.push(lot.steamDate);
      if ((lot.age || 0) > u.age) u.age = lot.age;
    });
    // Sort steam dates ascending
    Object.values(urgentBySize).forEach(function(u) {
      u.steamDates.sort();
    });

    // Silent upgrade: show only one direct action per size (no cascade)
    var SILENT_UPGRADE = {
      'Colossal':       'Premium',
      'Premium':        'Select',
      'Select':         'Standard',
      'Large Female':   'Regular Female'
    };
    var OFFER_UP = {
      'Standard':       'Select',
      'Regular Female': 'Large Female'
    };

    // Cutoff label — no "must ship today" redundancy
    function cutoffLabel(age) {
      if (isFriday) return 'Friday cutoff';
      var day9Date = urgentBySize[Object.keys(urgentBySize)[0]] && urgentBySize[Object.keys(urgentBySize)[0]].steamDate
        ? _addDays(urgentBySize[Object.keys(urgentBySize)[0]].steamDate, 9) : null;
      if (age === 9) return 'last day';
      return 'Friday cutoff';
    }

    // Scheduled to go out remainder of week from parsedDays
    // EOD mode: start from tomorrow (today already shipped); Daily Plan: start from today
    var scheduledBySize = {};
    var scheduledTotal = 0;
    if (parsedDays) {
      var schedCur = new Date(today + 'T12:00:00');
      if (eodMode) schedCur.setDate(schedCur.getDate() + 1); // skip today for EOD recap
      var schedEnd = new Date(today + 'T12:00:00');
      var schedDow = schedEnd.getDay();
      var daysToFri = schedDow === 0 ? 5 : (schedDow <= 5 ? 5 - schedDow : 6);
      schedEnd.setDate(schedEnd.getDate() + daysToFri);
      while (schedCur <= schedEnd) {
        var d = schedCur.getDay();
        if (d >= 1 && d <= 5) {
          var dayISO = schedCur.toISOString().slice(0, 10);
          var dayData = parsedDays[dayISO];
          if (dayData) {
            SIZES.forEach(function({ size }) {
              var bu = Number(dayData[size]) || 0;
              if (bu > 0) {
                scheduledBySize[size] = r2((scheduledBySize[size] || 0) + bu);
                scheduledTotal = r2(scheduledTotal + bu);
              }
            });
          }
        }
        schedCur.setDate(schedCur.getDate() + 1);
      }
    }

    // Build day range label for scheduled section
    var schedStartDate = new Date(today + 'T12:00:00');
    if (eodMode) schedStartDate.setDate(schedStartDate.getDate() + 1);
    var schedStartLabel = schedStartDate.toLocaleDateString([], { weekday: 'short' });
    var schedFriDate = new Date(today + 'T12:00:00');
    schedFriDate.setDate(schedFriDate.getDate() + (schedFriDate.getDay() === 0 ? 5 : 5 - schedFriDate.getDay()));
    var schedFriLabel = 'Fri, ' + schedFriDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
    var schedRangeLabel = schedStartLabel === 'Fri' ? 'Fri' : schedStartLabel + '\u2013Fri';

    // Scheduled section lines
    var SIZE_ABBREV = { 'Colossal': 'Col', 'Premium': 'Prem', 'Select': 'Sel', 'Standard': 'Std', 'Large Female': 'LF', 'Regular Female': 'RF' };
    var scheduledLines = [];
    if (scheduledTotal > 0) {
      scheduledLines.push('  Scheduled ' + schedRangeLabel + ': ' + fb(scheduledTotal) + ' bu');
      var abbrevParts = SIZES
        .filter(function({ size }) { return scheduledBySize[size] > 0; })
        .map(function({ size }) { return SIZE_ABBREV[size] + ': ' + fb(scheduledBySize[size]); });
      if (abbrevParts.length) scheduledLines.push('  ' + abbrevParts.join('  '));
    } else {
      scheduledLines.push('  \u2022 No orders on file for remainder of week');
    }

    // Build ACT NOW lines — one direct action per size, no cascade
    var urgentSizes = Object.values(urgentBySize);
    urgentSizes.sort(function(a, b) { return (b.age || 0) - (a.age || 0); });
    var urgentSizeNames = new Set(urgentSizes.map(function(u) { return u.size; }));

    var actNow = urgentSizes.map(function(u) {
      var risk = expiryRisks[u.size] || {};
      var dl = u.steamDates && u.steamDates.length > 0
        ? u.steamDates.map(function(d) { return new Date(d + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' }); }).join(', ') + ' '
        : '';
      var cutoff = u.cutoffLabel || (u.age === 9 ? 'last day' : 'Friday cutoff');
      var lines = ['  \u2022 ' + dl + u.size + ' \u2014 ' + fb(u.bu) + ' bu  |  Out by ' + cutoff];
      if (risk.gap !== undefined) {
        lines.push('  At risk: ' + fb(risk.gap) + ' bu');
      }
      var silent = SILENT_UPGRADE[u.size];
      var offerUp = OFFER_UP[u.size];
      if (silent) {
        lines.push('    \u2192 Upgrade ' + silent + ' orders to ' + u.size);
      } else if (offerUp) {
        lines.push('    \u2192 Offer ' + u.size + ' to ' + offerUp + ' customers w/ credit or extras');
      } else {
        lines.push('    \u2192 Consider markdown or sale');
      }
      return lines.join('\n');
    });
    if (!actNow.length) {
      actNow = parsedDays
        ? ['  \u2022 No immediate act-now items']
        : ['  \u2022 Loading sales data\u2026 reopen email in a moment if data has loaded'];
    }

    // ── INVENTORY: per-size + total ───────────────────────────────────────────
    var invTotal = 0;
    var inv = SIZES.flatMap(function({ size }) {
      var s = sizeInv[size];
      if (!s || s.usable <= 0) return [];
      invTotal = r2(invTotal + s.usable);
      return ['  \u2022 ' + size + ' \u2014 ' + fb(s.usable) + ' bu'];
    });
    if (!inv.length) {
      inv = ['  \u2022 No inventory on hand'];
    } else {
      inv.push('  Total: ' + fb(invTotal) + ' bu');
    }

    // ── HELPER: compute last usable ship day from steamDate ─────────────────
    function lotCutoffISO(steamDate) {
      if (!steamDate) return null;
      var day9 = _addDays(steamDate, 9);
      var dow = _dow(day9);
      if (dow === 6) return _addDays(day9, -1); // Sat -> Fri
      if (dow === 0) return _addDays(day9, -2); // Sun -> Fri
      return day9;
    }

    // ── AGING INVENTORY REPORT ────────────────────────────────────────────────
    var todayDate = new Date(today + 'T12:00:00');
    // Friday of current week
    var fridayDate = new Date(todayDate);
    var fridayDow = fridayDate.getDay();
    var daysToFriday = fridayDow === 0 ? 5 : (fridayDow <= 5 ? 5 - fridayDow : 6);
    fridayDate.setDate(fridayDate.getDate() + daysToFriday);
    var fridayISO = fridayDate.toISOString().slice(0, 10);
    var fridayLabel = fridayDate.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' });

    var agingLines = [];
    var outByFridayTotal = 0;
    var grandTotal = 0;

    SIZES.forEach(function({ size }) {
      // Collect all usable lots for this size with their cutoff
      var lots = [];
      (eLots || []).forEach(function(lot) {
        if (lot.size !== size || !lot.usable) return;
        var bu = Number(lot.bushels) || 0;
        if (!bu) return;
        var cutoff = lotCutoffISO(lot.steamDate);
        lots.push({ bu: bu, cutoff: cutoff, steamDate: lot.steamDate });
      });
      if (!lots.length) return;

      // Sort lots by cutoff ascending
      lots.sort(function(a, b) {
        if (!a.cutoff && !b.cutoff) return 0;
        if (!a.cutoff) return 1;
        if (!b.cutoff) return -1;
        return a.cutoff < b.cutoff ? -1 : 1;
      });

      var sizeTotalBu = lots.reduce(function(s, l) { return r2(s + l.bu); }, 0);
      grandTotal = r2(grandTotal + sizeTotalBu);

      if (agingLines.length > 0) agingLines.push(''); // blank line between size groups
      agingLines.push('  \u25b8 ' + size.toUpperCase() + ' \u2014 ' + fb(sizeTotalBu) + ' bu');

      lots.forEach(function(l) {
        var cutoffDate = l.cutoff ? new Date(l.cutoff + 'T12:00:00') : null;
        var isUrgent = cutoffDate && l.cutoff <= fridayISO && l.cutoff >= today;
        if (isUrgent) outByFridayTotal = r2(outByFridayTotal + l.bu);
        var steamStr = l.steamDate
          ? new Date(l.steamDate + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' })
          : '?';
        var cutoffStr = cutoffDate
          ? cutoffDate.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' })
          : '?';
        if (isUrgent) {
          agingLines.push('    \u26a0\ufe0f ' + steamStr + ' \u2014 ' + fb(l.bu) + ' bu  |  Out by ' + cutoffStr);
        } else {
          agingLines.push('    ' + steamStr + ' \u2014 ' + fb(l.bu) + ' bu  |  Out by ' + cutoffStr);
        }
      });
    });

    if (!agingLines.length) {
      agingLines.push('  \u2022 No inventory on hand');
    } else {
      agingLines.push('  \u2500'.repeat(24));
      var footerLine = '  Total: ' + fb(grandTotal) + ' bu';
      if (outByFridayTotal > 0) footerLine += '  |  Out by ' + fridayLabel + ': ' + fb(outByFridayTotal) + ' bu';
      agingLines.push(footerLine);
    }

    // ── LAST WEEK SAME DAYS comparison ────────────────────────────────────────
    // Walk back week by week until we find a non-holiday week to use as baseline
    var queue = [];
    var comparisonNote = '';

    if (parsedDays) {
      // Figure out which days of the current week are remaining
      // EOD mode: exclude today (recap already sent), Daily Plan: include today
      var remainingDays = [];
      var cur = new Date(todayDate);
      if (eodMode) cur.setDate(cur.getDate() + 1); // start from tomorrow
      var end = new Date(fridayDate);
      while (cur <= end) {
        var dow = cur.getDay();
        if (dow >= 1 && dow <= 5) { // Mon-Fri only
          remainingDays.push(cur.toISOString().slice(0, 10));
        }
        cur.setDate(cur.getDate() + 1);
      }

      // Find a clean comparison week — walk back 1, 2, 3 weeks until non-holiday
      var baseWeekMonday = null;
      var holidayWarning = null;
      var weeksBack = 1;
      var MAX_WEEKS_BACK = 6;
      while (weeksBack <= MAX_WEEKS_BACK) {
        var candidateMonday = mondayOfWeek(today);
        var candDate = new Date(candidateMonday + 'T12:00:00');
        candDate.setDate(candDate.getDate() - (7 * weeksBack));
        var candISO = candDate.toISOString().slice(0, 10);
        var candHoliday = getHolidayForWeek(candISO);
        if (!candHoliday) {
          baseWeekMonday = candISO;
          break;
        }
        if (weeksBack === 1) {
          holidayWarning = candHoliday.name;
        }
        weeksBack++;
      }

      if (holidayWarning) {
        comparisonNote = '  \u26a0\ufe0f Last week was ' + holidayWarning + ' \u2014 numbers may be elevated. Showing ' + weeksBack + ' week(s) ago instead.';
      }

      // Map remaining days to their comparison-week equivalents
      var compTotal = 0;
      var compLines = [];
      var hasCompData = false;

      if (baseWeekMonday) {
        remainingDays.forEach(function(dayISO) {
          // Find the same day-of-week in the comparison week
          var dayDow = new Date(dayISO + 'T12:00:00').getDay(); // 1=Mon..5=Fri
          var compWeekStart = new Date(baseWeekMonday + 'T12:00:00');
          var compDate = new Date(compWeekStart);
          compDate.setDate(compWeekStart.getDate() + (dayDow - 1));
          var compISO = compDate.toISOString().slice(0, 10);
          var compDayLabel = new Date(dayISO + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' });
          var compDay = parsedDays[compISO];
          if (compDay) {
            hasCompData = true;
            var dayTotal = 0;
            var sizeParts = [];
            SIZES.forEach(function({ size }) {
              var bu = Number(compDay[size]) || 0;
              if (bu > 0) { sizeParts.push(size.replace(' Female', 'F') + ': ' + fb(bu)); dayTotal = r2(dayTotal + bu); }
            });
            compTotal = r2(compTotal + dayTotal);
            compLines.push('  ' + compDayLabel + ' \u2014 ' + fb(dayTotal) + ' bu' + (sizeParts.length ? '  (' + sizeParts.join(', ') + ')' : ''));
          }
        });
      }

      if (comparisonNote) queue.push(comparisonNote);
      if (hasCompData) {
        var weeksBackLabel = weeksBack === 1 ? 'Last week' : weeksBack + ' weeks ago';
        // Build day range label e.g. "Wed–Fri"
        var firstDayLabel = remainingDays.length > 0
          ? new Date(remainingDays[0] + 'T12:00:00').toLocaleDateString([], { weekday: 'short' })
          : '';
        var lastDayLabel = remainingDays.length > 1
          ? new Date(remainingDays[remainingDays.length - 1] + 'T12:00:00').toLocaleDateString([], { weekday: 'short' })
          : firstDayLabel;
        var dayRangeLabel = firstDayLabel === lastDayLabel ? firstDayLabel : firstDayLabel + '\u2013' + lastDayLabel;
        queue.push('  ' + weeksBackLabel + ' same days (' + dayRangeLabel + '): ' + fb(compTotal) + ' bu');
        // Abbreviated size breakdown on one line
        var SIZE_ABBREV = {
          'Colossal': 'Col', 'Premium': 'Prem', 'Select': 'Sel',
          'Standard': 'Std', 'Large Female': 'LF', 'Regular Female': 'RF'
        };
        var sizeBreakdown = {};
        compLines.forEach(function(line) {
          // compLines already formatted — re-derive totals from parsedDays directly
        });
        // Re-sum by size across all comparison days
        var compSizeTotals = {};
        remainingDays.forEach(function(dayISO) {
          var dayDow = new Date(dayISO + 'T12:00:00').getDay();
          var compWeekStart = new Date(baseWeekMonday + 'T12:00:00');
          var compDate = new Date(compWeekStart);
          compDate.setDate(compWeekStart.getDate() + (dayDow - 1));
          var compISO = compDate.toISOString().slice(0, 10);
          var compDay = parsedDays[compISO];
          if (!compDay) return;
          SIZES.forEach(function({ size }) {
            var bu = Number(compDay[size]) || 0;
            if (bu > 0) compSizeTotals[size] = r2((compSizeTotals[size] || 0) + bu);
          });
        });
        var abbrevParts = SIZES
          .filter(function({ size }) { return compSizeTotals[size] > 0; })
          .map(function({ size }) { return (SIZE_ABBREV[size] || size) + ': ' + fb(compSizeTotals[size]); });
        if (abbrevParts.length) queue.push('  ' + abbrevParts.join('  '));
      } else {
        queue.push('  \u2022 No comparison data available');
      }
    } else {
      queue.push('  \u2022 No forecast data loaded');
    }

    // ── LIVE / UNGRADED: split by crab status ────────────────────────────────
    var liveEntries = allLive.filter(function(e) {
      return (e.status || 'live') === 'live';
    });
    var steamedEntries = allLive.filter(function(e) {
      return (e.status || 'live') === 'steamed';
    });

    var liveLines = [];

    // Live (still alive) — counted in boxes
    if (liveEntries.length > 0) {
      liveLines.push('  Live (still alive):');
      liveEntries.forEach(function(e) {
        var rem = calcRemaining(e.breakdown, e.graded);
        if (!rem.parts.some(function(p){ return p > 0; })) return;
        var total = rem.parts.reduce(function(a, b){ return a + b; }, 0);
        var vendor = e.vendor || '(unknown)';
        liveLines.push('    \u2022 ' + vendor + ' \u2014 ' + rem.str + ' boxes/bu remaining (' + total + ' total)');
      });
    }

    // Steamed / Ungraded — counted in bushels
    if (steamedEntries.length > 0) {
      if (liveLines.length > 0) liveLines.push('');
      liveLines.push('  Steamed / Ungraded (in cooler):');
      steamedEntries.forEach(function(e) {
        var rem = calcRemaining(e.breakdown, e.graded);
        if (!rem.parts.some(function(p){ return p > 0; })) return;
        var total = rem.parts.reduce(function(a, b){ return a + b; }, 0);
        var vendor = e.vendor || '(unknown)';
        // Build grade breakdown: 1s: X / 2s: X / 3s: X (only non-zero grades)
        var grades = ['1s','2s','3s'];
        var breakdown = grades.map(function(g, i) {
          var v = rem.parts[i] || 0;
          return v > 0 ? g + ': ' + v + ' bu' : null;
        }).filter(Boolean).join(' / ');
        liveLines.push('    \u2022 ' + vendor + ' \u2014 ' + total + ' bu remaining' + (breakdown ? '  (' + breakdown + ')' : ''));
      });
    }

    // Add total breakdown at bottom
    var totalBoxes = 0, total1s = 0, total2s = 0, total3s = 0;
    allLive.forEach(function(e) {
      var rem = calcRemaining(e.breakdown, e.graded);
      rem.parts.forEach(function(p, i) {
        totalBoxes += (p || 0);
        if (i === 0) total1s += (p || 0);
        if (i === 1) total2s += (p || 0);
        if (i === 2) total3s += (p || 0);
      });
    });
    if (totalBoxes > 0 && liveLines.length > 0) {
      liveLines.push('  \u2500'.repeat(16));
      liveLines.push('  Total: ' + totalBoxes + ' boxes/bu  |  1s: ' + total1s + '  2s: ' + total2s + '  3s: ' + total3s);
    }

    if (liveLines.length === 0) {
      liveLines.push('  \u2022 No live crab entries');
    }

    // Grading priority
    var lbg = summarizeLiveRemaining(allLive);
    var gfe = buildGradeFirstRecs(lbg, demandBySize, eLots);
    if (gfe.length) {
      liveLines.push('');
      gfe.forEach(function(r) {
        liveLines.push('  \u2192 Grade ' + r.grade + ' first (' + r.liveBoxes + ' boxes) \u2014 ' + r.primarySizes.join('/'));
      });
    }

    // ── GRADING PERFORMANCE ───────────────────────────────────────────────────
    var graders = taktDay.graders || [];
    var totalGrBu = graders.reduce(function(s, g){ return s + (parseFloat(g.bushels) || 0); }, 0);
    var grading = [];
    if (graders.length > 0) {
      var individualGPHs = [];
      graders.forEach(function(g) {
        var bu = parseFloat(g.bushels) || 0;
        var hr = parseFloat(g.hours) || 0;
        var gph = bu > 0 && hr > 0 ? r2(bu / hr) : null;
        if (bu > 0 || hr > 0) {
          grading.push('  \u2022 ' + (g.name || '(unnamed)') + ' \u2014 ' + fb(bu) + ' bu' + (gph !== null ? ' \u2192 ' + gph + ' GPH' : ''));
          if (gph !== null) individualGPHs.push(gph);
        }
      });
      // Team GPH = average of individual GPHs
      var teamGPH = individualGPHs.length > 0 ? r2(individualGPHs.reduce(function(s,v){return s+v;},0) / individualGPHs.length) : null;
      if (totalGrBu > 0) grading.push('  Team: ' + fb(totalGrBu) + ' bu' + (teamGPH !== null ? ' \u2192 ' + teamGPH + ' GPH' : ''));
      // SPH if filled in
      var sorting = taktDay.sorting || {};
      var actualSPH = parseFloat(sorting.actualSPH) || 0;
      var actualBu = parseFloat(sorting.actualBushels) || 0;
      var sortHrs = parseFloat(sorting.actualHours) || 0;
      if (actualSPH > 0 || (actualBu > 0 && sortHrs > 0)) {
        var sphLine = '  Sorting: ' + fb(actualBu) + ' bu sorted';
        if (sortHrs > 0) sphLine += '  |  ' + sortHrs + ' hrs';
        if (actualSPH > 0) sphLine += '  |  ' + actualSPH + ' SPH';
        grading.push(sphLine);
      }
    } else {
      grading.push('  \u2022 No grader data logged');
    }

    // ── CRAB MEAT section ────────────────────────────────────────────────────
    var crabMeatLines = [];
    var cme = crabMeatEntries || [];
    CRAB_MEAT_PRODUCTS.forEach(function(product) {
      var productEntries = cme.filter(function(e) { return e.product === product; });
      var total = productEntries.reduce(function(s, e) { return s + (Number(e.lbs) || 0); }, 0);
      if (!total && productEntries.length === 0) {
        crabMeatLines.push('  ' + product + ' \u2014 0 lbs');
        return;
      }
      if (productEntries.length === 1) {
        var e = productEntries[0];
        var dl = e.date ? new Date(e.date + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' }) : '';
        crabMeatLines.push('  ' + product + ' \u2014 ' + Number(total).toFixed(2) + ' lbs' + (dl ? '  (' + dl + ')' : ''));
      } else {
        var dateParts = productEntries.map(function(e) {
          var dl = e.date ? new Date(e.date + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' }) : '?';
          return dl + ': ' + Number(e.lbs || 0).toFixed(2);
        }).join('  ');
        crabMeatLines.push('  ' + product + ' \u2014 ' + Number(total).toFixed(2) + ' lbs  (' + dateParts + ')');
      }
    });

    return { actNow: actNow, inv: inv, agingLines: agingLines, scheduledLines: scheduledLines, queue: queue, liveLines: liveLines, grading: grading, crabMeatLines: crabMeatLines };
  }


  var emailData = buildOpsEmailData(eodMode);

  // ── EMAIL ASSEMBLY ────────────────────────────────────────────────────────
  const SEP = '\u2550'.repeat(32);
  const sep = '\u2500'.repeat(32);

  const sections = [
    eodMode ? 'CrabPlace \u2014 Daily Hard Crab Ops Recap' : 'CrabPlace \u2014 Daily Hard Crab Ops Plan',
    dateLabel, SEP, '',
    '\uD83D\uDD34 ACT NOW', sep,
    ...emailData.actNow, '',
    '\uD83D\uDCE6 INVENTORY \u2014 AGING BY EXPIRY', sep,
    ...emailData.agingLines, '',
    '\uD83D\uDCC5 SCHEDULED TO GO OUT', sep,
    ...emailData.scheduledLines, '',
    '\uD83D\uDCC5 LAST WEEK SAME DAYS', sep,
    ...emailData.queue, '',
    '\uD83E\uDD80 LIVE / UNGRADED', sep,
    ...emailData.liveLines, '',
    '\uD83D\uDCCA GRADING PERFORMANCE', sep,
    ...emailData.grading, '',
    '\uD83E\uDED9 CRAB MEAT', sep,
    ...emailData.crabMeatLines, '',
    SEP,
    'Generated: ' + new Date().toLocaleString()
  ].filter(function(l){ return l !== null; }).join('\n');

  // ── Copy / mailto handlers ────────────────────────────────────────────────
  function doCopy() {
    navigator.clipboard.writeText(sections).then(function () {
      setCopied(true);
      setTimeout(function () {
        return setCopied(false);
      }, 2500);
    })["catch"](function () {
      const ta = document.createElement("textarea");
      ta.value = sections;
      ta.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(function () {
        return setCopied(false);
      }, 2500);
    });
  }
  function doMailto() {
    const subject = encodeURIComponent((eodMode ? "CrabPlace Daily Hard Crab Ops Recap" : "CrabPlace Daily Hard Crab Ops Plan") + " \u2014 " + dateLabel);
    // Copy full content to clipboard first, then open mail app with just subject
    // (mailto body has browser char limits — user pastes the clipboard content)
    navigator.clipboard.writeText(sections).catch(function() {});
    window.open("mailto:?subject=".concat(subject));
  }
  return <div style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.55)",
    zIndex: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16
  }} onClick={function onClick(e) {
    if (e.target === e.currentTarget) onClose();
  }}>{<div style={{
      background: "#fff",
      borderRadius: 14,
      width: "100%",
      maxWidth: 620,
      maxHeight: "92vh",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 20px 60px rgba(0,0,0,.3)",
      overflow: "hidden"
    }}>{<div style={{
        background: B.navy,
        padding: "14px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0
      }}>{<div>{<div style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 15
          }}>Daily Ops Recap</div>}{<div style={{
            color: "rgba(255,255,255,.5)",
            fontSize: 11,
            marginTop: 2
          }}>{dateLabel} · copy to email or clipboard</div>}</div>}{<div style={{display:"flex",borderRadius:7,overflow:"hidden",border:"1px solid rgba(255,255,255,.25)",fontSize:11,fontWeight:700,marginRight:6,flexShrink:0}}><button onClick={function(){setEodMode(false);}} style={{padding:"5px 10px",background:!eodMode?"rgba(255,255,255,.9)":"rgba(255,255,255,.1)",color:!eodMode?B.navy:"rgba(255,255,255,.6)",border:"none",cursor:"pointer",fontWeight:700}}>Daily Plan</button><button onClick={function(){setEodMode(true);}} style={{padding:"5px 10px",background:eodMode?"rgba(255,255,255,.9)":"rgba(255,255,255,.1)",color:eodMode?B.navy:"rgba(255,255,255,.6)",border:"none",cursor:"pointer",fontWeight:700}}>EOD Recap</button></div>}{<button onClick={onClose} style={{
          background: "rgba(255,255,255,.15)",
          border: "none",
          color: "#fff",
          borderRadius: 8,
          width: 34,
          height: 34,
          fontSize: 18,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0
        }}>✕</button>}</div>}{<div style={{
        flex: 1,
        overflowY: "auto",
        padding: "14px 18px"
      }}>{<pre style={{
          fontFamily: "'Courier New','Lucida Console',monospace",
          fontSize: 11.5,
          lineHeight: 1.75,
          color: B.textDark,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          background: "#fafaf8",
          borderRadius: 8,
          padding: "14px 16px",
          border: "1px solid ".concat(B.cardBorder),
          margin: 0
        }}>{sections}</pre>}</div>}{<div style={{
        padding: "12px 18px",
        borderTop: "1px solid ".concat(B.rowSep),
        display: "flex",
        gap: 10,
        flexShrink: 0,
        flexWrap: "wrap"
      }}>{<button onClick={doCopy} style={{
          flex: 1,
          minHeight: 46,
          background: copied ? "#166534" : B.navy,
          color: "#fff",
          border: "none",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          transition: "background .2s"
        }}>{copied ? "✓ Copied!" : "📋 Copy to Clipboard"}</button>}{<button onClick={doMailto} style={{
          flex: 1,
          minHeight: 46,
          background: "#fff",
          color: B.navy,
          border: "1.5px solid ".concat(B.navy),
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer"
        }}>✉ Open in Mail App (paste to fill)</button>}{<button onClick={onClose} style={{
          width: "100%",
          minHeight: 38,
          background: "transparent",
          color: B.textLight,
          border: "none",
          fontSize: 12,
          cursor: "pointer"
        }}>Close</button>}</div>}</div>}</div>;
}

// ─── DAILY OPS PLANNING TAB ───────────────────────────────────────────────────
// ─── DAILY OPS PLANNING TAB ───────────────────────────────────────────────────
// Decision engine — reads inventory ages and weekly demand to determine
// what needs to move, what is already committed, and what exposure remains.
// Generates recommendations automatically with plain-language explanations.
const DAILY_OPS_KEY = "hcp_v1_daily_ops";
function loadDailyOps() {
  try {
    const r = localStorage.getItem(DAILY_OPS_KEY);
    return r ? JSON.parse(r) : {};
  } catch (e) {
    return {};
  }
}
function saveDailyOps(d) {
  try {
    localStorage.setItem(DAILY_OPS_KEY, JSON.stringify(d));
  } catch (e) {}
}
function makeDailyOpsDay() {
  return {
    notes: "",
    overrides: {}
  };
}
// overrides shape: { [id]: {enabled:bool, volBu:string|null} }

// ── Operational upgrade chain ─────────────────────────────────────────────────
// upgrade: fill a *lower* grade order with *this* aging size (no customer contact)
// cr:      customer contact required — downgrade, substitution, or sex-change offer
const OPS_UPGRADE_CHAIN = {
  // expiring size → silent upgrade (give better product, no call) + offer-up (credit back)
  "Colossal": {
    upgradeFrom: ["Premium"],        // upgrade Premium orders to Colossal (one step only)
    crFrom: []                        // no offer-up — nothing above Colossal
  },
  "Premium": {
    upgradeFrom: ["Select"],         // upgrade Select orders to Premium
    crFrom: ["Colossal"]             // offer Premium to Colossal customers w/ credit
  },
  "Select": {
    upgradeFrom: ["Standard"],       // upgrade Standard orders to Select
    crFrom: ["Premium"]              // offer Select to Premium customers w/ credit
  },
  "Standard": {
    upgradeFrom: [],                  // no size below Standard to silently upgrade
    crFrom: ["Select"]               // offer Standard to Select customers w/ credit
  },
  "Large Female": {
    upgradeFrom: ["Regular Female"],  // upgrade Regular Female orders to Large Female
    crFrom: []                         // no offer-up above Large Female
  },
  "Regular Female": {
    upgradeFrom: [],                   // no size below Regular Female to silently upgrade
    crFrom: ["Large Female"]          // offer Regular Female to Large Female customers w/ credit
  }
};
const OOS_SUB_MAP = {
  "Colossal": {
    sub: "Premium",
    crNote: "offer Premium as substitute or downgrade"
  },
  "Premium": {
    sub: "Select",
    crNote: "offer Select as substitute or downgrade"
  },
  "Large Female": {
    sub: "Premium",
    crNote: "offer Premium as substitute"
  },
  "Regular Female": {
    sub: "Select",
    crNote: "offer Select as substitute"
  },
  "Standard": {
    sub: "Select",
    crNote: "offer Select as substitute"
  },
  "Select": {
    sub: "Premium",
    crNote: "offer Premium as substitute"
  }
};

// ── Confidence scoring ────────────────────────────────────────────────────────
// High   = day 9, exposure > 2 bu, no demand covering it
// Moderate = day 8, or day 9 with partial coverage
// Low    = day 7 only, or exposure < 1 bu
function scoreConfidence(urgency, exposure, committed, totalAging) {
  if (urgency === "CRITICAL" && exposure > 2) return "high";
  if (urgency === "CRITICAL") return "high";
  if (urgency === "URGENT" && exposure > 1) return "high";
  if (urgency === "URGENT" && committed > totalAging * 0.5) return "moderate";
  if (urgency === "URGENT") return "moderate";
  if (urgency === "OOS") return "high";
  if (exposure > 2) return "moderate";
  return "low";
}
const CONF_STYLE = {
  high: {
    label: "High",
    color: "#b91c1c",
    bg: "#fef2f2",
    border: "#fecaca"
  },
  moderate: {
    label: "Moderate",
    color: "#c2540a",
    bg: "#fff7ed",
    border: "#fed7aa"
  },
  low: {
    label: "Low",
    color: "#92400e",
    bg: "#fefce8",
    border: "#fde68a"
  }
};

// ── Core decision engine ──────────────────────────────────────────────────────
// Pure function: takes enriched lots + demand per size → returns structured decisions
function computeOpsDecisions(enrichedLots, demandBySize, todayActual, liveSupplyByGrade) {
  // Build per-size pressure (aging inventory)
  const pressure = {};
  SIZES.forEach(function ({
    size
  }) {
    pressure[size] = {
      d7: 0,
      d8: 0,
      d9: 0,
      totalAging: 0,
      lots: []
    };
  });
  enrichedLots.forEach(function (lot) {
    if (lot.status === "EXPIRED") return;
    const bu = parseFloat(lot.bushels) || 0;
    const p = pressure[lot.size];
    if (!p) return;
    if (lot.age === 7) p.d7 = r2(p.d7 + bu);
    if (lot.age === 8) p.d8 = r2(p.d8 + bu);
    if (lot.age === 9) p.d9 = r2(p.d9 + bu);
    if (lot.age >= 7) {
      p.totalAging = r2(p.totalAging + bu);
      p.lots.push(lot);
    }
  });

  // Committed outgoing = actual shipdate data for today if available, else weekly/7
  const committed = {};
  const exposure = {}; // how much still needs active help
  SIZES.forEach(function ({
    size
  }) {
    let _demandBySize$size3;
    const demand = (demandBySize === null || demandBySize === void 0 || (_demandBySize$size3 = demandBySize[size]) === null || _demandBySize$size3 === void 0 ? void 0 : _demandBySize$size3.trueDemand) || 0;
    // Use actual shipdate bushels for today if available, otherwise fall back to weekly/7
    const actualToday = todayActual && todayActual[size] || 0;
    const dailyOut = actualToday > 0 ? actualToday : r2(demand / 7);
    committed[size] = dailyOut;
    const aging = pressure[size].totalAging;
    // Exposure = how short we are for today's demand
    // If we have inventory: aging minus what flows out today
    // If we're OOS: full daily demand is exposed
    const usableInv = enrichedLots.filter(function(l) {
      return l.size === size && l.usable;
    }).reduce(function(s, l) { return s + (parseFloat(l.bushels) || 0); }, 0);
    if (usableInv === 0 && dailyOut > 0) {
      // OOS: dailyOut is already the per-day committed amount
      // Exposure = how much is going out today with no stock to fill it
      exposure[size] = dailyOut;
    } else {
      // Have inventory: exposure is aging lots not covered by today's outflow
      exposure[size] = r2(Math.max(0, aging - dailyOut));
    }
  });

  // Generate decisions
  const autoUpgrades = []; // no customer contact
  const crActions = []; // customer contact required
  const explanations = []; // per-size reasoning

  SIZES.forEach(function ({
    size
  }) {
    let _chain$upgradeFrom2, _chain$crFrom;
    const p = pressure[size];
    const exp = exposure[size];
    const com = committed[size];
    if (!p.totalAging) return;
    const urgency = p.d9 > 0 ? "CRITICAL" : p.d8 > 0 ? "URGENT" : "AGING";
    const urgLabel = p.d9 > 0 ? "🔴" : p.d8 > 0 ? "🟠" : "🟡";
    const dayDesc = [p.d9 > 0 ? "".concat(fb(p.d9), " bu on day 9") : null, p.d8 > 0 ? "".concat(fb(p.d8), " bu on day 8") : null, p.d7 > 0 ? "".concat(fb(p.d7), " bu on day 7") : null].filter(Boolean).join(", ");

    // Explanation for this size
    const expParts = ["We have ".concat(fb(p.totalAging), " bu of aging ").concat(size, " (").concat(dayDesc, ")."), com > 0 ? "~".concat(fb(com), " bu is already flowing out through today's orders.") : "No demand is currently forecasted for ".concat(size, "."), exp > 0 ? "".concat(fb(exp), " bu of ").concat(size, " still needs to move \u2014 action required.") : "The natural order flow should cover this inventory today."];
    explanations.push({
      size: size,
      urgency: urgency,
      urgLabel: urgLabel,
      totalAging: p.totalAging,
      committed: com,
      exposure: exp,
      text: expParts.join(" ")
    });
    if (exp <= 0) return; // natural flow will handle it — no action needed

    const chain = OPS_UPGRADE_CHAIN[size];

    // Auto-upgrades (no customer contact)
    if (chain !== null && chain !== void 0 && (_chain$upgradeFrom2 = chain.upgradeFrom) !== null && _chain$upgradeFrom2 !== void 0 && _chain$upgradeFrom2.length) {
      const fromList = chain.upgradeFrom.join(" and ");
      const conf = scoreConfidence(urgency, exp, com, p.totalAging);
      autoUpgrades.push({
        id: "up-".concat(size),
        urgency: urgency,
        urgLabel: urgLabel,
        size: size,
        confidence: conf,
        volBu: exp,
        // recommended volume — overridable
        action: "Move ".concat(fb(exp), " bu of ").concat(urgLabel, " ").concat(size),
        detail: "Send ".concat(size, " to fill ").concat(fromList, " orders (free upgrade for customer)."),
        explanation: "".concat(fb(exp), " bu of ").concat(size, " needs to move. Pack ").concat(size, " for ").concat(fromList, " orders — customer gets a bigger crab, you move aging inventory.")
      });
    }

    // CR actions (downgrades / subs)
    if (chain !== null && chain !== void 0 && (_chain$crFrom = chain.crFrom) !== null && _chain$crFrom !== void 0 && _chain$crFrom.length && exp > 0) {
      chain.crFrom.forEach(function (fromSize) {
        const conf = scoreConfidence(urgency, exp, com, p.totalAging);
        crActions.push({
          id: "cr-".concat(size, "-").concat(fromSize),
          urgency: urgency,
          urgLabel: urgLabel,
          size: size,
          confidence: conf,
          volBu: exp,
          action: "Sub ".concat(fromSize, " orders to ").concat(size, " (").concat(size, " OOS)"),
          detail: "Offer ".concat(fromSize, " customers a downgrade to ").concat(size, " with credit back or extra crabs."),
          explanation: "Still ".concat(fb(exp), " bu of ").concat(size, " at risk after upgrades. Offering ").concat(fromSize, " customers a downgrade to ").concat(size, " (with compensation) can help move this inventory.")
        });
      });
    }
  });

  // OOS / missing size substitutions — sizes with demand but no usable inventory
  SIZES.forEach(function ({
    size
  }) {
    let _demandBySize$size4;
    const forecastDemand = (demandBySize === null || demandBySize === void 0 || (_demandBySize$size4 = demandBySize[size]) === null || _demandBySize$size4 === void 0 ? void 0 : _demandBySize$size4.trueDemand) || 0;
    const actualToday = (todayActual && todayActual[size]) || 0;
    const demand = forecastDemand || actualToday;
    const usable = enrichedLots.filter(function (l) {
      return l.size === size && l.usable;
    }).reduce(function (s, l) {
      return s + (parseFloat(l.bushels) || 0);
    }, 0);
    if (usable > 0) return; // we have inventory, no OOS issue
    // With 0 inventory, flag OOS for any size that has demand OR that ships regularly.
    // When no crab count has been entered yet, all 6 sizes show 0 inventory
    // and all should be flagged so CR can handle every size.
    const sub = OOS_SUB_MAP[size];
    if (!sub) return;
    crActions.push({
      id: "oos-".concat(size),
      urgency: "OOS",
      urgLabel: "⚫",
      size: size,
      confidence: "high",
      volBu: r2(actualToday || demand / 7),
      action: "".concat(size, " \u2014 Out of Stock"),
      detail: "Send affected ".concat(size, " orders to CR to ").concat(sub.crNote, "."),
      explanation: "No usable inventory on hand for ".concat(size, ". CR must contact affected customers to offer alternatives.")
    });
  });
  // ── Inventory pressure — is a size HEAVY? ────────────────────────────────────
  // A size is HEAVY if:
  //   1. Average lot age >= 4 days (inventory is getting older)
  //   2. Usable inventory > recent Wed-Fri daily pace * days until expiry
  //      i.e. we cannot sell through it before deadline at current pace
  //
  // Upgrade ladder when HEAVY:
  //   Colossal heavy → pack Colossal on Premium orders
  //   Premium heavy  → pack Premium on Select orders
  //   Select heavy   → pack Select on Standard orders

  // Wed-Fri daily pace per size: use actual days data from parsed (passed via todayActual context)
  // Fall back to committed[size] (forecast/7) if no recent data
  const UPGRADE_INTO = {
    'Colossal': 'Premium',
    'Premium': 'Select',
    'Select': 'Standard'
  };

  const coverageRisk = [];

  SIZES.forEach(function({ size }) {
    const lotsForSize = enrichedLots.filter(function(l) { return l.size === size && l.usable; });
    if (!lotsForSize.length) return;

    const usable = lotsForSize.reduce(function(s, l) { return s + (parseFloat(l.bushels) || 0); }, 0);
    const avgAge = lotsForSize.reduce(function(s, l) { return s + (l.age || 0); }, 0) / lotsForSize.length;
    const oldestAge = lotsForSize.reduce(function(mx, l) { return Math.max(mx, l.age || 0); }, 0);
    const daysUntilExpiry = 9 - oldestAge;

    // Daily pace: use committed (actual today or forecast/7)
    const dailyPace = committed[size] || 0;
    if (dailyPace <= 0) return;

    // HEAVY: avg age >= 4 AND can't sell through before expiry at current pace
    const canSellThrough = dailyPace * daysUntilExpiry;
    const isHeavy = avgAge >= 4 && usable > canSellThrough;

    if (isHeavy) {
      const surplusBu = r2(usable - canSellThrough);
      const upgradeInto = UPGRADE_INTO[size];
      const urgency = daysUntilExpiry <= 3 ? 'URGENT' : 'AGING';
      coverageRisk.push({
        type: 'HEAVY',
        size,
        usable: r2(usable),
        surplusBu,
        avgAge: Math.round(avgAge * 10) / 10,
        oldestAge,
        daysUntilExpiry,
        dailyPace: r2(dailyPace),
        subInto: upgradeInto,
        urgency
      });

      // Add upgrade action if not already present from aging logic
      const alreadyExists = autoUpgrades.some(function(u) { return u.id === 'up-' + size || u.id === 'cov-up-' + size; });
      if (!alreadyExists && surplusBu > 0.25 && upgradeInto) {
        autoUpgrades.push({
          id: 'heavy-' + size,
          urgency: urgency,
          urgLabel: urgency === 'URGENT' ? '🟠' : '🟡',
          size: size,
          confidence: 'high',
          volBu: surplusBu,
          action: 'Pack ' + size + ' on ' + upgradeInto + ' orders',
          detail: 'Use ' + size + ' to fill ' + upgradeInto + ' orders — move older inventory before deadline.',
          explanation: 'Average lot age is ' + Math.round(avgAge) + ' days. At current pace you have ' + fb(surplusBu) + ' bu more than you can clear before expiry. Upgrading ' + upgradeInto + ' orders burns through it.'
        });
      }
    }
  });

  return {
    pressure: pressure,
    committed: committed,
    exposure: exposure,
    autoUpgrades: autoUpgrades,
    crActions: crActions,
    explanations: explanations,
    coverageRisk: coverageRisk
  };
}

// ─── OPS DECISION ENGINE ──────────────────────────────────────────────────────
// Single source of truth for Daily Ops decisions.
// Determines action type: UPGRADE (no approval) vs SUBSTITUTION (CR required).
// UPGRADE: customer gets same or better product (e.g. Colossal fills Premium orders).
// SUBSTITUTION: customer gets lower product — requires CR contact.

// ─── REMAINING SELL WINDOW ────────────────────────────────────────────────────
// Count remaining shipping days (Mon-Fri) from todayISO to cutoffISO (exclusive today, inclusive cutoff)
// and multiply by daily demand to get remaining sellable bushels.
// ─── GENERATE OPS ISSUES ─────────────────────────────────────────────────────
// Standalone function: evaluates enriched lots against remaining demand and
// returns a sorted array of actionable issues for Daily Ops.
// INPUT:  { enrichedLots, demandBySize, today }
function getRemainingSellWindow(todayISO, cutoffISO, dailyDemandBu) {
  var d = new Date(todayISO + "T12:00:00");
  var cutoff = new Date(cutoffISO + "T12:00:00");
  var shippingDays = 0;
  while (d < cutoff) {
    d.setDate(d.getDate() + 1);
    var dow = d.getDay();
    if (dow !== 0 && dow !== 6) shippingDays++;
  }
  return r2(shippingDays * dailyDemandBu);
}

// ─── LOT-LEVEL OPS EVALUATION ─────────────────────────────────────────────────
// Evaluates each usable lot individually:
//   cutoff = day 9, adjusted if day 9 falls on Sat/Sun → Friday
//   remainingSellable = shipping days from today to cutoff × daily demand
//   leftover = lot bushels - remainingSellable
// Only lots with leftover > 0 (or weekend-cutoff urgent) become issues.

// ─── SHARED EXPIRY RISK CALCULATOR ───────────────────────────────────────────
// Used by all four sections: email ACT NOW, Daily Ops Planning, Daily Snapshot,
// and evaluateLotsForOps. Returns a risk object per size with gap > 2 bu flagged.
//
// Logic:
//   1. Find all usable lots expiring within 3 days (last ship day <= today+3 shipping days)
//   2. Sum committed orders from parsedDays for today → cutoff window
//   3. Get last week same days pace for that size (with holiday fallback)
//   4. Gap = expiring bu - committed - last week pace
//   5. Flag if gap > 2 bu
function computeExpiryRisk(enrichedLots, parsedDays, today, eodMode) {
  var todayStr = today || todayISO();
  var todayDate = new Date(todayStr + 'T12:00:00');
  // In EOD mode, committed window starts tomorrow (today already shipped)
  var committedStart = new Date(todayDate);
  if (eodMode) committedStart.setDate(committedStart.getDate() + 1);

  // Find the cutoff window end: 3 shipping days from today
  var windowEnd = new Date(todayDate);
  var shippingDaysAdded = 0;
  var cur = new Date(todayDate);
  cur.setDate(cur.getDate() + 1);
  while (shippingDaysAdded < 3) {
    var dow = cur.getDay();
    if (dow >= 1 && dow <= 5) shippingDaysAdded++;
    if (shippingDaysAdded < 3) cur.setDate(cur.getDate() + 1);
  }
  var windowEndISO = cur.toISOString().slice(0, 10);

  // Find clean comparison week (holiday fallback, same as email)
  function getCompMonday() {
    var weeksBack = 1;
    while (weeksBack <= 6) {
      var cand = new Date(new Date(mondayOfWeek(todayStr) + 'T12:00:00'));
      cand.setDate(cand.getDate() - (7 * weeksBack));
      var candISO = cand.toISOString().slice(0, 10);
      if (!getHolidayForWeek(candISO)) return candISO;
      weeksBack++;
    }
    return null;
  }
  var compMonday = getCompMonday();

  // For each size, compute expiring bu within 3-day window
  var expiringBySize = {};
  var cutoffBySize = {};
  SIZES.forEach(function({ size }) { expiringBySize[size] = 0; });

  (enrichedLots || []).forEach(function(lot) {
    if (!lot.usable || !lot.steamDate) return;
    var bu = Number(lot.bushels) || 0;
    if (!bu) return;
    // Compute last ship day
    var day9 = _addDays(lot.steamDate, 9);
    var d9dow = _dow(day9);
    var lastShip = day9;
    if (d9dow === 6) lastShip = _addDays(day9, -1);
    if (d9dow === 0) lastShip = _addDays(day9, -2);
    if (lastShip < todayStr) return; // already expired
    if (lastShip > windowEndISO) return; // not expiring within 3 shipping days
    expiringBySize[lot.size] = r2(expiringBySize[lot.size] + bu);
    // Track earliest cutoff per size
    if (!cutoffBySize[lot.size] || lastShip < cutoffBySize[lot.size]) {
      cutoffBySize[lot.size] = lastShip;
    }
  });

  // Upgrade cascade chains: if expiring size has a gap, it will be filled by
  // upgrading orders of the next size down — reducing that size's effective committed.
  // Male chain: Colossal → Premium → Select → Standard
  // Female chain: Large Female → Regular Female
  var UPGRADE_CHAIN = {
    'Colossal': 'Premium',
    'Premium': 'Select',
    'Select': 'Standard',
    'Large Female': 'Regular Female'
  };

  // First pass: compute raw committed for all sizes
  var rawCommitted = {};
  SIZES.forEach(function({ size }) {
    rawCommitted[size] = 0;
    var cutoff = cutoffBySize[size] || windowEndISO;
    if (parsedDays) {
      var d = new Date(committedStart); // EOD: start tomorrow; Daily Plan: start today
      var endD = new Date(cutoff + 'T12:00:00');
      while (d <= endD) {
        var dow = d.getDay();
        if (dow >= 1 && dow <= 5) {
          var iso = d.toISOString().slice(0, 10);
          rawCommitted[size] = r2(rawCommitted[size] + (Number((parsedDays[iso] && parsedDays[iso][size]) || 0)));
        }
        d.setDate(d.getDate() + 1);
      }
    }
  });

  // Second pass: cascade gaps down the upgrade chain, adjusting effective committed
  var effectiveCommitted = {};
  SIZES.forEach(function({ size }) { effectiveCommitted[size] = rawCommitted[size]; });

  // Process in chain order so Colossal affects Premium before Premium is evaluated
  var chainOrder = ['Colossal', 'Premium', 'Select', 'Standard', 'Large Female', 'Regular Female'];
  chainOrder.forEach(function(size) {
    var expiring = expiringBySize[size] || 0;
    if (expiring <= 0) return;
    var gap = r2(Math.max(0, expiring - effectiveCommitted[size]));
    // If there's a gap, those orders are being filled by upgrading the next size down
    // Reduce the next size's effective committed by the gap amount
    var nextSize = UPGRADE_CHAIN[size];
    if (nextSize && gap > 0) {
      effectiveCommitted[nextSize] = r2(Math.max(0, effectiveCommitted[nextSize] - gap));
    }
  });

  // Third pass: compute last week pace and build risks using effective committed
  var risks = {};
  SIZES.forEach(function({ size }) {
    var expiring = expiringBySize[size];
    if (expiring <= 0) return;

    var cutoff = cutoffBySize[size];
    var committed = effectiveCommitted[size];

    // Last week same days pace for this size (context only)
    var lastWeekPace = 0;
    if (compMonday && parsedDays) {
      var d2 = new Date(todayDate);
      var endD2 = new Date(cutoff + 'T12:00:00');
      while (d2 <= endD2) {
        var dow2 = d2.getDay();
        if (dow2 >= 1 && dow2 <= 5) {
          var compDate = new Date(compMonday + 'T12:00:00');
          compDate.setDate(compDate.getDate() + (dow2 - 1));
          var compISO = compDate.toISOString().slice(0, 10);
          lastWeekPace = r2(lastWeekPace + (Number((parsedDays[compISO] && parsedDays[compISO][size]) || 0)));
        }
        d2.setDate(d2.getDate() + 1);
      }
    }

    var gap = r2(Math.max(0, expiring - committed));
    var threshold = (size === 'Regular Female' || size === 'Standard') ? 0 : 2;
    if (gap > threshold) {
      risks[size] = {
        size: size,
        expiring: expiring,
        committed: committed,
        lastWeekPace: lastWeekPace,
        gap: gap,
        cutoff: cutoff,
        cutoffLabel: cutoff ? new Date(cutoff + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' }) : '?'
      };
    }
  });

  return risks;
}

function evaluateLotsForOps({ lots, forecastBySize, today, committedBySize, parsedDays }) {
  var todayStr = today || todayISO();

  // Use computeExpiryRisk as the single gate — only sizes with gap > 2 bu show up
  var risks = computeExpiryRisk(lots, parsedDays, todayStr);

  var issues = [];
  Object.values(risks).forEach(function(risk) {
    var size = risk.size;
    // Find the oldest lot for this size to use as the representative steam date
    var oldestLot = null;
    (lots || []).forEach(function(lot) {
      if (!lot.usable || lot.size !== size) return;
      if (!oldestLot || lot.steamDate < oldestLot.steamDate) oldestLot = lot;
    });
    if (!oldestLot) return;

    var dateLabel = new Date(oldestLot.steamDate + 'T12:00:00').toLocaleDateString(
      [], { month: 'numeric', day: 'numeric' }
    );
    var daysToFinalCutoff = Math.round(
      (new Date(risk.cutoff + 'T12:00:00') - new Date(todayStr + 'T12:00:00')) / 86400000
    );
    var isOperationallyUrgent = oldestLot.isOperationallyUrgent === true;
    var urgency = daysToFinalCutoff <= 1 ? 'CRITICAL' : daysToFinalCutoff <= 2 ? 'URGENT' : 'AGING';

    issues.push({
      size: size,
      steamDate: oldestLot.steamDate,
      dateLabel: dateLabel,
      bushels: risk.gap,
      expiring: risk.expiring,
      committed: risk.committed,
      lastWeekPace: risk.lastWeekPace,
      leftover: risk.gap,
      isWeekendCutoff: oldestLot.weekendCutoffUrgent === true,
      isOperationallyUrgent: isOperationallyUrgent,
      urgency: urgency,
      cutoff: risk.cutoff,
      cutoffLabel: risk.cutoffLabel,
      daysToFinalCutoff: daysToFinalCutoff
    });
  });

  issues.sort(function(a, b) {
    var urgOrder = { CRITICAL: 0, URGENT: 1, AGING: 2 };
    var uDiff = (urgOrder[a.urgency] || 9) - (urgOrder[b.urgency] || 9);
    if (uDiff !== 0) return uDiff;
    return b.leftover - a.leftover;
  });

  return issues;
}

// Builds ops actions. Urgent issues get strong act-now language; non-urgent get monitor language.
function buildOpsActions(issues) {
  return issues.map(function(i) {
    var action = '';
    var actionType = 'UPGRADE';
    var urgent = i.isOperationallyUrgent;

    // Tier 1: Colossal, Premium, Select, Large Female
    // Tier 2: Standard, Regular Female
    if (i.size === 'Colossal') {
      action = 'Upgrade Premium orders to Colossal';
    } else if (i.size === 'Premium') {
      action = 'Upgrade Select orders to Premium';
    } else if (i.size === 'Select') {
      action = 'Upgrade Standard orders to Select';
    } else if (i.size === 'Large Female') {
      action = 'Upgrade Regular Female orders to Large Female';
    } else if (i.size === 'Regular Female') {
      action = 'Offer Regular Female to Large Female customers w/ credit or extras';
      actionType = 'CR';
    } else if (i.size === 'Standard') {
      action = 'Offer Standard to Select customers w/ credit or extras';
      actionType = 'CR';
    } else {
      action = 'CR outreach to move volume';
      actionType = 'CR';
    }

    if (i.isWeekendCutoff) {
      action = '\uD83D\uDEA8 FRIDAY CUTOFF \u2014 ' + action;
    }

    return Object.assign({}, i, {
      action: action,
      actionType: actionType,
      onHand: i.bushels,
      projectedLeftover: i.leftover,
      remainingWeekDemand: i.remainingSellable,
      drivenBy: [
        i.dateLabel + ' lots: ' + fb(i.bushels) + ' bu on hand'
        + ', ' + fb(i.remainingSellable) + ' bu projected to sell before cutoff'
        + (i.isWeekendCutoff ? ' \u2014 expires Sunday, Friday is last ship day' : '')
      ]
    });
  });
}



function computeOpsPlan({ inventoryBySize, demandPicture, weekCompletion, lotAgeDetail, enrichedLots, today }) {
  const PRIORITY = [
    'Colossal', 'Premium', 'Select', 'Standard', 'Large Female', 'Regular Female'
  ];
  const FEMALE = new Set(['Large Female', 'Regular Female']);
  const todayStr = today || todayISO();

  // dowPct from weekCompletion tells us what fraction of weekly demand falls on each day.
  // We use this to calculate remaining sellable demand between today and any cutoff date.
  const dowPct = (weekCompletion && weekCompletion.dowPct) || { 1:0.2, 2:0.2, 3:0.2, 4:0.2, 5:0.2 };
  const remainingPct = (weekCompletion && weekCompletion.remainingPct) || 1.0;

  // Count remaining shipping days (Mon-Fri) between today (exclusive) and cutoffISO (inclusive)
  function shippingDaysBetween(fromISO, toISO) {
    var days = 0;
    var d = new Date(fromISO + 'T12:00:00');
    var end = new Date(toISO + 'T12:00:00');
    while (d < end) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) days++;
    }
    return days;
  }

  // Fraction of weekly demand that falls in remaining shipping days to cutoff
  function demandFractionToDate(fromISO, toISO) {
    var frac = 0;
    var d = new Date(fromISO + 'T12:00:00');
    var end = new Date(toISO + 'T12:00:00');
    while (d < end) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow >= 1 && dow <= 5) {
        frac += dowPct[dow] || (remainingPct / 5);
      }
    }
    return Math.min(frac, 1);
  }

  // For a lot, determine its effective cutoff date (last viable ship day)
  function lotCutoffDate(steamDate) {
    var day9 = _addDays(steamDate, 9);
    var day9Dow = _dow(day9);
    if (day9Dow === 6) return _addDays(day9, -1); // Sat -> Fri
    if (day9Dow === 0) return _addDays(day9, -2); // Sun -> Fri
    return day9;
  }

  // Group enriched lots by size, filter to usable only, analyze each steam-date group
  var issuesBySize = {};

  if (enrichedLots && enrichedLots.length) {
    // Group usable lots by size+steamDate
    var lotGroups = {};
    enrichedLots.forEach(function(lot) {
      if (!lot.usable) return;
      var age = lot.age || 0;
      if (age < 6 && !lot.weekendCutoffUrgent) return; // only look at aging/at-risk lots
      var key = lot.size + '|' + (lot.steamDate || '');
      if (!lotGroups[key]) lotGroups[key] = { size: lot.size, steamDate: lot.steamDate || '', bu: 0, lots: [] };
      lotGroups[key].bu = r2(lotGroups[key].bu + (Number(lot.bushels) || 0));
      lotGroups[key].lots.push(lot);
    });

    // For each group, calculate: cutoff, remaining sellable demand, leftover
    Object.values(lotGroups).forEach(function(group) {
      var size = group.size;
      var steamDate = group.steamDate;
      var bu = group.bu;
      if (bu <= 0) return;

      var weekDemand = (demandPicture && demandPicture.bySize &&
        demandPicture.bySize[size] && demandPicture.bySize[size].trueDemand) || 0;

      var cutoff = steamDate ? lotCutoffDate(steamDate) : null;
      var isWeekendCutoff = steamDate ? (function() {
        var day9 = _addDays(steamDate, 9);
        var d = _dow(day9);
        return d === 0 || d === 6;
      })() : false;

      // Fraction of weekly demand that can sell between today and cutoff
      var sellFrac = cutoff ? demandFractionToDate(todayStr, cutoff) : remainingPct;
      var projectedSell = r2(weekDemand * sellFrac);
      var projectedLeftover = r2(Math.max(0, bu - projectedSell));

      // Determine if this group is actionable
      var threshold = FEMALE.has(size) ? 0.5 : 1.5;
      var lot0 = group.lots[0] || {};
      var age0 = lot0.age || 0;
      var weekendUrgent = group.lots.some(function(l) { return l.weekendCutoffUrgent; });
      var forceFlag = weekendUrgent;
      if (projectedLeftover < threshold && !forceFlag) return;

      // Urgency based on worst lot in group
      var maxAge = group.lots.reduce(function(m, l) { return Math.max(m, l.age || 0); }, 0);
      var urgency = (maxAge >= 9 || (isWeekendCutoff && maxAge < 8)) ? 'CRITICAL'
        : maxAge >= 8 ? 'URGENT' : 'AGING';

      // If a lot's day 9 is tomorrow (Saturday), it's critical today (last 2-day window)
      if (isWeekendCutoff && cutoff && cutoff === todayStr) urgency = 'CRITICAL';

      if (!issuesBySize[size]) {
        issuesBySize[size] = {
          size: size,
          urgency: urgency,
          atRiskBu: 0,
          projectedSell: 0,
          projectedLeftover: 0,
          onHand: (inventoryBySize && inventoryBySize[size] && inventoryBySize[size].usable) || 0,
          isWeekendCutoff: isWeekendCutoff,
          lotGroups: [],
          maxAge: 0
        };
      }
      var iss = issuesBySize[size];
      iss.atRiskBu = r2(iss.atRiskBu + bu);
      iss.projectedSell = r2(iss.projectedSell + projectedSell);
      iss.projectedLeftover = r2(iss.projectedLeftover + projectedLeftover);
      iss.maxAge = Math.max(iss.maxAge, maxAge);
      if (isWeekendCutoff) iss.isWeekendCutoff = true;
      // Escalate urgency
      var urgOrd = { CRITICAL: 0, URGENT: 1, AGING: 2 };
      if ((urgOrd[urgency]||9) < (urgOrd[iss.urgency]||9)) iss.urgency = urgency;
      // Build lot label
      var dateLabel = steamDate ? new Date(steamDate + 'T12:00:00').toLocaleDateString([], { month: 'numeric', day: 'numeric' }) : '?';
      iss.lotGroups.push({
        steamDate: steamDate,
        dateLabel: dateLabel,
        bu: bu,
        projectedSell: projectedSell,
        projectedLeftover: projectedLeftover,
        cutoff: cutoff,
        isWeekendCutoff: isWeekendCutoff,
        weekendUrgent: weekendUrgent,
        urgency: urgency
      });
    });
  } else {
    // Fallback: use inventoryBySize + remainingPct if no enriched lots passed
    PRIORITY.forEach(function(size) {
      var inv = (inventoryBySize && inventoryBySize[size]) || {};
      var db = inv.dayBuckets || {};
      var weekendCutoffBu = inv.weekendCutoff || 0;
      var agingBu = r2((db[6]||0)+(db[7]||0)+(db[8]||0)+(db[9]||0));
      var atRiskBu = r2(Math.max(agingBu, weekendCutoffBu));
      if (atRiskBu <= 0) return;
      var weekDemand = (demandPicture && demandPicture.bySize &&
        demandPicture.bySize[size] && demandPicture.bySize[size].trueDemand) || 0;
      var projectedSell = r2(weekDemand * remainingPct);
      var projectedLeftover = r2(Math.max(0, atRiskBu - projectedSell));
      var threshold = FEMALE.has(size) ? 0.5 : 1.5;
      if (projectedLeftover < threshold && weekendCutoffBu === 0) return;
      var hasDay9 = (db[9]||0) > 0;
      var hasDay8 = (db[8]||0) > 0;
      var isWeekend = weekendCutoffBu > 0;
      var urgency = (hasDay9 || (isWeekend && !hasDay8)) ? 'CRITICAL' : hasDay8 ? 'URGENT' : 'AGING';
      issuesBySize[size] = {
        size: size, urgency: urgency, atRiskBu: atRiskBu,
        projectedSell: projectedSell, projectedLeftover: projectedLeftover,
        onHand: inv.usable || 0, isWeekendCutoff: isWeekend,
        lotGroups: [], maxAge: hasDay9 ? 9 : hasDay8 ? 8 : 7
      };
    });
  }

  // Build final issues array
  var issues = Object.values(issuesBySize).filter(function(iss) { return iss.projectedLeftover > 0 || iss.isWeekendCutoff; });
  var urgOrdFinal = { CRITICAL: 0, URGENT: 1, AGING: 2 };
  issues.sort(function(a, b) { return (urgOrdFinal[a.urgency]||9) - (urgOrdFinal[b.urgency]||9); });

  // Attach actions to each issue
  issues.forEach(function(iss) {
    if (iss.size === 'Colossal') {
      iss.action = 'Upgrade Premium orders to Colossal'; iss.actionType = 'UPGRADE';
    } else if (iss.size === 'Premium') {
      iss.action = 'Upgrade Select orders to Premium; offer Premium to Colossal customers w/ credit'; iss.actionType = 'UPGRADE';
    } else if (iss.size === 'Select') {
      iss.action = 'Upgrade Standard orders to Select; offer Select to Premium customers w/ credit'; iss.actionType = 'UPGRADE';
    } else if (iss.size === 'Large Female') {
      iss.action = 'Upgrade Regular Female orders to Large Female (no call needed)'; iss.actionType = 'UPGRADE';
    } else if (iss.size === 'Regular Female') {
      iss.action = 'Offer Regular Female to Large Female customers w/ credit or extras';
      iss.actionType = 'CR';
    } else if (iss.size === 'Standard') {
      iss.action = 'Offer Standard to Select customers w/ credit or extras';
      iss.actionType = 'CR';
    } else {
      iss.action = 'CR outreach to move volume'; iss.actionType = 'CR';
    }
    // Build drivenBy from lot groups
    iss.drivenBy = [];
    if (iss.isWeekendCutoff) {
      var wcGroups = (iss.lotGroups || []).filter(function(g) { return g.weekendUrgent; });
      wcGroups.forEach(function(g) {
        iss.drivenBy.push(g.dateLabel + ' lots: ' + fb(g.bu) + ' bu \u2014 last 2-day window today (expires Sunday)');
      });
    }
    (iss.lotGroups || []).filter(function(g) { return !g.weekendUrgent || iss.drivenBy.length === 0; }).forEach(function(g) {
      if (g.projectedLeftover > 0) {
        iss.drivenBy.push(g.dateLabel + ' lots: ' + fb(g.bu) + ' bu on hand, ' + fb(g.projectedSell) + ' bu projected to sell, ' + fb(g.projectedLeftover) + ' bu leftover');
      }
    });
  });

  var selectedIssue = issues[0] || null;
  var selectedSize = selectedIssue ? selectedIssue.size : null;
  var atRiskBushels = selectedIssue ? selectedIssue.projectedLeftover : 0;

  var selectedContext = selectedIssue ? {
    onHand: selectedIssue.onHand,
    projectedToSell: selectedIssue.projectedSell,
    atRisk: selectedIssue.projectedLeftover
  } : null;

  var upgradeActions = issues.map(function(i) { return i.action; });

  var summary = selectedIssue
    ? (selectedIssue.isWeekendCutoff && selectedIssue.maxAge < 8
        ? 'Weekend cutoff alert: ' + selectedSize + ' lots expire Sunday \u2014 last 2-day shipping window is today.'
        : 'Heavy on older ' + selectedSize + '. ' + (selectedIssue.drivenBy[0] || '') + ' Risk of not clearing before deadline.')
    : 'Inventory balanced \u2014 no immediate action needed.';

  var projectedClearanceBySize = {};
  var inventoryAtRiskBySize = {};
  var projectedDemandBySize = {};
  PRIORITY.forEach(function(size) {
    var iss = issuesBySize[size];
    inventoryAtRiskBySize[size] = iss ? iss.projectedLeftover : 0;
    projectedDemandBySize[size] = iss ? iss.projectedSell : 0;
    projectedClearanceBySize[size] = iss && iss.onHand > 0
      ? r2(iss.projectedSell / iss.onHand) : 1;
  });

  return {
    selectedSize: selectedSize,
    atRiskBushels: atRiskBushels,
    selectedContext: selectedContext,
    issues: issues,
    projectedClearanceBySize: projectedClearanceBySize,
    inventoryAtRiskBySize: inventoryAtRiskBySize,
    projectedDemandBySize: projectedDemandBySize,
    upgradeActions: upgradeActions,
    summary: summary,
    heavySizes: issues.map(function(i) { return { size: i.size, surplusBu: i.projectedLeftover }; }),
    actions: issues.map(function(i) { return { label: i.action, surplusBu: i.projectedLeftover }; }),
    notes: issues.length ? ['Move oldest inventory first', 'Recheck inventory tomorrow morning'] : []
  };
}



function DailyOpsPlanningTab({
  enrichedLots,
  planDate,
  demandBySize,
  todayActual,
  parsedDays,
  allDays,
  liveCrabEntries,
  liveSupplyByGrade,
  inventoryBySize,
  demandPicture,
  weekCompletion,
  forecastBySize
}) {
  const today = planDate || todayISO();
  const dateLabel = new Date(today + "T12:00:00").toLocaleDateString([], {
    weekday: "long",
    month: "short",
    day: "numeric"
  });
  const _useState59 = useState(function () {
      return loadDailyOps();
    }),
    _useState60 = _useState59,
    allOps = _useState60[0],
    setAllOps = _useState60[1];
  const _useState61 = useState(false),
    _useState62 = _useState61,
    showExpl = _useState62[0],
    setShowExpl = _useState62[1];
  useEffect(function () {
    saveDailyOps(allOps);
  }, [allOps]);
  const ops = allOps[today] || makeDailyOpsDay();
  const overrides = ops.overrides || {};
  function setNotes(v) {
    setAllOps(function (all) {
      return {
        ...all,
        [today]: {
          ...(all[today] || makeDailyOpsDay()),
          notes: v
        }
      };
    });
  }
  function setOverride(id, patch) {
    setAllOps(function (all) {
      let _cur$overrides;
      const cur = all[today] || makeDailyOpsDay();
      return {
        ...all,
        [today]: {
          ...cur,
          overrides: {
            ...cur.overrides,
            [id]: {
              ...(((_cur$overrides = cur.overrides) === null || _cur$overrides === void 0 ? void 0 : _cur$overrides[id]) || {}),
              ...patch
            }
          }
        }
      };
    });
  }
  function isEnabled(id) {
    let _overrides$id;
    return ((_overrides$id = overrides[id]) === null || _overrides$id === void 0 ? void 0 : _overrides$id.enabled) !== false; // default = on
  }
  function getVol(item) {
    let _overrides$item$id;
    const ov = (_overrides$item$id = overrides[item.id]) === null || _overrides$item$id === void 0 ? void 0 : _overrides$item$id.volBu;
    return ov != null && ov !== "" ? parseFloat(ov) || 0 : item.volBu;
  }

  // Inventory Pressure + Committed Outgoing cards still use pressure/committed/exposure
  const decisions = useMemo(function () {
    return computeOpsDecisions(enrichedLots, demandBySize, todayActual, liveSupplyByGrade);
  }, [enrichedLots, demandBySize, todayActual, liveSupplyByGrade]);
  const pressure = decisions.pressure,
    committed = decisions.committed,
    exposure = decisions.exposure;

  // liveByGrade for the Committed Outgoing + live gate
  const liveByGrade = useMemo(function () {
    return summarizeLiveRemaining(liveCrabEntries);
  }, [liveCrabEntries]);

  // agingSizes: Inventory Pressure card only
  const agingSizes = SIZES.filter(function ({ size }) {
    let _p;
    return ((_p = pressure[size]) === null || _p === void 0 ? void 0 : _p.totalAging) > 0;
  });

  // ── Single decision engine — ALL ops decisions come from here ─────────────
  // Lot-level ops plan: evaluateLotsForOps + buildOpsActions.
  // Uses per-lot steamDate, cutoff, remaining shipping days to cutoff.
  const opsPlanIssues = useMemo(function() {
    if (!enrichedLots || !enrichedLots.length) return [];
    var fc = forecastBySize || {};
    if (!Object.values(fc).some(function(v){ return v > 0; })) {
      fc = {};
      SIZES.forEach(function({ size }) {
        fc[size] = (demandPicture && demandPicture.bySize && demandPicture.bySize[size] && demandPicture.bySize[size].trueDemand) || 0;
      });
    }
    var raw = evaluateLotsForOps({ lots: enrichedLots, forecastBySize: fc, today: planDate || todayISO(), committedBySize: committed, parsedDays: parsedDays });
    return buildOpsActions(raw);
  }, [enrichedLots, forecastBySize, demandPicture, planDate]);
  // Shortage alerts: sizes where committed today > usable, not covered by live/ungraded
  const shortageAlerts = useMemo(function() {
    var todayOrders = todayActual || {};
    var alerts = [];
    SIZES.forEach(function({ size }) {
      var committedToday = Number(todayOrders[size]) || 0;
      if (committedToday <= 0) return;
      var usableForSize = enrichedLots.filter(function(l) {
        return l.size === size && l.usable;
      }).reduce(function(s, l) { return s + (parseFloat(l.bushels) || 0); }, 0);
      if (usableForSize >= committedToday) return; // covered

      var shortfall = r2(committedToday - usableForSize);
      var isOos = usableForSize === 0;

      // Check if live/ungraded can cover this size
      // liveByGrade: 1s covers Colossal/Premium, 2s covers Select/Standard, 3s covers LF/RF
      var gradeForSize = null;
      if (size === 'Colossal' || size === 'Premium') gradeForSize = '1s';
      else if (size === 'Select' || size === 'Standard') gradeForSize = '2s';
      else if (size === 'Large Female' || size === 'Regular Female') gradeForSize = '3s';

      var liveGrade = gradeForSize && liveByGrade[gradeForSize];
      var liveBu = liveGrade ? (liveGrade.liveBu || 0) : 0;
      var liveBoxes = liveGrade ? (liveGrade.boxes || 0) : 0;

      if (liveBu >= shortfall && liveBoxes > 0) {
        // Live/ungraded can cover it
        alerts.push({
          size: size,
          committedToday: committedToday,
          usable: usableForSize,
          shortfall: shortfall,
          isOos: isOos,
          coveredByLive: true,
          liveBoxes: liveBoxes,
          gradeForSize: gradeForSize,
          action: 'Grade ' + gradeForSize + ' first \u2014 covers ' + size + ' (' + liveBoxes + ' boxes available)'
        });
      } else {
        // Not covered internally
        alerts.push({
          size: size,
          committedToday: committedToday,
          usable: usableForSize,
          shortfall: shortfall,
          isOos: isOos,
          coveredByLive: false,
          action: (isOos ? '\u26A0 ' + size + ' OUT \u2014 ' : '\u26A0 ' + size + ' short ' + fb(shortfall) + ' bu \u2014 ') + 'needs attention'
        });
      }
    });
    return alerts;
  }, [todayActual, enrichedLots, liveByGrade]);



  const opsPlan = useMemo(function() {
    var issues = opsPlanIssues;
    return {
      issues: issues,
      selectedSize: issues.length ? issues[0].size : null,
      upgradeActions: issues.map(function(i) { return i.action; }),
      summary: issues.length === 0
        ? "Inventory balanced \u2014 no immediate action needed."
        : issues.length === 1
          ? (issues[0].isWeekendCutoff
              ? "Weekend cutoff alert: " + issues[0].size + " lots expire Sunday \u2014 last 2-day window is today."
              : "Heavy on older " + issues[0].size + " (" + issues[0].dateLabel + " lots). Risk of not clearing before deadline.")
          : "Multiple active issues: " + issues.map(function(i){ return i.size; }).join(" and ") + " need action before cutoff.",
      atRiskBushels: issues.length ? issues[0].leftover : 0,
      selectedContext: issues.length ? { onHand: issues[0].bushels, projectedToSell: issues[0].remainingSellable, atRisk: issues[0].leftover } : null,
      heavySizes: issues.map(function(i) { return { size: i.size, surplusBu: i.leftover }; }),
      actions: issues.map(function(i) { return { label: i.action, surplusBu: i.leftover }; }),
      inventoryAtRiskBySize: Object.fromEntries(SIZES.map(function({ size }) {
        return [size, issues.filter(function(i){ return i.size === size; }).reduce(function(m,i){ return m + i.leftover; }, 0)];
      }))
    };
  }, [opsPlanIssues]);


  return <div>{<div style={{
      background: B.navy,
      borderRadius: 12,
      padding: "14px 16px",
      marginBottom: 14
    }}>{<div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: (opsPlan.issues && opsPlan.issues.length > 0) ? 12 : 0
      }}>{<div>{<div style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 15
          }}>Today's Ops Plan</div>}{<div style={{
            color: "rgba(255,255,255,.45)",
            fontSize: 11,
            marginTop: 1
          }}>{dateLabel + " · auto-generated from inventory & demand"}</div>}</div>}{opsPlan.issues && opsPlan.issues.length > 0 && <div style={{
          background: "#fff7ed",
          border: "1.5px solid #fed7aa",
          borderRadius: 7,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 700,
          color: "#c2540a"
        }}>{opsPlan.issues.length === 1
          ? ("Action needed: " + opsPlan.issues[0].size)
          : opsPlan.issues.length <= 3
            ? (opsPlan.issues.map(function(i){ return i.size; }).join(" & ") + " need action")
            : (opsPlan.issues.length + " active issues")}</div>}</div>}{(opsPlan.issues && opsPlan.issues.length > 0) || shortageAlerts.length > 0 ? <div style={{
        background: "rgba(255,255,255,.08)",
        borderRadius: 9,
        padding: "12px 14px"
      }}>{opsPlan.issues.map(function(issue, iss_i) {  var isUrgent = issue.isOperationallyUrgent;  var urgColor = isUrgent ? "#fca5a5" : issue.urgency === "URGENT" ? "#fdba74" : "#fde68a";  var rowBg = isUrgent ? "rgba(239,68,68,.08)" : "rgba(255,255,255,.04)";  var notLast = iss_i < opsPlan.issues.length - 1;  return <div key={issue.size + "_" + (issue.steamDate||"")} style={{ marginBottom: notLast ? 10 : 0, paddingBottom: notLast ? 10 : 0, borderBottom: notLast ? "1px solid rgba(255,255,255,.1)" : "none", background: rowBg, borderRadius: 7, padding: "10px 12px" }}>    <div style={{ marginBottom: 5 }}>      <span style={{ fontSize: 10, fontWeight: 700, color: urgColor, background: "rgba(255,255,255,.1)", borderRadius: 4, padding: "1px 7px", letterSpacing: 0.5, marginRight: 7 }}>        {isUrgent ? "\uD83D\uDD34 ACT NOW" : issue.urgency}      </span>      <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>        {(issue.dateLabel ? issue.dateLabel + " " : "") + issue.size + " \u2014 " + fb(issue.leftover || 0) + " bu at risk"}      </span>      {issue.isWeekendCutoff && <span style={{ color: "#fca5a5", fontSize: 11, marginLeft: 7 }}>{"Friday cutoff"}</span>}    </div>    <div style={{ color: "rgba(255,255,255,.6)", fontSize: 12, lineHeight: 1.8, paddingLeft: 2 }}>      <div>{"Projected to sell before cutoff: " + fb(issue.remainingSellable || 0) + " bu"}</div>      <div>{"Projected left at cutoff: " + fb(issue.leftover || 0) + " bu"}</div>    </div>    <div style={{ color: isUrgent ? "#fff" : "rgba(255,255,255,.75)", fontSize: 13, marginTop: 6, fontWeight: isUrgent ? 700 : 400 }}>{"\u2192 " + issue.action}</div>  </div>;})}{shortageAlerts.length > 0 && <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,.12)" }}>  {shortageAlerts.map(function(alert, ai) {    var isCovered = alert.coveredByLive;    var bg = isCovered ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)";    var border = isCovered ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)";    var icon = isCovered ? "\uD83D\uDFE2" : (alert.isOos ? "\u26AB" : "\uD83D\uDD34");    return <div key={alert.size} style={{ background: bg, border: "1px solid " + border, borderRadius: 7, padding: "8px 12px", marginBottom: ai < shortageAlerts.length - 1 ? 6 : 0 }}>      <div style={{ color: "#fff", fontWeight: 700, fontSize: 12, marginBottom: 3 }}>{icon + " " + alert.size + " \u2014 " + (alert.isOos ? "OUT" : fb(alert.shortfall) + " bu short")}</div>      <div style={{ color: "rgba(255,255,255,.8)", fontSize: 12 }}>{alert.action}</div>    </div>;  })}</div>}<div style={{ borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: 8, marginTop: 10 }}>  <div style={{ color: "rgba(255,255,255,.5)", fontSize: 12 }}>{"\u2022 Recheck inventory tomorrow morning"}</div></div></div> : <div style={{
        background: "rgba(255,255,255,.06)",
        borderRadius: 9,
        padding: "10px 14px",
        color: "rgba(255,255,255,.5)",
        fontSize: 12,
        textAlign: "center"
      }}>{"\u2713 Inventory balanced \u2014 no immediate action needed"}</div>}</div>}}{<Card style={{
      marginBottom: 14
    }}>{<div style={{
        background: "#1e3a5f",
        padding: "9px 16px",
        borderRadius: "10px 10px 0 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>{<div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 13
        }}>Inventory Pressure</div>}{<div style={{
          color: "rgba(255,255,255,.45)",
          fontSize: 11
        }}>Day 7–9 aging lots</div>}</div>}{<div style={{
        padding: "12px 14px"
      }}>{agingSizes.length === 0 ? <div style={{
          color: B.textLight,
          fontSize: 13,
          textAlign: "center",
          padding: "10px 0"
        }}>✓ No aging pressure — all inventory is fresh or aging normally.</div> : <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
          gap: 9
        }}>{agingSizes.map(function ({
            size
          }) {
            const p = pressure[size];
            const sc = SIZE_COLORS[size];
            const com = committed[size];
            const exp = exposure[size];
            const urg = p.d9 > 0 ? "🔴" : p.d8 > 0 ? "🟠" : "🟡";
            return <div key={size} style={{
              borderLeft: "4px solid ".concat(sc),
              border: "1.5px solid ".concat(sc, "33"),
              background: p.d9 > 0 ? "#fef2f2" : p.d8 > 0 ? "#fff7ed" : "#fefce8",
              borderRadius: 9,
              padding: "10px 12px"
            }}>{<div style={{
                fontWeight: 700,
                fontSize: 12,
                color: sc,
                marginBottom: 5
              }}>{urg} {size}</div>}{p.d9 > 0 && <div style={{
                fontSize: 11,
                color: "#b91c1c",
                fontWeight: 700
              }}>Day 9: {fb(p.d9)} bu</div>}{p.d8 > 0 && <div style={{
                fontSize: 11,
                color: "#c2540a",
                fontWeight: 700
              }}>Day 8: {fb(p.d8)} bu</div>}{p.d7 > 0 && <div style={{
                fontSize: 11,
                color: "#92400e"
              }}>Day 7: {fb(p.d7)} bu</div>}{<div style={{
                borderTop: "1px solid ".concat(sc, "22"),
                marginTop: 6,
                paddingTop: 6
              }}>{<div style={{
                  fontSize: 10,
                  color: B.textLight
                }}>Committed out: ~{fb(com)} bu/day</div>}{<div style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: exp > 0 ? sc : "#166534",
                  marginTop: 2
                }}>{(function() {  if (!com || com <= 0) return "\u2014";  if (p.totalAging <= com) return "\u2713 covered";  return fb(r2(p.totalAging - com)) + " bu at risk";})()}</div>}</div>}</div>;
          })}</div>}</div>}</Card>}{<Card style={{
      marginBottom: 14
    }}>{<div style={{
        background: "#1e3a5f",
        padding: "9px 16px",
        borderRadius: "10px 10px 0 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>{<div style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 13
        }}>Committed Outgoing Today</div>}{<div style={{
          color: "rgba(255,255,255,.45)",
          fontSize: 11
        }}>Actual orders shipping today</div>}</div>}{<div style={{
        padding: "12px 14px"
      }}>{(function() {
        // todayActual = parsed.days[today] = {size: bushels} for today's shipdate
        var todayOrders = todayActual || {};
        // Build per-size rows: show if has inventory OR has today orders
        var rows = SIZES.map(function({ size }) {
          var usableInvRow = enrichedLots.filter(function(l) {
            return l.size === size && l.usable;
          }).reduce(function(s, l) { return s + (parseFloat(l.bushels) || 0); }, 0);
          var committedToday = Number(todayOrders[size]) || 0;
          if (!usableInvRow && !committedToday) return null;
          var covered = usableInvRow >= committedToday;
          var shortfall = committedToday > 0 ? r2(Math.max(0, committedToday - usableInvRow)) : 0;
          return { size, usableInvRow, committedToday, covered, shortfall };
        }).filter(Boolean);

        if (rows.length === 0) return <div style={{ color: B.textLight, fontSize: 13, textAlign: "center", padding: "8px 0" }}>No orders or inventory data for today.</div>;

        return <div style={{ display: "flex", flexDirection: "column", gap: 0, border: "1px solid " + B.cardBorder, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr 1fr 1fr", background: B.rowAlt, borderBottom: "1px solid " + B.rowSep }}>
            {["Size", "Usable Inv", "Committed Today", "Status"].map(function(h, i) {
              return <div key={h} style={{ padding: "6px 12px", fontSize: 10, fontWeight: 700, letterSpacing: .5, color: B.textLight, textTransform: "uppercase", textAlign: i > 0 ? "center" : "left" }}>{h}</div>;
            })}
          </div>
          {rows.map(function({ size, usableInvRow, committedToday, covered, shortfall }) {
            var sc = SIZE_COLORS[size];
            var p = pressure[size];
            return <div key={size} style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr 1fr 1fr", borderBottom: "1px solid " + B.rowSep, background: "#fff", alignItems: "center" }}>
              <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: sc, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 12, color: sc }}>{size}</span>
              </div>
              <div style={{ textAlign: "center", padding: "9px 8px", fontSize: 12, fontWeight: p && p.totalAging > 0 ? 700 : 400, color: p && p.totalAging > 0 ? B.textDark : B.textLight }}>
                {usableInvRow > 0 ? fb(usableInvRow) + " bu" : "\u2014"}
                {usableInvRow > 0 && inventoryBySize && inventoryBySize[size] && (function() {
                  var db = inventoryBySize[size].dayBuckets || {};
                  var pb = inventoryBySize[size].priorityBuckets || {};
                  if (!pb.urgent && !pb.aging) return null;
                  var parts = [];
                  if (db[9] > 0) parts.push("\uD83D\uDD34 D9: " + fb(db[9]) + " bu");
                  if (db[8] > 0) parts.push("\uD83D\uDEA8 D8: " + fb(db[8]) + " bu");
                  if (db[7] > 0) parts.push("\u26A0 D7: " + fb(db[7]) + " bu");
                  if (db[6] > 0) parts.push("\u26A0 D6: " + fb(db[6]) + " bu");
                  return <div style={{ fontSize: 10, color: pb.urgent > 0 ? "#b91c1c" : "#92400e", marginTop: 2, lineHeight: 1.4 }}>{parts.join("  ")}</div>;
                })()}
              </div>
              <div style={{ textAlign: "center", padding: "9px 8px", fontSize: 12, color: committedToday > 0 ? B.textDark : B.textLight }}>
                {committedToday > 0 ? fb(committedToday) + " bu" : "\u2014"}
              </div>
              <div style={{ textAlign: "center", padding: "9px 8px" }}>
                {(function() {
                  if (!committedToday || committedToday <= 0) return <span style={{ fontSize: 11, color: B.textLight }}>{"\u2014"}</span>;
                  if (usableInvRow >= committedToday) return <span style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>{"\u2713 covered"}</span>;
                  if (usableInvRow === 0) return <span style={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", background: "#fee2e2", padding: "2px 7px", borderRadius: 4 }}>OUT</span>;
                  return <span style={{ fontWeight: 700, fontSize: 12, color: "#b91c1c" }}>{fb(shortfall) + " bu short"}</span>;
                })()}
              </div>
            </div>;
          })}
        </div>;
      })()}</div>}</Card>}{<Card style={{
      marginBottom: 14
    }}>{<CardHeader>Decisions & Notes for Today</CardHeader>}{<div style={{
        padding: "10px 16px 14px"
      }}>{<Textarea rows={4} value={ops.notes || ""} placeholder="Record decisions made this morning:\n– Upgraded select orders to premiums (11.75 bu colossal, day 7)\n– Included 2–3 extra crabs per order\n– Sent large female orders to CR — out of stock" onChange={function onChange(e) {
          return setNotes(e.target.value);
        }} />}{<div style={{
          color: B.textLight,
          fontSize: 11,
          marginTop: 5
        }}>Notes are included in the daily email recap</div>}</div>}</Card>}{<div style={{
      textAlign: "center",
      marginTop: 4,
      marginBottom: 8
    }}>{<button onClick={function onClick() {
        if (window.confirm("Clear ops notes for ".concat(today, "?"))) {
          setAllOps(function (all) {
            const n = {
              ...all
            };
            delete n[today];
            return n;
          });
        }
      }} style={{
        background: "transparent",
        border: "none",
        color: B.textLight,
        fontSize: 12,
        cursor: "pointer",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: 3
      }}>Clear today's notes</button>}</div>}</div>;
}


// ─── DAILY OPS SNAPSHOT ───────────────────────────────────────────────────────
// Fast-read summary. Uses enrichedLots directly — no computeOpsDecisions.
function DailyOpsSnapshot({
  enrichedLots,
  demandBySize,
  invTotals,
  planDate,
  liveCrabEntries,
  todayActual,
  parsedDays
}) {
  const today = planDate || todayISO();
  const dateLabel = new Date(today + "T12:00:00").toLocaleDateString([], {
    weekday: "long", month: "long", day: "numeric", year: "numeric"
  });

  // ── Daily committed by size: weekly demand / 5 (Mon-Fri) ─────────────────
  const committedBySize = useMemo(function() {
    var out = {};
    SIZES.forEach(function({ size }) {
      var raw = demandBySize && demandBySize[size];
      var weekly = typeof raw === "number" ? raw
        : (raw && typeof raw.trueDemand === "number" ? raw.trueDemand : 0);
      out[size] = weekly > 0 ? r2(weekly / 5) : 0;
    });
    return out;
  }, [demandBySize]);

  // ── Per-size usable inventory from enrichedLots ───────────────────────────
  const usableBySize = useMemo(function() {
    var out = {};
    SIZES.forEach(function({ size }) { out[size] = 0; });
    (enrichedLots || []).forEach(function(l) {
      if (l.usable && out[l.size] !== undefined)
        out[l.size] = r2(out[l.size] + (parseFloat(l.bushels) || 0));
    });
    return out;
  }, [enrichedLots]);

  // ── Expiry risks via shared computeExpiryRisk (> 2 bu gap after committed + last week pace) ──
  const expiryRisks = useMemo(function() {
    // Re-runs whenever enrichedLots or parsedDays changes (handles first-load race)
    return computeExpiryRisk(enrichedLots, parsedDays, today, false);
  }, [enrichedLots, parsedDays, today, JSON.stringify(parsedDays ? Object.keys(parsedDays).length : 0)]);

  // urgentBySize for backward compat with pressure/plan rendering
  const urgentBySize = useMemo(function() {
    var out = {};
    SIZES.forEach(function({ size }) { out[size] = 0; });
    Object.values(expiryRisks).forEach(function(r) {
      out[r.size] = r.expiring;
    });
    return out;
  }, [expiryRisks]);

  // ── Aging lots (day 6+) by size ───────────────────────────────────────────
  const agingBySize = useMemo(function() {
    var out = {};
    SIZES.forEach(function({ size }) { out[size] = { d6:0, d7:0, d8:0, d9:0, total:0 }; });
    (enrichedLots || []).forEach(function(l) {
      if (!l.usable) return;
      var age = l.age || 0;
      if (age < 6) return;
      var bu = parseFloat(l.bushels) || 0;
      var s = out[l.size];
      if (!s) return;
      s.total = r2(s.total + bu);
      if (age === 6) s.d6 = r2(s.d6 + bu);
      else if (age === 7) s.d7 = r2(s.d7 + bu);
      else if (age === 8) s.d8 = r2(s.d8 + bu);
      else if (age >= 9) s.d9 = r2(s.d9 + bu);
    });
    return out;
  }, [enrichedLots]);

  // ── Live / ungraded ───────────────────────────────────────────────────────
  const liveByGrade = useMemo(function() {
    return summarizeLiveRemaining(liveCrabEntries);
  }, [liveCrabEntries]);
  const gradeFirst = useMemo(function() {
    return buildGradeFirstRecs(liveByGrade, demandBySize, enrichedLots);
  }, [liveByGrade, demandBySize, enrichedLots]);

  // Sizes that live/ungraded grading will cover
  const gradeCoveredSizes = new Set(
    gradeFirst.flatMap(function(g) { return g.primarySizes || []; })
  );

  // ── Inventory pressure: only sizes with real gap > 2 bu (from computeExpiryRisk) ─────
  const pressureSizes = SIZES.map(function({ size }) {
    var ag = agingBySize[size];
    var risk = expiryRisks[size];
    // Only show pressure if there's a real gap flagged by computeExpiryRisk
    if (!risk) return null;
    return { size: size, ag: ag, remaining: risk.gap };
  }).filter(Boolean);

  // ── Shortages: usable < daily committed and not covered by live/ungraded ──
  const shortSizes = SIZES.map(function({ size }) {
    var usable = usableBySize[size];
    var com = committedBySize[size] || 0;
    if (com <= 0) return null;
    if (usable >= com) return null;
    if (gradeCoveredSizes.has(size)) return null; // grading will cover
    var isOos = usable === 0;
    return { size: size, usable: usable, com: com, isOos: isOos };
  }).filter(Boolean);

  // ── Today's plan ──────────────────────────────────────────────────────────
  const planLines = [
    // Grade actions first — always show
    ...gradeFirst.map(function(i) {
      return {
        type: "grade",
        text: "Grade " + i.grade + " first → covers " + i.primarySizes.join(" / ") + " (" + i.liveBoxes + " boxes ready)",
        urgency: "GRADE"
      };
    }),
    // Expiry risk actions — only sizes with gap > 2 bu after committed + last week pace
    ...SIZES.flatMap(function({ size }) {
      var risk = expiryRisks[size];
      if (!risk) return [];
      var snapActions = {
        'Colossal':       'Upgrade Premium orders to Colossal — ' + fb(risk.gap) + ' bu at risk',
        'Premium':        'Upgrade Select orders to Premium — ' + fb(risk.gap) + ' bu at risk',
        'Select':         'Upgrade Standard orders to Select — ' + fb(risk.gap) + ' bu at risk',
        'Large Female':   'Upgrade Regular Female orders to Large Female — ' + fb(risk.gap) + ' bu at risk',
        'Regular Female': 'Offer Regular Female to Large Female customers w/ credit — ' + fb(risk.gap) + ' bu at risk',
        'Standard':       'Offer Standard to Select customers w/ credit — ' + fb(risk.gap) + ' bu at risk'
      };
      var action = snapActions[size] || (size + ' — ' + fb(risk.gap) + ' bu at risk');
      var aType = (size === 'Regular Female' || size === 'Standard') ? 'cr' : 'upgrade';
      return [{ type: aType, text: action, urgency: 'CRITICAL' }];
    }),
    // CR actions for real shortages not covered by grading
    ...shortSizes.map(function(s) {
      return {
        type: "cr",
        text: s.isOos ? s.size + " OUT — send to CR" : "CR: " + s.size + " short — offer alternatives",
        urgency: s.isOos ? "OOS" : "SHORT"
      };
    }),
    // Pressure alerts: aging sizes with uncovered inventory
    ...pressureSizes.flatMap(function({ size, ag }) {
      if (size === "Regular Female") return [{ type: "cr", text: "Aging Regular Female — offer to Large Female customers w/ credit", urgency: "AGING" }];
      if (size === "Standard")       return [{ type: "cr", text: "Aging Standard — offer to Select customers w/ credit", urgency: "AGING" }];
      if (ag.d8 > 0 || ag.d9 > 0) {
        if (size === "Colossal")     return [{ type: "upgrade", text: "Aging Colossal — upgrade Premium orders to Colossal", urgency: "AGING" }];
        if (size === "Premium")      return [{ type: "upgrade", text: "Aging Premium — upgrade Select orders to Premium", urgency: "AGING" }];
        if (size === "Select")       return [{ type: "upgrade", text: "Aging Select — upgrade Standard orders to Select", urgency: "AGING" }];
        if (size === "Large Female") return [{ type: "upgrade", text: "Aging Large Female — upgrade Regular Female orders to Large Female", urgency: "AGING" }];
      }
      return [];
    })
  ];

  const isEmpty = pressureSizes.length === 0 && shortSizes.length === 0 && planLines.length === 0;

  // ── Renderers ─────────────────────────────────────────────────────────────
  function SectionHead({ icon, title, count, accent }) {
    return <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 14px", background: accent || B.navy,
      borderRadius: 8, marginBottom: 10
    }}><span style={{ fontSize: 15 }}>{icon}</span>
      <span style={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>{title}</span>
      {count != null && <span style={{
        marginLeft: "auto", background: "rgba(255,255,255,.15)",
        color: "rgba(255,255,255,.9)", fontSize: 10, fontWeight: 700,
        padding: "1px 7px", borderRadius: 10
      }}>{count}</span>}
    </div>;
  }
  function Empty({ msg }) {
    return <div style={{ color: B.textLight, fontSize: 12, fontStyle: "italic", padding: "2px 0 6px 0" }}>{msg}</div>;
  }

  return <div>
    {/* Header */}
    <div style={{
      background: "linear-gradient(135deg, " + B.navy + " 0%, #1e3a5f 100%)",
      borderRadius: 12, padding: "14px 16px", marginBottom: 14,
      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8
    }}>
      <div>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Daily Ops Snapshot</div>
        <div style={{ color: "rgba(255,255,255,.5)", fontSize: 12, marginTop: 2 }}>{dateLabel}</div>
      </div>
      {isEmpty
        ? <div style={{ background: "rgba(134,239,172,.15)", border: "1px solid rgba(134,239,172,.3)", borderRadius: 8, padding: "5px 12px", color: "#86efac", fontSize: 12, fontWeight: 700 }}>✓ All clear</div>
        : <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {pressureSizes.length > 0 && <div style={{ background: "rgba(253,186,116,.15)", border: "1px solid rgba(253,186,116,.3)", borderRadius: 8, padding: "5px 12px", color: "#fdba74", fontSize: 11, fontWeight: 700 }}>{pressureSizes.length} aging</div>}
            {shortSizes.length > 0 && <div style={{ background: "rgba(252,165,165,.15)", border: "1px solid rgba(252,165,165,.3)", borderRadius: 8, padding: "5px 12px", color: "#fca5a5", fontSize: 11, fontWeight: 700 }}>{shortSizes.length} short</div>}
            {planLines.length > 0 && <div style={{ background: "rgba(134,239,172,.15)", border: "1px solid rgba(134,239,172,.3)", borderRadius: 8, padding: "5px 12px", color: "#86efac", fontSize: 11, fontWeight: 700 }}>{planLines.length} action{planLines.length !== 1 ? "s" : ""}</div>}
          </div>
      }
    </div>

    {/* 2-col grid */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 12, marginBottom: 12 }}>

      {/* Inventory Pressure */}
      <Card style={{ padding: "12px 14px" }}>
        <SectionHead icon="⚠️" title="Inventory Pressure" count={pressureSizes.length || undefined} accent="#1e3a5f" />
        {pressureSizes.length === 0
          ? <Empty msg="No uncovered aging inventory today." />
          : <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {pressureSizes.map(function({ size, ag, remaining }) {
                var sc = SIZE_COLORS[size];
                var urg = ag.d9 > 0 ? "🔴" : ag.d8 > 0 ? "🟠" : "🟡";
                var urgBg = ag.d9 > 0 ? "#fef2f2" : ag.d8 > 0 ? "#fff7ed" : "#fefce8";
                return <div key={size} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7, background: urgBg, borderLeft: "3px solid " + sc }}>
                  <span style={{ fontSize: 13 }}>{urg}</span>
                  <span style={{ fontWeight: 700, fontSize: 12, color: sc, minWidth: 90 }}>{size}</span>
                  <div style={{ flex: 1, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {ag.d9 > 0 && <span style={{ fontSize: 11, color: "#b91c1c", fontWeight: 700 }}>D9:{fb(ag.d9)}</span>}
                    {ag.d8 > 0 && <span style={{ fontSize: 11, color: "#c2540a", fontWeight: 700 }}>D8:{fb(ag.d8)}</span>}
                    {ag.d7 > 0 && <span style={{ fontSize: 11, color: "#92400e" }}>D7:{fb(ag.d7)}</span>}
                    {ag.d6 > 0 && <span style={{ fontSize: 11, color: "#b45309" }}>D6:{fb(ag.d6)}</span>}
                  </div>
                  <span style={{ fontSize: 11, color: B.textLight, whiteSpace: "nowrap" }}>{fb(remaining)} bu</span>
                </div>;
              })}
            </div>
        }
      </Card>

      {/* Committed Outgoing Today - actual orders from product list */}
      <Card style={{ padding: "12px 14px" }}>
        <SectionHead icon="📦" title="Committed Outgoing Today" accent="#1e3a5f" />
        {(function() {
          var orders = todayActual || {};
          var rows = SIZES.map(function({ size }) {
            var usable = usableBySize[size] || 0;
            var com = Number(orders[size]) || 0;
            if (!usable && !com) return null;
            var covered = com <= 0 ? null : usable >= com;
            return { size: size, usable: usable, com: com, covered: covered };
          }).filter(Boolean);
          if (!rows.length) return <Empty msg="No orders or inventory data for today." />;
          return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {rows.map(function({ size, usable, com, covered }) {
              var sc = SIZE_COLORS[size];
              return <div key={size} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 6, background: "#fafaf8" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: sc, flexShrink: 0 }} />
                <span style={{ fontWeight: 600, fontSize: 12, color: sc, flex: 1 }}>{size}</span>
                <span style={{ fontSize: 11, color: B.textLight }}>on hand:</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: usable > 0 ? B.textDark : B.textLight, minWidth: 50, textAlign: "right" }}>{usable > 0 ? fb(usable) + " bu" : "—"}</span>
                <span style={{ fontSize: 11, color: B.textLight, marginLeft: 4 }}>out:</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: B.navy, minWidth: 50, textAlign: "right" }}>{com > 0 ? fb(com) + " bu" : "—"}</span>
                {covered !== null && <span style={{ fontSize: 11, fontWeight: 600, color: covered ? "#166534" : "#b91c1c", marginLeft: 4 }}>{covered ? "✓" : "⚠"}</span>}
              </div>;
            })}
          </div>;
        })()}
      </Card>

      {/* Shortages */}
      <Card style={{ padding: "12px 14px" }}>
        <SectionHead icon="🔻" title="Shortages" count={shortSizes.length || undefined} accent={shortSizes.length > 0 ? "#b45309" : "#1e3a5f"} />
        {shortSizes.length === 0
          ? <Empty msg="No shortages detected." />
          : <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {shortSizes.map(function({ size, isOos }) {
                var sc = SIZE_COLORS[size];
                return <div key={size} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 7, background: isOos ? "#fef2f2" : "#fff7ed", borderLeft: "3px solid " + (isOos ? "#ef4444" : sc) }}>
                  <span style={{ fontSize: 13 }}>{isOos ? "⚫" : "🔻"}</span>
                  <span style={{ fontWeight: 700, fontSize: 12, color: sc, flex: 1 }}>{size}</span>
                  {isOos ? <span style={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", background: "#fee2e2", padding: "2px 7px", borderRadius: 4 }}>OUT</span>
                         : <span style={{ fontSize: 12, fontWeight: 700, color: "#c2540a" }}>short</span>}
                </div>;
              })}
            </div>
        }
      </Card>

      {/* Today's Plan */}
      <Card style={{ padding: "12px 14px" }}>
        <SectionHead icon="📋" title="Today's Plan" count={planLines.length || undefined} accent="#166534" />
        {planLines.length === 0
          ? <Empty msg="No action needed — inventory is balanced." />
          : <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {planLines.map(function(line, i) {
                var isGrade = line.type === "grade";
                var isCr = line.type === "cr";
                var dotColor = isGrade ? "#22c55e" : isCr ? "#f97316" : "#3b82f6";
                var rowBg = isGrade ? "#f0fdf4" : isCr ? "#fff7ed" : "#f0f9ff";
                var textColor = isGrade ? "#166534" : isCr ? "#c2540a" : "#1e40af";
                return <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 10px", borderRadius: 7, background: rowBg }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor, flexShrink: 0, marginTop: 3 }} />
                  <span style={{ fontSize: 12, color: textColor, lineHeight: 1.5 }}>{line.text}</span>
                </div>;
              })}
            </div>
        }
      </Card>
    </div>

    {/* Grading Priority */}
    {gradeFirst.length > 0 && <Card style={{ marginBottom: 14 }}>
      <div style={{ background: "#166534", padding: "9px 16px", borderRadius: "10px 10px 0 0", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14 }}>1st</span>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>Grading Priority — Grade These First</span>
      </div>
      <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        {gradeFirst.map(function(rec, i) {
          return <div key={i} style={{ background: "#f0fdf4", borderRadius: 8, padding: "10px 12px", borderLeft: "3px solid #22c55e" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#166534", marginBottom: 3 }}>Grade {rec.grade} first <span style={{ background: "#dcfce7", color: "#166534", fontSize: 10, padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>High</span></div>
            <div style={{ fontSize: 12, color: B.textMid }}>{rec.detail || ("→ Covers " + rec.primarySizes.join(" / "))}</div>
          </div>;
        })}
      </div>
    </Card>}
  </div>;
}


async function getLiveHardCrabRows(endDateIso) {
  try {
    const today = new Date(todayISO() + 'T12:00:00');
    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 2);
    const end = new Date(today);
    end.setDate(end.getDate() + 14);
    const startDate = start.toISOString().slice(0, 10);
    const endDate = end.toISOString().slice(0, 10);
    console.log('Loading hard crab data:', startDate, '→', endDate);
    const rows = await neonQuery('product_report_hard_crabs_by_date', { startDate, endDate });
    const result = (Array.isArray(rows) ? rows : []).map(function(r) {
      return {
        size: r.size || '',
        steaming: r.steaming || '',
        gender: r.gender || '',
        bushels: Number(r.bushels || 0),
        shipdate: r.shipdate || endDateIso || ''
      };
    });
    console.log('product_report_hard_crabs rows:', result.length, result[0]);
    return result;
  } catch (err) {
    console.error('product_report_hard_crabs failed:', err);
    return [];
  }
}


// ─── DEFERRED INPUT — holds local state, only pushes up on blur/Enter ──────────
function DeferredInput({ value, onChange, onKeyDown, type, placeholder, min, step, style }) {
  const _ls = useState(value !== undefined ? value : '');
  const localVal = _ls[0];
  const setLocalVal = _ls[1];
  const localRef = useRef(localVal);
  localRef.current = localVal;
  // Sync if external value changes (e.g. clear all)
  const prevValue = useRef(value);
  if (prevValue.current !== value && value !== localRef.current) {
    prevValue.current = value;
    setLocalVal(value !== undefined ? value : '');
  }
  function commit() {
    onChange(localRef.current);
  }
  return React.createElement('input', {
    type: type || 'text',
    value: localVal,
    placeholder: placeholder,
    min: min,
    step: step,
    style: style,
    onChange: function(ev) { setLocalVal(ev.target.value); localRef.current = ev.target.value; },
    onBlur: commit,
    onKeyDown: function(ev) {
      if (ev.key === 'Enter') { commit(); if (onKeyDown) onKeyDown(ev); }
      else if (onKeyDown) onKeyDown(ev);
    }
  });
}

// ─── CRAB MEAT COUNT PANEL ────────────────────────────────────────────────────
var CRAB_MEAT_PRODUCTS = ['Jumbo', 'Gourmet', 'Backfin'];

function CrabMeatPanel({ entries, onChange }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  function addEntry() {
    onChangeRef.current([...entriesRef.current, { id: Date.now(), product: 'Jumbo', date: todayISO(), lbs: '' }]);
  }
  function updateEntry(id, field, value) {
    onChangeRef.current(entriesRef.current.map(function(e) { return e.id === id ? { ...e, [field]: value } : e; }));
  }
  function removeEntry(id) {
    onChangeRef.current(entriesRef.current.filter(function(e) { return e.id !== id; }));
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); addEntry(); }
  }

  return <div style={{ padding: '0 14px 14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, marginBottom: 12 }}>
      <div style={{ color: B.navy, fontWeight: 700, fontSize: 14 }}>Crab Meat Inventory</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {entries.length > 0 && <Btn variant="secondary" small onClick={function() { if (window.confirm('Clear all crab meat entries?')) onChange([]); }}>Clear All</Btn>}
        <Btn variant="primary" small onClick={addEntry}>+ Add Entry</Btn>
      </div>
    </div>
    {entries.length === 0
      ? <div style={{ color: B.textLight, fontSize: 12, textAlign: 'center', padding: '12px 0' }}>No crab meat entries. Add entries to include in the daily recap.</div>
      : <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 100px 36px', gap: 6, marginBottom: 6 }}>
            {['Product', 'Date', 'Lbs', ''].map(function(h, i) {
              return <div key={i} style={{ fontSize: 10, fontWeight: 700, color: B.textMid, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</div>;
            })}
          </div>
          {entries.map(function(e) {
            return <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 100px 36px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <select value={e.product} onChange={function(ev) { updateEntry(e.id, 'product', ev.target.value); }} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid ' + B.cardBorder, fontSize: 13, color: B.textDark }}>
                {CRAB_MEAT_PRODUCTS.map(function(p) { return <option key={p} value={p}>{p}</option>; })}
              </select>
              <input type="date" defaultValue={e.date} onBlur={function(ev) { updateEntry(e.id, 'date', ev.target.value); }} onChange={function(ev) { updateEntry(e.id, 'date', ev.target.value); }} key={'date-' + e.id} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid ' + B.cardBorder, fontSize: 13, color: B.textDark }} />
              <DeferredInput type="number" value={e.lbs} placeholder="0" min="0" step="0.1" onChange={function(v) { updateEntry(e.id, 'lbs', v); }} onKeyDown={handleKeyDown} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid ' + B.cardBorder, fontSize: 13, color: B.textDark, textAlign: 'right', width: '100%' }} />
              <button onClick={function() { removeEntry(e.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.alertRed, fontSize: 16, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>;
          })}
          <div style={{ borderTop: '1px solid ' + B.rowSep, marginTop: 8, paddingTop: 8 }}>
            {CRAB_MEAT_PRODUCTS.map(function(product) {
              var total = entries.filter(function(e) { return e.product === product; }).reduce(function(s, e) { return s + (Number(e.lbs) || 0); }, 0);
              if (!total) return null;
              return <div key={product} style={{ fontSize: 12, color: B.textMid, marginBottom: 2 }}>
                <span style={{ fontWeight: 700, color: B.textDark }}>{product}:</span> {total.toFixed(2)} lbs
              </div>;
            })}
          </div>
        </div>
    }
  </div>;
}

function App() {
  // Forecaster state
  const _useState63 = useState(null),
    _useState64 = _useState63,
    parsed = _useState64[0],
    setParsed = _useState64[1];
  const _useState65 = useState(false),
    _useState66 = _useState65,
    loading = _useState66[0],
    setLoading = _useState66[1];
  const _useState71 = useState("weekly"),
    _useState72 = _useState71,
    viewMode = _useState72[0],
    setViewMode = _useState72[1];
  const _useState73 = useState(null),
    _useState74 = _useState73,
    selWk = _useState74[0],
    setSelWk = _useState74[1];
  const _useState75 = useState(null),
    _useState76 = _useState75,
    selDay = _useState76[0],
    setSelDay = _useState76[1];
  const _useState77 = useState(false),
    _useState78 = _useState77,
    showChart = _useState78[0],
    setShowChart = _useState78[1];
  const _usePaceData = useState({ thisWeek: {}, lastWeek: {}, loading: true, lastUpdated: null, error: null });
  const paceData = _usePaceData[0];
  const setPaceData = _usePaceData[1];
  const _useState79 = useState("forecaster"),
    _useState80 = _useState79,
    appTab = _useState80[0],
    setAppTab = _useState80[1];
  const _useState81 = useState(false),
    _useState82 = _useState81,
    showEmailModal = _useState82[0],
    setShowEmailModal = _useState82[1];
  // Live crab entries lifted here so liveSupplyByGrade reacts to Live tab changes
  const _useState83 = useState(function () {
      return loadLiveCrabs();
    }),
    _useState84 = _useState83,
    liveCrabEntries = _useState84[0],
    setLiveCrabEntries = _useState84[1];

  // ── Planner state — hydrated from localStorage on first load ────────────────
  const _saved = useMemo(function () {
    const raw = loadStorage();
    return raw ? hydrateState(raw) : null;
  }, []); // runs once on mount

  const _useState85 = useState(function () {
      let _saved$inventoryLots;
      return (_saved$inventoryLots = _saved === null || _saved === void 0 ? void 0 : _saved.inventoryLots) !== null && _saved$inventoryLots !== void 0 ? _saved$inventoryLots : [];
    }),
    _useState86 = _useState85,
    inventoryLots = _useState86[0],
    setInventoryLots = _useState86[1];
  const _useState87 = useState(function () {
      let _saved$planningInputs0;
      return (_saved$planningInputs0 = _saved === null || _saved === void 0 ? void 0 : _saved.planningInputs) !== null && _saved$planningInputs0 !== void 0 ? _saved$planningInputs0 : {
        inbound1s: "",
        inbound2s: "",
        inbound3s: "",
        weeklyOverride: "",
        planDate: todayISO()
      };
    }),
    _useState88 = _useState87,
    planningInputs = _useState88[0],
    setPlanningInputs = _useState88[1];
  const _useState89 = useState(function () {
      let _saved$demandOverride;
      return (_saved$demandOverride = _saved === null || _saved === void 0 ? void 0 : _saved.demandOverrides) !== null && _saved$demandOverride !== void 0 ? _saved$demandOverride : {};
    }),
    _useState90 = _useState89,
    demandOverrides = _useState90[0],
    setDemandOverrides = _useState90[1];
  const _useState91 = useState(function () {
      let _saved$gradeOverrides;
      return (_saved$gradeOverrides = _saved === null || _saved === void 0 ? void 0 : _saved.gradeOverrides) !== null && _saved$gradeOverrides !== void 0 ? _saved$gradeOverrides : {};
    }),
    _useState92 = _useState91,
    gradeOverrides = _useState92[0],
    setGradeOverrides = _useState92[1];
  const _useState93 = useState(function () {
      let _saved$purchases;
      return (_saved$purchases = _saved === null || _saved === void 0 ? void 0 : _saved.purchases) !== null && _saved$purchases !== void 0 ? _saved$purchases : [];
    }),
    _useState94 = _useState93,
    purchases = _useState94[0],
    setPurchases = _useState94[1];
  const _useState95 = useState(function () {
      let _saved$vendors;
      return (_saved$vendors = _saved === null || _saved === void 0 ? void 0 : _saved.vendors) !== null && _saved$vendors !== void 0 ? _saved$vendors : [];
    }),
    _useState96 = _useState95,
    vendors = _useState96[0],
    setVendors = _useState96[1];
  const _useState97 = useState(false),
    _useState98 = _useState97,
    showForm = _useState98[0],
    setShowForm = _useState98[1];
  const _useState99 = useState(null),
    _useState100 = _useState99,
    editingPurchase = _useState100[0],
    setEditingPurchase = _useState100[1];
  const _useState101 = useState(makePurchase()),
    _useState102 = _useState101,
    draftPurchase = _useState102[0],
    setDraftPurchase = _useState102[1];
  const _useState103 = useState(function () {
      let _loadMeta$savedAt, _loadMeta;
      return (_loadMeta$savedAt = (_loadMeta = loadMeta()) === null || _loadMeta === void 0 ? void 0 : _loadMeta.savedAt) !== null && _loadMeta$savedAt !== void 0 ? _loadMeta$savedAt : null;
    }),
    _useState104 = _useState103,
    savedAt = _useState104[0],
    setSavedAt = _useState104[1];
  const _useState105 = useState(function () {
      return loadSnapshots();
    }),
    _useState106 = _useState105,
    snapshotHistory = _useState106[0],
    setSnapshotHistory = _useState106[1];

  const _useCrabMeat = useState([]);
  const crabMeatEntries = _useCrabMeat[0];
  const setCrabMeatEntries = _useCrabMeat[1];
  const _useTaktHistory = useState(function() { return loadTaktHist(); });
  const taktHistory = _useTaktHistory[0];
  const setTaktHistory = _useTaktHistory[1];
  const _useTaktAll = useState(function() { return loadTakt(); });
  const taktAll = _useTaktAll[0];
  const setTaktAll = _useTaktAll[1];

  // ── Neon shared state ─────────────────────────────────────────────────────
  const [neonLoaded, setNeonLoaded] = useState(false);
  const saveTimerRef = useRef(null);

  function serializeState() {
    return {
      inventoryLots: inventoryLots,
      planningInputs: planningInputs,
      purchases: purchases,
      vendors: vendors,
      snapshotHistory: snapshotHistory,
      liveCrabEntries: liveCrabEntries,
      demandOverrides: demandOverrides,
      gradeOverrides: gradeOverrides,
      crabMeatEntries: crabMeatEntries,
      taktHistory: taktHistory,
      taktAll: taktAll
    };
  }

  function deserializeState(s) {
    if (!s) return;
    if (s.inventoryLots !== undefined) setInventoryLots(s.inventoryLots);
    if (s.planningInputs !== undefined) setPlanningInputs(s.planningInputs);
    if (s.purchases !== undefined) setPurchases(s.purchases);
    if (s.vendors !== undefined) setVendors(s.vendors);
    if (s.snapshotHistory !== undefined) setSnapshotHistory(s.snapshotHistory);
    if (s.liveCrabEntries !== undefined) setLiveCrabEntries(s.liveCrabEntries);
    if (s.demandOverrides !== undefined) setDemandOverrides(s.demandOverrides);
    if (s.gradeOverrides !== undefined) setGradeOverrides(s.gradeOverrides);
    if (s.crabMeatEntries !== undefined) setCrabMeatEntries(s.crabMeatEntries);
    if (s.taktHistory !== undefined && s.taktHistory.length > 0) {
      setTaktHistory(normalizeTaktHistory(s.taktHistory));
    } else {
      var localHist = loadTaktHist();
      if (localHist && localHist.length > 0) setTaktHistory(normalizeTaktHistory(localHist));
    }
    if (s.taktAll !== undefined && Object.keys(s.taktAll).length > 0) {
      setTaktAll(s.taktAll);
    } else {
      var localTakt = loadTakt();
      if (localTakt && Object.keys(localTakt).length > 0) setTaktAll(localTakt);
    }
  }

  // On mount: load the most recently saved state from Neon
  // Uses load_latest so we always get Montel's last save regardless of plan date
  useEffect(function () {
    neonQuery('hcp_state_load_latest', {})
      .then(function (res) {
        var row = Array.isArray(res) ? res[0] : (res && res.rows ? res.rows[0] : null);
        if (row && row.state) {
          deserializeState(row.state);
        }
      })
      .catch(function (err) {
        console.warn('[HCP] Neon load failed:', err);
      })
      .finally(function () {
        setNeonLoaded(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced auto-save to Neon — fires 3s after any shared state change
  useEffect(function () {
    if (!neonLoaded) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(function () {
      const planDate = planningInputs.planDate || todayISO();
      const state = serializeState();
      neonQuery('hcp_state_save', { plan_date: planDate, state: state })
        .catch(function (err) {
          console.warn('[HCP] Neon save failed:', err);
        });
    }, 3000);
    return function () {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [inventoryLots, planningInputs, purchases, vendors, snapshotHistory, liveCrabEntries, demandOverrides, gradeOverrides, neonLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Immediate save — called when critical data changes (e.g. grader history commit)
  function doNeonSaveNow() {
    const planDate = (planningInputs && planningInputs.planDate) || todayISO();
    const state = serializeState();
    neonQuery('hcp_state_save', { plan_date: planDate, state: state })
      .catch(function(err) { console.warn('[HCP] Immediate save failed:', err); });
  }

  // ── Auto-save whenever any persisted state changes ────────────────────────
  useEffect(function () {
    const data = {
      inventoryLots: inventoryLots,
      planningInputs: planningInputs,
      demandOverrides: demandOverrides,
      gradeOverrides: gradeOverrides,
      purchases: purchases,
      vendors: vendors
    };
    saveStorage(data);
    setSavedAt(new Date().toISOString());
  }, [inventoryLots, planningInputs, demandOverrides, gradeOverrides, purchases, vendors]);

  // ── Persist snapshot history separately ───────────────────────────────────
  useEffect(function () {
    saveSnapshots(snapshotHistory);
  }, [snapshotHistory]);

  // ── Reset ─────────────────────────────────────────────────────────────────
  function handleReset() {
    if (!window.confirm("Reset all planner data? This will clear inventory, purchases, vendors, and overrides. This cannot be undone.")) return;
    clearStorage();
    clearSnapshots();
    setInventoryLots([]);
    setPlanningInputs({
      inbound1s: "",
      inbound2s: "",
      inbound3s: "",
      weeklyOverride: "",
      planDate: todayISO()
    });
    setDemandOverrides({});
    setGradeOverrides({});
    setPurchases([]);
    setVendors([]);
    setDraftPurchase(makePurchase());
    setEditingPurchase(null);
    setShowForm(false);
    setSavedAt(null);
    setSnapshotHistory([]);
  }
  const allWks = useMemo(function () {
    return parsed ? Object.keys(parsed.weeks).sort() : [];
  }, [parsed]);
  const allDays = useMemo(function () {
    return parsed ? Object.keys(parsed.days).sort() : [];
  }, [parsed]);

  // ── Load live hard crab data on mount (same source as ProductReport) ───────
  function doLoadHardCrabData(attempt) {
    setLoading(true);
    getLiveHardCrabRows(todayISO()).then(function (rows) {
      console.log("hard_crabs rows:", (rows || []).length, rows && rows[0]);
      if (!rows || rows.length === 0) {
        // Retry once after 2s — handles Netlify cold start / first-load race
        if ((attempt || 0) < 1) {
          setTimeout(function() { doLoadHardCrabData(1); }, 2000);
          return;
        }
        console.warn("No hard crab rows returned for this date");
        setParsed(null);
        setLoading(false);
        return;
      }
      // Build weeks/days structure from all returned rows grouped by shipdate
      const weeks = {}, days = {};
      rows.forEach(function (r) {
        if (!r.shipdate || !r.size || !r.bushels) return;
        const day = r.shipdate.slice(0, 10);
        // Week start: Monday of that week
        const d = new Date(day + 'T12:00:00');
        const dow = d.getDay(); // 0=Sun
        const diff = (dow === 0 ? -6 : 1 - dow);
        const mon = new Date(d);
        mon.setDate(d.getDate() + diff);
        const wk = mon.toISOString().slice(0, 10);
        // Accumulate into days
        if (!days[day]) days[day] = {};
        days[day][r.size] = (days[day][r.size] || 0) + r.bushels;
        // Accumulate into weeks
        if (!weeks[wk]) weeks[wk] = {};
        weeks[wk][r.size] = (weeks[wk][r.size] || 0) + r.bushels;
      });
      const allWksSorted = Object.keys(weeks).sort();
      const allDaysSorted = Object.keys(days).sort();
      const lastWk = allWksSorted[allWksSorted.length - 1] || '';
      const lastDay = allDaysSorted[allDaysSorted.length - 1] || '';
      // Overall bySize for current planning period
      const bySize = {};
      rows.forEach(function (r) { bySize[r.size] = (bySize[r.size] || 0) + r.bushels; });
      const p = { weeks, days, bySize, rows };
      setParsed(p);
      // Default forecaster to current week's Monday
      const todayD = new Date(todayISO() + 'T12:00:00');
      const dw = todayD.getDay();
      const mon = new Date(todayD);
      mon.setDate(todayD.getDate() + (dw === 0 ? -6 : 1 - dw));
      const currentWkKey = mon.toISOString().slice(0, 10);
      setSelWk(allWksSorted.includes(currentWkKey) ? currentWkKey : lastWk);
      setSelDay(todayISO() in days ? todayISO() : lastDay);
      setLoading(false);
    })["catch"](function (err) {
      console.error("hard_crabs fetch error:", err);
      setLoading(false);
    });
  }
  // ── Sales Pace fetch — lives in App so data survives tab switches ───────────
  function appFetchPaceData() {
    var today = todayISO();
    var todayDate = new Date(today + 'T12:00:00');
    var fridayDate = new Date(todayDate);
    var fdow = fridayDate.getDay();
    fridayDate.setDate(fridayDate.getDate() + (fdow === 0 ? 5 : fdow <= 5 ? 5 - fdow : 6));
    var remainingDays = [];
    var cur = new Date(todayDate);
    while (cur <= fridayDate) {
      var d = cur.getDay();
      if (d >= 1 && d <= 5) remainingDays.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    var compMonday = (function() {
      var wb = 1;
      while (wb <= 6) {
        var cand = new Date(new Date(mondayOfWeek(today) + 'T12:00:00'));
        cand.setDate(cand.getDate() - (7 * wb));
        var candISO = cand.toISOString().slice(0, 10);
        if (!getHolidayForWeek(candISO)) return candISO;
        wb++;
      }
      return null;
    })();
    if (!compMonday || !remainingDays.length) return;
    setPaceData(function(p) { return { ...p, loading: true }; });
    var compDays = remainingDays.map(function(dayISO) {
      var dow = new Date(dayISO + 'T12:00:00').getDay();
      var cd = new Date(compMonday + 'T12:00:00');
      cd.setDate(cd.getDate() + (dow - 1));
      return cd.toISOString().slice(0, 10);
    });
    Promise.all([
      neonQuery('product_report_hard_crabs_by_date', { startDate: remainingDays[0], endDate: remainingDays[remainingDays.length - 1] }),
      neonQuery('product_report_hard_crabs_by_date', { startDate: compDays[0], endDate: compDays[compDays.length - 1] }),
    ]).then(function(results) {
      var thisRows = results[0]; var lastRows = results[1];
      function sumBySize(rows) {
        var out = {};
        (Array.isArray(rows) ? rows : []).forEach(function(r) {
          var s = r.size || 'Other';
          if (!out[s]) out[s] = 0;
          out[s] = r2(out[s] + Number(r.bushels || 0));
        });
        return out;
      }
      setPaceData({ thisWeek: sumBySize(thisRows), lastWeek: sumBySize(lastRows), loading: false, lastUpdated: new Date(), error: null });
    }).catch(function(err) {
      if (!appFetchPaceData._retried) {
        appFetchPaceData._retried = true;
        setTimeout(appFetchPaceData, 2000);
      } else {
        setPaceData(function(p) { return { ...p, loading: false, error: err.message }; });
      }
    });
  }

  useEffect(function () {
    // Warm up the Netlify function first — it goes cold after inactivity
    // and the first real query often fails with a 500 while it wakes up.
    // A lightweight ping 300ms before the real fetch gives it time to warm.
    neonQuery('orders_placed_today', {})
      .catch(function() {}) // ignore result — just waking the function
      .finally(function() {
        setTimeout(function() { doLoadHardCrabData(0); }, 300);
        setTimeout(function() { appFetchPaceData(); }, 500);
      });
    var paceInterval = setInterval(appFetchPaceData, 5 * 60 * 1000);
    return function() { clearInterval(paceInterval); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Inventory derived
  const enrichedLots = useMemo(function () {
    return inventoryLots.map(function (l) {
      return enrichLot(l, planningInputs.planDate || todayISO());
    });
  }, [inventoryLots, planningInputs.planDate]);
  const inventoryBySize = useMemo(function () {
    return summarizeInventoryBySize(enrichedLots);
  }, [enrichedLots]);
  const invTotals = useMemo(function () {
    let total = 0,
      usable = 0,
      urgent = 0,
      expired = 0;
    enrichedLots.forEach(function (l) {
      const bu = Number(l.bushels) || 0;
      total = r2(total + bu);
      if (l.usable) usable = r2(usable + bu);
      if (l.status === "URGENT") urgent = r2(urgent + bu);
      if (!l.usable) expired = r2(expired + bu);
    });
    return {
      total: total,
      usable: usable,
      urgent: urgent,
      expired: expired
    };
  }, [enrichedLots]);

  // Forecast
  const weekCompletionFactor = useMemo(function () {
    if (!parsed || !allDays.length) return null;
    const todayKey = todayISO();
    return getWeekCompletionFactor(parsed.days, allDays, todayKey);
  }, [parsed, allDays]);
  // ── FORECAST: clean 2-week trailing average of completed Mon-Fri weeks ────
  // No SWLY blending, no trend, no holiday math, no week-completion projection.
  // A "completed week" = a week whose Friday is strictly before today.
  const forecastBySize = useMemo(function() {
    if (!parsed || !allWks.length) return {};
    const todayKey = todayISO();

    // Find the Monday of the current week
    const todayD = new Date(todayKey + 'T12:00:00');
    const dow = todayD.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const currentWkMon = new Date(todayD);
    currentWkMon.setDate(todayD.getDate() + diff);
    const currentWkMonISO = currentWkMon.toISOString().slice(0, 10);

    // Completed weeks = allWks where week Monday < current week Monday
    const completedWks = allWks.filter(function(wk) { return wk < currentWkMonISO; });
    const last2 = completedWks.slice(-2);

    const out = {};
    SIZES.forEach(function({ size }) {
      if (last2.length === 0) { out[size] = 0; return; }
      const total = last2.reduce(function(s, wk) {
        return s + (getBu(parsed.weeks, wk, size) || 0);
      }, 0);
      out[size] = r2(total / last2.length);
    });
    return out;
  }, [parsed, allWks]);

  const forecastTotalBu = useMemo(function() {
    return r2(Object.values(forecastBySize).reduce(function(s, v) { return s + v; }, 0));
  }, [forecastBySize]);

  // ── PACE SIGNAL: this week so far vs same point last week ─────────────────
  const weekPaceSignal = useMemo(function() {
    if (!parsed || !allDays.length) return null;
    const todayKey = todayISO();
    const todayD = new Date(todayKey + 'T12:00:00');
    const dow = todayD.getDay(); // 1=Mon..5=Fri
    if (dow === 0 || dow === 6) return null; // weekend

    // Days in current week up to and including today
    const currentWkMon = new Date(todayD);
    const diff = dow === 0 ? -6 : 1 - dow;
    currentWkMon.setDate(todayD.getDate() + diff);
    const currentWkMonISO = currentWkMon.toISOString().slice(0, 10);

    // Sum this week Mon through today
    let thisWkBu = 0;
    for (let i = 0; i < dow; i++) {
      const d = new Date(currentWkMon);
      d.setDate(currentWkMon.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      if (iso <= todayKey) {
        SIZES.forEach(function({ size }) {
          thisWkBu += getBu(parsed.days, iso, size) || 0;
        });
      }
    }
    thisWkBu = r2(thisWkBu);

    // Last week: same DOW span (Mon through same weekday)
    const lastWkMon = new Date(currentWkMon);
    lastWkMon.setDate(currentWkMon.getDate() - 7);
    let lastWkBu = 0;
    for (let i = 0; i < dow; i++) {
      const d = new Date(lastWkMon);
      d.setDate(lastWkMon.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      SIZES.forEach(function({ size }) {
        lastWkBu += getBu(parsed.days, iso, size) || 0;
      });
    }
    lastWkBu = r2(lastWkBu);

    if (lastWkBu <= 0) return null;
    const pct = r2(((thisWkBu - lastWkBu) / lastWkBu) * 100);
    return { thisWkBu, lastWkBu, pct, dow };
  }, [parsed, allDays]);

  // Last completed week total
  const lastWeekActualBu = useMemo(function() {
    if (!parsed || !allWks.length) return 0;
    const todayD = new Date(todayISO() + 'T12:00:00');
    const dow = todayD.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    const currentWkMon = new Date(todayD);
    currentWkMon.setDate(todayD.getDate() + diff);
    const currentWkMonISO = currentWkMon.toISOString().slice(0, 10);
    const completedWks = allWks.filter(function(wk) { return wk < currentWkMonISO; });
    const lastWk = completedWks[completedWks.length - 1];
    if (!lastWk) return 0;
    return r2(SIZES.reduce(function(s, { size }) {
      return s + (getBu(parsed.weeks, lastWk, size) || 0);
    }, 0));
  }, [parsed, allWks]);

  const nextWeekForecastBu = forecastTotalBu;

  const effectiveForecastBySize = useMemo(function () {
    const ov = planningInputs.weeklyOverride ? Number(planningInputs.weeklyOverride) : 0;
    if (!ov) return forecastBySize;
    if (!forecastTotalBu) {
      const per = r2(ov / SIZES.length);
      return Object.fromEntries(SIZES.map(function ({
        size
      }) {
        return [size, per];
      }));
    }
    const scale = ov / forecastTotalBu;
    return Object.fromEntries(Object.entries(forecastBySize).map(function ([s, bu]) {
      return [s, r2(bu * scale)];
    }));
  }, [forecastBySize, forecastTotalBu, planningInputs.weeklyOverride]);
  const demandPicture = useMemo(function () {
    return computeDemandPicture({
      forecastBySize: effectiveForecastBySize,
      demandOverrides: demandOverrides
    });
  }, [effectiveForecastBySize, demandOverrides]);
  const inboundByGrade = useMemo(function () {
    return {
      "1s": Number(planningInputs.inbound1s) || 0,
      "2s": Number(planningInputs.inbound2s) || 0,
      "3s": Number(planningInputs.inbound3s) || 0
    };
  }, [planningInputs.inbound1s, planningInputs.inbound2s, planningInputs.inbound3s]);
  const totalInboundBu = useMemo(function () {
    return r2(Object.values(inboundByGrade).reduce(function (s, v) {
      return s + v;
    }, 0));
  }, [inboundByGrade]);

  // Live / ungraded internal supply — derived from React state (liveCrabEntries),
  // NOT from localStorage. This ensures instant reactivity when the Live tab changes.
  // No date filter — all steam dates currently in the room are included.
  // Passes the full summarize result so computeGradeBuy can apply separate factors.
  const liveSupplyByGrade = useMemo(function () {
    return summarizeLiveRemaining(liveCrabEntries);
  } // returns {grade: {boxes,liveBu,ungradedBu,vendors}}
  , [liveCrabEntries]);
  const gradeBuy = useMemo(function () {
    return computeGradeBuy({
      demandPicture: demandPicture,
      inventoryBySize: inventoryBySize,
      inboundByGrade: inboundByGrade,
      gradeOverrides: gradeOverrides,
      liveSupplyByGrade: liveSupplyByGrade
    });
  }, [demandPicture, inventoryBySize, inboundByGrade, gradeOverrides, liveSupplyByGrade]);
  const timingPlan = useMemo(function () {
    if (!demandPicture.totalTrueDemand && !invTotals.usable && !totalInboundBu) return null;
    return computeTimingPlan({
      totalUsableBu: invTotals.usable,
      inboundByGrade: inboundByGrade,
      totalTrueDemandBu: demandPicture.totalTrueDemand,
      nextWeekDemandBu: nextWeekForecastBu || demandPicture.totalTrueDemand,
      today: planningInputs.planDate || todayISO(),
      parsedDays: parsed && parsed.days,
      allDays: allDays,
      parsedWeeks: parsed && parsed.weeks,
      allWks: allWks
    });
  }, [demandPicture, invTotals, inboundByGrade, totalInboundBu, planningInputs.planDate, nextWeekForecastBu, parsed, allDays, allWks]);

  // Purchase CRUD
  function savePurchase() {
    let _d$_newVendorName;
    let d = {
      ...draftPurchase
    };
    if (d.vendorId === "__new__" && (_d$_newVendorName = d._newVendorName) !== null && _d$_newVendorName !== void 0 && _d$_newVendorName.trim()) {
      const nv = makeVendor({
        name: d._newVendorName.trim(),
        sourceType: d.sourceType,
        typicalWindow: d.buyWindow
      });
      setVendors(function (vs) {
        return [].concat([...vs], [nv]);
      });
      d = {
        ...d,
        vendorId: nv.id
      };
    }
    delete d._newVendorName;
    delete d._editing;
    if (editingPurchase) {
      setPurchases(function (ps) {
        return ps.map(function (p) {
          return p.id === d.id ? d : p;
        });
      });
      setEditingPurchase(null);
    } else {
      setPurchases(function (ps) {
        return [].concat([...ps], [{
          ...d,
          id: uid()
        }]);
      });
    }
    setDraftPurchase(makePurchase());
    setShowForm(false);
  }
  function deletePurchase(id) {
    setPurchases(function (ps) {
      return ps.filter(function (p) {
        return p.id !== id;
      });
    });
  }
  function editPurchase(p) {
    setDraftPurchase({
      ...p,
      _editing: true
    });
    setEditingPurchase(p);
    setShowForm(true);
  }
  function cancelPurchase() {
    setDraftPurchase(makePurchase());
    setEditingPurchase(null);
    setShowForm(false);
  }
  function addVendor(v) {
    setVendors(function (vs) {
      return [].concat([...vs], [{
        ...v,
        id: uid()
      }]);
    });
  }
  function updateVendor(v) {
    setVendors(function (vs) {
      return vs.map(function (x) {
        return x.id === v.id ? v : x;
      });
    });
  }
  function deleteVendor(id) {
    setVendors(function (vs) {
      return vs.filter(function (x) {
        return x.id !== id;
      });
    });
  }
  return <div style={{
    minHeight: "100vh",
    background: B.cream,
    fontFamily: "'Segoe UI',system-ui,sans-serif",
    color: B.textDark
  }}>{<div style={{
      background: B.navy,
      padding: "0 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      position: "sticky",
      top: 0,
      zIndex: 100,
      boxShadow: "0 2px 14px rgba(26,46,74,.3)",
      minHeight: 52
    }}>{<CrabPlaceLogo size={28} />}{<div style={{
        flex: 1
      }} />}{invTotals.urgent > 0 && <div style={{
        background: "#fed7aa",
        color: B.urgentText,
        fontSize: 11,
        fontWeight: 700,
        padding: "4px 9px",
        borderRadius: 6
      }}>⚠ {fb(invTotals.urgent)} bu urgent</div>}{invTotals.expired > 0 && <div style={{
        background: "#fecaca",
        color: B.expiredText,
        fontSize: 11,
        fontWeight: 700,
        padding: "4px 9px",
        borderRadius: 6
      }}>⛔ {fb(invTotals.expired)} bu expired</div>}{savedAt && <div style={{
        color: "rgba(255,255,255,.35)",
        fontSize: 10,
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: 4
      }}>{<span style={{
          color: "#86efac",
          fontSize: 9
        }}>●</span>}{fmtSavedAt(savedAt)}</div>}{<button onClick={handleReset} style={{
        background: "rgba(255,255,255,.08)",
        color: "rgba(255,255,255,.5)",
        border: "1px solid rgba(255,255,255,.12)",
        borderRadius: 6,
        padding: "4px 9px",
        fontSize: 10,
        fontWeight: 600,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        whiteSpace: "nowrap"
      }}>↺ Reset</button>}{<button onClick={function onClick() {
        return setShowEmailModal(true);
      }} style={{
        background: "rgba(255,255,255,.13)",
        color: "rgba(255,255,255,.9)",
        border: "1px solid rgba(255,255,255,.2)",
        borderRadius: 6,
        padding: "5px 11px",
        fontSize: 11,
        fontWeight: 700,
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        gap: 5
      }}>✉ Save & Email</button>}</div>}{showEmailModal && <EmailSummaryModal onClose={function onClose() {
      return setShowEmailModal(false);
    }} invTotals={invTotals} demandPicture={demandPicture} inboundByGrade={inboundByGrade} gradeBuy={gradeBuy} planningInputs={planningInputs} inventoryLots={inventoryLots} purchases={purchases} enrichedLots={enrichedLots} parsedDays={parsed && parsed.days} crabMeatEntries={crabMeatEntries} taktAllData={taktAll} liveCrabEntries={liveCrabEntries} />}{<div style={{
      padding: "20px 16px",
      maxWidth: 860,
      margin: "0 auto",
      paddingBottom: 48
    }}>{<AppTabs tab={appTab} onChange={setAppTab} />}{appTab === "snapshot" && <DailyOpsSnapshot enrichedLots={enrichedLots} demandBySize={demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.bySize} invTotals={invTotals} planDate={planningInputs.planDate || todayISO()} liveCrabEntries={liveCrabEntries} todayActual={parsed && parsed.days && parsed.days[todayISO()]} parsedDays={parsed && parsed.days} />}{appTab === "planner" && <>{<BuyerSummaryCard invTotals={invTotals} demandPicture={demandPicture} inboundByGrade={inboundByGrade} totalInboundBu={totalInboundBu} gradeBuy={gradeBuy} liveSupplyByGrade={liveSupplyByGrade} weekPaceSignal={weekPaceSignal} lastWeekActualBu={lastWeekActualBu} />}{<Card style={{ marginBottom: 14, padding: "11px 16px" }}>{<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>{<div>{<span style={{ color: B.navy, fontWeight: 700, fontSize: 13 }}>Demand Basis: </span>}{<span style={{ color: B.textMid, fontSize: 12 }}>{planningInputs.weeklyOverride ? "Manual override \u2014 ".concat(planningInputs.weeklyOverride, " bu/week") : parsed && forecastTotalBu > 0 ? "Sales forecast \u2014 ~".concat(fb(forecastTotalBu), " bu/week (").concat(allWks.length, " weeks of data)").concat((function() {
  const todayKey = todayISO();
  const cwm = mondayOfWeek(todayKey);
  const nwm = (function(){ const d = new Date(cwm + 'T12:00:00'); d.setDate(d.getDate()+7); return isoFromDate(d); })();
  const ch = getHolidayForWeek(cwm);
  const nh = getHolidayForWeek(nwm);
  const parts = [];
  if (nh) parts.push('\uD83C\uDF89 ' + nh.name + ' next week \u2014 buying now');
  else if (ch) parts.push('\uD83C\uDF89 ' + ch.name + ' this week');
  else {
    const wk2m = (function(){ const d = new Date(cwm + 'T12:00:00'); d.setDate(d.getDate()+14); return isoFromDate(d); })();
    const w2h = getHolidayForWeek(wk2m);
    if (w2h) parts.push('\uD83C\uDF89 ' + w2h.name + ' in 2 weeks \u2014 plan ahead');
  }
  if (weekCompletionFactor && weekCompletionFactor.factor > 1 && weekCompletionFactor.todayDow <= 4) {
    parts.push('week ' + Math.round(weekCompletionFactor.soFarPct * 100) + '% committed');
  }
  return parts.length ? ' \u2014 ' + parts.join(', ') : '';
})()) : "No forecast data yet \u2014 enter a manual weekly demand override below"}</span>}</div>}</div>}</Card>}{<CollapsibleSection title="Inventory \u2014 Crab Count" badge={invTotals.usable > 0 ? fb(invTotals.usable) + " bu usable" : "No lots"} defaultOpen={true}><InventoryLotPanel lots={inventoryLots} onChange={setInventoryLots} planDate={planningInputs.planDate} onSnapshot={function onSnapshot(newLots) {
          const snap = makeSnapshot(newLots, planningInputs.planDate || todayISO());
          setSnapshotHistory(function (h) {
            return [].concat([...h], [snap]);
          });
        }} /></CollapsibleSection>}{<CollapsibleSection title="Snapshot History" defaultOpen={false}><SnapshotHistoryPanel snapshots={snapshotHistory} /></CollapsibleSection>}{<CollapsibleSection title="Planning Inputs" defaultOpen={false}><PlanningInputsPanel planningInputs={planningInputs} onChange={setPlanningInputs} liveSupplyByGrade={liveSupplyByGrade} /></CollapsibleSection>}{<CollapsibleSection title="Projected Demand by Finished Size" defaultOpen={false}><DemandPictureTable demandPicture={demandPicture} inventoryBySize={inventoryBySize} demandOverrides={demandOverrides} onChange={setDemandOverrides} /></CollapsibleSection>}{<CollapsibleSection title="Buy Recommendation" badge={gradeBuy && gradeBuy.totalIdealBu > 0 ? "Buy needed" : "Covered"} defaultOpen={true}><GradeBuyPanel gradeBuy={gradeBuy} gradeOverrides={gradeOverrides} onChange={setGradeOverrides} timingPlan={timingPlan} /></CollapsibleSection>}{<CollapsibleSection title="Why This Recommendation" defaultOpen={false}><RecommendationExplanation invTotals={invTotals} demandPicture={demandPicture} inboundByGrade={inboundByGrade} totalInboundBu={totalInboundBu} gradeBuy={gradeBuy} timingPlan={timingPlan} weekPaceSignal={weekPaceSignal} lastWeekActualBu={lastWeekActualBu} /></CollapsibleSection>}{<CollapsibleSection title="Purchase Log" defaultOpen={false}><div style={{ padding: "0 14px 14px" }}>{<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 10, marginBottom: 10 }}>{<div style={{ color: B.navy, fontWeight: 700, fontSize: 14 }}>Purchase Log</div>}{!showForm && <Btn variant="primary" small onClick={function onClick() { setDraftPurchase(makePurchase()); setEditingPurchase(null); setShowForm(true); }}>+ Log Purchase</Btn>}</div>}{showForm && <PurchaseForm purchase={draftPurchase} onChange={setDraftPurchase} vendors={vendors} onSave={savePurchase} onCancel={cancelPurchase} />}{<PurchaseLogTable purchases={purchases} vendors={vendors} onDelete={deletePurchase} onEdit={editPurchase} />}{<VendorManager vendors={vendors} onAdd={addVendor} onUpdate={updateVendor} onDelete={deleteVendor} />}</div></CollapsibleSection>}{<CollapsibleSection title="🫙 Crab Meat Count" defaultOpen={false}><CrabMeatPanel entries={crabMeatEntries} onChange={setCrabMeatEntries} /></CollapsibleSection>}</>}{appTab === "forecaster" && <div>{<WeekPaceTab enrichedLots={enrichedLots} demandBySize={demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.bySize} paceData={paceData} onRefresh={appFetchPaceData} />}{<CollapsibleSection title="Weekly Forecaster" defaultOpen={false}>{parsed && parsed.weeks && allWks.length > 0 ? <div>{<WeeklySummaryBar weeks={parsed.weeks} allWks={allWks} sel={selWk} />}{selWk && <WeekPlanTable weeks={parsed.weeks} allWks={allWks} sel={selWk} />}</div> : <div style={{padding:"20px",color:B.textLight,fontSize:12,textAlign:"center"}}>No forecast data loaded</div>}</CollapsibleSection>}</div>}{appTab === "live" && <LiveUngradedTab entries={liveCrabEntries} onEntriesChange={setLiveCrabEntries} />}{appTab === "takt" && <HardCrabTaktTab taktAll={taktAll} onTaktAllChange={setTaktAll} taktHistory={taktHistory} onHistoryChange={setTaktHistory} onSaveNow={doNeonSaveNow} />}{appTab === "ops" && <DailyOpsPlanningTab enrichedLots={enrichedLots} planDate={planningInputs.planDate || todayISO()} demandBySize={demandPicture === null || demandPicture === void 0 ? void 0 : demandPicture.bySize} todayActual={parsed && parsed.days && parsed.days[todayISO()]} parsedDays={parsed && parsed.days} allDays={allDays} liveCrabEntries={liveCrabEntries} liveSupplyByGrade={liveSupplyByGrade} inventoryBySize={inventoryBySize} demandPicture={demandPicture} weekCompletion={weekCompletionFactor} forecastBySize={forecastBySize} />}</div>}</div>;
}
export default function HardCrabPlanning() {
  return <div style={{
    width: '100%',
    minHeight: '100vh',
    background: '#fdf8f3',
    fontFamily: "'Segoe UI',system-ui,sans-serif",
    WebkitFontSmoothing: 'antialiased',
    boxSizing: 'border-box'
  }}>
      <ErrorBoundary><App /></ErrorBoundary>
    </div>;
}